import React, { useState, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import { useAuth } from '../hooks/useAuth';
import atributoService from '../services/atributoService';
import tareoService from '../services/tareoService';
import configuracionTareoService from '../services/configuracionTareoService';
import configuracionTasaNocturnaService from '../services/configuracionTasaNocturnaService';
import configuracionPlanillaAlternativaService from '../services/configuracionPlanillaAlternativaService';
import plameConfiguracionService from '../services/plameConfiguracionService';
import asignacionUsuarioProyectoService from '../services/asignacionUsuarioProyectoService';
import ExcelJS from 'exceljs';
import ImportadorTareos from '../components/ImportadorTareos';
import { aplicarFlagTasaNocturna, prepararTareosImportacion } from '../utils/tareoTasaNocturna';
import { expandirDestinosBoleta } from '../services/configuracionViaticosStandardService';
import { empleadoActivoDuranteRangoPlameOInterno, empleadoActivoEnFechaPlameOInterno } from '../utils/fechaImportacion';

const DESTINOS_BOLETA_OPTS = [
  { value: 'plame', label: 'PLAME' },
  { value: 'interna', label: 'Interna' },
  { value: 'ambas', label: 'Ambas' },
];

/** Resuelve destinos de boleta para un concepto (PA inactiva → solo plame). */
const destinosBoletaConcepto = (destino, planillaAlternativaActiva) => {
  if (!planillaAlternativaActiva) return ['plame'];
  return expandirDestinosBoleta(destino || 'plame');
};

// Input de búsqueda por nombre: estado local; el padre solo recibe valor al pulsar Buscar o al limpiar (evita re-render en cada tecla).
const SearchNameInput = forwardRef(({ appliedSearchName, onClear }, ref) => {
  const [localValue, setLocalValue] = useState(appliedSearchName || '');
  useEffect(() => {
    if (appliedSearchName === '') setLocalValue('');
  }, [appliedSearchName]);
  useImperativeHandle(ref, () => ({ getValue: () => localValue }), [localValue]);
  const handleClear = () => {
    setLocalValue('');
    onClear?.();
  };
  return (
    <div className="relative flex-1">
      <input
        type="text"
        placeholder="Buscar empleado..."
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        className="pl-8 pr-8 py-2 border border-gray-300 rounded-md text-sm w-full focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
      />
      <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
        <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
      {localValue ? (
        <button type="button" onClick={handleClear} className="absolute inset-y-0 right-0 pr-2 flex items-center">
          <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ) : null}
    </div>
  );
});
SearchNameInput.displayName = 'SearchNameInput';

// Edición masiva: inputs por horas con estado local; solo actualizan el padre al salir (onBlur).
const MassiveEditHoursInputs = ({ employeeId, entrada, hTeoricas, hReales, onCommit, calcularHoraSalida, variant }) => {
  const [ent, setEnt] = useState(entrada || '08:00');
  const [hT, setHT] = useState(hTeoricas ?? 8);
  const [hR, setHR] = useState(hReales ?? 8);
  useEffect(() => {
    setEnt(entrada || '08:00');
    setHT(hTeoricas ?? 8);
    setHR(hReales ?? 8);
  }, [entrada, hTeoricas, hReales]);
  const handleBlur = () => { onCommit(employeeId, ent, hT, hR); };
  const salidaT = ent && calcularHoraSalida ? calcularHoraSalida(ent, hT || 8) : '';
  // Cuando h reales es 0 (falta, feriado, etc.) no hay salida real
  const salidaR = (hR != null && hR > 0) && ent && calcularHoraSalida ? calcularHoraSalida(ent, hR) : '';
  const inputClassTable = 'w-18 px-1 py-1 border border-purple-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-purple-50';
  const inputClassNumTable = 'w-14 px-1 py-1 border border-purple-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 bg-purple-50 text-center';
  if (variant === 'table') {
    return (
      <>
        <td className="px-2 py-3">
          <input type="time" value={ent} onChange={(e) => setEnt(e.target.value)} onBlur={handleBlur} className={inputClassTable} />
        </td>
        <td className="px-2 py-3">
          <input type="number" step="0.25" min="0" max="24" value={hT} onChange={(e) => setHT(parseFloat(e.target.value) || 0)} onBlur={handleBlur} className={inputClassNumTable} />
        </td>
        <td className="px-2 py-3">
          <input type="number" step="0.25" min="0" max="24" value={typeof hR === 'number' ? hR : 8} onChange={(e) => setHR(parseFloat(e.target.value) || 0)} onBlur={handleBlur} className={inputClassNumTable} />
        </td>
        <td className="px-2 py-3">
          <div className="text-xs text-gray-600 bg-gray-50 px-1 py-1 rounded text-center">
            <div>{salidaT || '--:--'}</div>
            <div className="text-purple-600">{salidaR || '--:--'}</div>
          </div>
        </td>
      </>
    );
  }
  return (
    <>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className="block text-xs font-medium text-purple-700 mb-1">🕐 Entrada</label>
          <input type="time" value={ent} onChange={(e) => setEnt(e.target.value)} onBlur={handleBlur} className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white" />
        </div>
        <div>
          <label className="block text-xs font-medium text-purple-700 mb-1">⏱️ H. Teóricas</label>
          <input type="number" step="0.25" min="0" max="24" value={hT} onChange={(e) => setHT(parseFloat(e.target.value) || 0)} onBlur={handleBlur} className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-center" />
        </div>
        <div>
          <label className="block text-xs font-medium text-purple-700 mb-1">⚡ H. Reales</label>
          <input type="number" step="0.25" min="0" max="24" value={typeof hR === 'number' ? hR : 8} onChange={(e) => setHR(parseFloat(e.target.value) || 0)} onBlur={handleBlur} className="w-full px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-center" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center bg-gray-100 p-2 rounded">
          <div className="text-xs text-gray-600 font-medium">🕓 Salida Teórica</div>
          <div className="text-sm font-bold text-gray-800">{salidaT || '--:--'}</div>
        </div>
        <div className="text-center bg-gray-100 p-2 rounded">
          <div className="text-xs text-gray-600 font-medium">🕓 Salida Real</div>
          <div className="text-sm font-bold text-gray-800">{salidaR || '--:--'}</div>
        </div>
      </div>
    </>
  );
};

// Subcomponente: estado local + solo este evento. Al escribir, valor y salidas en tiempo real (calculadas aquí).
const HoursConfigInputs = ({ entrada, hTeoricas, hReales, onCommit, calcularHoraSalida }) => {
  const [ent, setEnt] = useState(entrada || '08:00');
  const [hT, setHT] = useState(hTeoricas ?? 8);
  const [hR, setHR] = useState(hReales ?? 8);
  useEffect(() => {
    setEnt(entrada || '08:00');
    setHT(hTeoricas ?? 8);
    setHR(hReales ?? 8);
  }, [entrada, hTeoricas, hReales]);
  const handleBlur = () => { onCommit(ent, hT, hR); };
  const salidaT = ent && calcularHoraSalida ? calcularHoraSalida(ent, hT || 8) : '';
  const salidaR = (hR != null && hR > 0) && ent && calcularHoraSalida ? calcularHoraSalida(ent, hR) : '';
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Hora de Entrada</label>
        <input
          type="time"
          value={ent}
          onChange={(e) => setEnt(e.target.value)}
          onBlur={handleBlur}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
        />
        <p className="text-xs text-gray-500 mt-1">Hora de entrada base para calcular las salidas (se agrega 1h de descanso si trabaja &gt;6h)</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Horas Teóricas</label>
          <input
            type="number"
            value={hT}
            onChange={(e) => setHT(parseFloat(e.target.value) || 0)}
            onBlur={handleBlur}
            min="0"
            max="24"
            step="0.5"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="8.0"
          />
          <p className="text-xs text-gray-500 mt-1">📅 Salida teórica: {salidaT || 'Calcular...'} {hT > 6 && ' (incluye 1h descanso)'}</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Horas Reales Trabajadas</label>
          <input
            type="number"
            value={hR}
            onChange={(e) => setHR(parseFloat(e.target.value) || 0)}
            onBlur={handleBlur}
            min="0"
            max="24"
            step="0.5"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            placeholder="8.0"
          />
          <p className="text-xs text-gray-500 mt-1">🕐 Salida real: {salidaR || 'Calcular...'} {hR > 6 && ' (incluye 1h descanso)'}</p>
        </div>
      </div>
    </div>
  );
};

// Fila de concepto en edición masiva: estado local; solo actualiza al padre en onBlur.
const MassiveConceptRowInputs = ({ employeeId, conceptIndex, type, amount, description, destinoBoleta = 'plame', mostrarDestino = false, conceptTypes, onBlurRow, onRemove, variant }) => {
  const [t, setT] = useState(type || '');
  const [a, setA] = useState(amount ?? 0);
  const [d, setD] = useState(description || '');
  const [dest, setDest] = useState(destinoBoleta || 'plame');
  useEffect(() => {
    setT(type || '');
    setA(amount ?? 0);
    setD(description || '');
    setDest(destinoBoleta || 'plame');
  }, [type, amount, description, destinoBoleta]);
  const handleBlur = () => { onBlurRow(employeeId, conceptIndex, { type: t, amount: a, description: d, destino_boleta: dest }); };
  if (variant === 'table') {
    return (
      <div className="bg-gray-100 p-1 rounded flex items-center space-x-1 flex-wrap">
        <select
          value={t}
          onChange={(e) => setT(e.target.value)}
          onBlur={handleBlur}
          className="flex-1 min-w-[4rem] text-xs border-0 bg-transparent focus:outline-none"
        >
          <option value="">Tipo</option>
          {(conceptTypes || []).map((ct) => (
            <option key={ct.value} value={ct.value}>{ct.label.substring(0, 12)}...</option>
          ))}
        </select>
        <input
          type="number"
          value={a}
          onChange={(e) => setA(parseFloat(e.target.value) || 0)}
          onBlur={handleBlur}
          className="w-12 text-xs border-0 bg-transparent focus:outline-none text-center"
          min="0"
          step="0.01"
        />
        {mostrarDestino && (
          <select
            value={dest}
            onChange={(e) => setDest(e.target.value)}
            onBlur={handleBlur}
            className="w-16 text-xs border-0 bg-transparent focus:outline-none"
            title="Destino boleta"
          >
            {DESTINOS_BOLETA_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        )}
        <button type="button" onClick={() => onRemove(employeeId, conceptIndex)} className="text-red-500 hover:text-red-700" title="Eliminar">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

    );
  }
  return (
    <div className="bg-gray-100 p-2 rounded border">
      <div className={`grid gap-2 mb-2 ${mostrarDestino ? 'grid-cols-3' : 'grid-cols-2'}`}>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Tipo</label>
          <select value={t} onChange={(e) => setT(e.target.value)} onBlur={handleBlur} className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500">
            <option value="">Seleccionar</option>
            {(conceptTypes || []).map((ct) => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Monto (S/)</label>
          <input type="number" value={a} onChange={(e) => setA(parseFloat(e.target.value) || 0)} onBlur={handleBlur} className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500" min="0" step="0.01" placeholder="0.00" />
        </div>
        {mostrarDestino && (
          <div>
            <label className="block text-xs text-gray-600 mb-1">Destino</label>
            <select value={dest} onChange={(e) => setDest(e.target.value)} onBlur={handleBlur} className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500">
              {DESTINOS_BOLETA_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-between items-center">
        <input type="text" value={d} onChange={(e) => setD(e.target.value)} onBlur={handleBlur} placeholder="Observaciones..." className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 mr-2" />
        <button type="button" onClick={() => onRemove(employeeId, conceptIndex)} className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors duration-200" title="Eliminar concepto">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
        </button>
      </div>
    </div>
  );
};

// Monto efectivo del concepto (el formulario edita `amount`; el backend usa `monto`).
const getConceptoMonto = (concepto) => {
  const raw = concepto?.amount ?? concepto?.monto ?? 0;
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
};

/** Arma uno o más payloads de concepto según destino (ambas → plame + interna). */
const mapearConceptoAPayloadsBackend = (concepto, conceptTypes, plameConceptos, planillaAlternativaActiva) => {
  const conceptoId = concepto.plame_configuracion_id || concepto.type;
  let plameConfigId = null;
  const conceptType = conceptTypes.find((ct) => ct.value === concepto.type || ct.value === parseInt(conceptoId, 10));
  if (conceptType) {
    plameConfigId = conceptType.value;
  } else {
    const plameConcepto = plameConceptos.find((pc) => pc.id === parseInt(conceptoId, 10));
    if (plameConcepto) plameConfigId = plameConcepto.id;
  }

  let tipoConcepto = 'descuento';
  const savedConceptType = concepto.conceptType || concepto.tipo_concepto || conceptType?.type;
  if (savedConceptType === 'advance' || savedConceptType === 'adelanto') tipoConcepto = 'adelanto';
  else if (savedConceptType === 'income' || savedConceptType === 'ingreso') tipoConcepto = 'ingreso';
  else if (savedConceptType === 'discount' || savedConceptType === 'descuento') tipoConcepto = 'descuento';

  const base = {
    plame_configuracion_id: plameConfigId || parseInt(conceptoId, 10) || 1,
    tipo_concepto: tipoConcepto,
    monto: getConceptoMonto(concepto),
    observaciones: concepto.observaciones || concepto.description || null,
  };
  const destinoRaw = concepto.destino_boleta || concepto.tipo_boleta || 'plame';
  return destinosBoletaConcepto(destinoRaw, planillaAlternativaActiva).map((tb) => ({
    ...base,
    tipo_boleta: tb,
  }));
};

// Fila de concepto: estado local; sincroniza al padre en onBlur y al cambiar monto/tipo.
const ConceptRowInputs = ({ index, type, amount, description, destinoBoleta = 'plame', mostrarDestino = false, conceptTypes, onBlurRow, onRemove }) => {
  const [t, setT] = useState(type || '');
  const [a, setA] = useState(amount ?? 0);
  const [d, setD] = useState(description || '');
  const [dest, setDest] = useState(destinoBoleta || 'plame');
  useEffect(() => {
    setT(type || '');
    setA(amount ?? 0);
    setD(description || '');
    setDest(destinoBoleta || 'plame');
  }, [type, amount, description, destinoBoleta]);
  const pushRow = (nextT, nextA, nextD, nextDest) => {
    onBlurRow(index, { type: nextT, amount: nextA, description: nextD, destino_boleta: nextDest });
  };
  const handleBlur = () => { pushRow(t, a, d, dest); };
  return (
    <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg relative">
      <div className={`grid grid-cols-1 gap-4 ${mostrarDestino ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
        <div className={mostrarDestino ? 'lg:col-span-2' : 'lg:col-span-2'}>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Concepto *</label>
          <select
            value={t}
            onChange={(e) => {
              const v = e.target.value;
              setT(v);
              pushRow(v, a, d, dest);
            }}
            onBlur={handleBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          >
            <option value="">Seleccionar concepto</option>
            {(conceptTypes || []).map((ct) => (
              <option key={ct.value} value={ct.value}>{ct.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Monto (S/) *</label>
          <input
            type="number"
            value={a}
            onChange={(e) => {
              const val = parseFloat(e.target.value) || 0;
              setA(val);
              pushRow(t, val, d, dest);
            }}
            onBlur={handleBlur}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            min="0"
            step="0.01"
            placeholder="0.00"
          />
        </div>
        {mostrarDestino && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Destino boleta *</label>
            <select
              value={dest}
              onChange={(e) => {
                const v = e.target.value;
                setDest(v);
                pushRow(t, a, d, v);
              }}
              onBlur={handleBlur}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {DESTINOS_BOLETA_OPTS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="mt-3">
        <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones (opcional)</label>
        <input
          type="text"
          value={d}
          onChange={(e) => setD(e.target.value)}
          onBlur={handleBlur}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          placeholder="Descripción adicional del concepto..."
        />
      </div>
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded-full transition-colors duration-200"
        title="Eliminar concepto"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

const Tareos = () => {
  const { selectedCompany, user } = useAuth();
  
  // Estados principales
  const [currentView, setCurrentView] = useState('project-selection'); // 'project-selection' | 'tareos' | 'calendar-analysis'
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [plameConceptos, setPlameConceptos] = useState([]);
  const [configuracionTareo, setConfiguracionTareo] = useState(null);
  const [tasaNocturnaActiva, setTasaNocturnaActiva] = useState(false);
  const [planillaAlternativaActiva, setPlanillaAlternativaActiva] = useState(false);
  const [considerarTasaNocturnaIndividual, setConsiderarTasaNocturnaIndividual] = useState(false);
  
  // Estados originales del tareo
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAutomaticEditModal, setShowAutomaticEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  // Filtro por período: día (por defecto), semana o mes
  const [filterPeriod, setFilterPeriod] = useState('day'); // 'day' | 'week' | 'month'
  // filterValue: día = YYYY-MM-DD, semana = YYYY-MM-DD (lunes), mes = YYYY-MM
  const [filterValue, setFilterValue] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  // Ref para leer valor del input de búsqueda sin estado en el padre (evita re-render en cada tecla)
  const searchInputRef = useRef(null);
  // Valor aplicado al filtrado: solo se actualiza al pulsar "Buscar" o al limpiar
  const [appliedSearchName, setAppliedSearchName] = useState('');
  const [appliedHoursFilter, setAppliedHoursFilter] = useState('');
  
  // 🗑️ ESTADOS PARA BORRADO MASIVO
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // 🔍 ESTADOS PARA MULTIFILTRO DE CARGOS
  const [filtrosCargosSeleccionados, setFiltrosCargosSeleccionados] = useState([]);

  // 🔍 ESTADO PARA FILTRO DE ÁREAS DE TRABAJO
  const [filtrosAreasSeleccionadas, setFiltrosAreasSeleccionadas] = useState([]);

  // 🔍 NUEVOS FILTROS
  const [eventTypeFilter, setEventTypeFilter] = useState(''); // Filtro por tipo de evento
  const [hoursFilter, setHoursFilter] = useState(''); // Filtro por horas: 'none', 'low', 'normal', 'high'
  
  // 📅 ESTADOS PARA VISTA DE CALENDARIO HORIZONTAL
  const [calendarStartDate, setCalendarStartDate] = useState(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [calendarEndDate, setCalendarEndDate] = useState(() => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return lastDay.toISOString().split('T')[0];
  });
  
  // Estados separados para los selectores para evitar problemas de conversión
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().getMonth());
  const [selectedWeekYear, setSelectedWeekYear] = useState(() => new Date().getFullYear());
  const [selectedWeek, setSelectedWeek] = useState(1); // Se inicializará correctamente después
  
  const [calendarSelectedEmployees, setCalendarSelectedEmployees] = useState([]);
  const [calendarGroupBy, setCalendarGroupBy] = useState('employee'); // 'employee' | 'position'
  const [showCalendarFilters, setShowCalendarFilters] = useState(true);
  
  // Inicializar la semana actual correctamente
  useEffect(() => {
    const today = new Date();
    setSelectedWeek(getWeekOfYear(today));
    
    // Inicializar con vista de semanas completas para el mes actual (solo una vez)
    handleMonthYearSelection(today.getFullYear(), today.getMonth());
  }, []); // Solo ejecutar al montar el componente

  // Estado para importador de tareos desde Excel
  const [showImportarTareosModal, setShowImportarTareosModal] = useState(false);
  const [importadorModoGlobal, setImportadorModoGlobal] = useState(false);
  const [importadorInitialMode, setImportadorInitialMode] = useState('importar');
  /** Filtro de sede cuando se ve "Todas las sedes" */
  const [filtroSedeId, setFiltroSedeId] = useState('');

  const esTodasSedes = Boolean(selectedProject?._todasSedes);

  const empleadoPerteneceASede = (emp, project) => {
    if (!emp || !project) return false;
    const proy = (emp.proyecto || '').toString().trim();
    const proyId = emp.proyecto_id != null ? String(emp.proyecto_id) : '';
    return proy === (project.nombre || '').trim() || proyId === String(project.id);
  };

  const empleadoEnAlcanceActual = (emp) => {
    if (!emp) return false;
    if (esTodasSedes) {
      if (!projects.length) return true;
      return projects.some((p) => empleadoPerteneceASede(emp, p));
    }
    return empleadoPerteneceASede(emp, selectedProject);
  };

  // Actualizar empleados seleccionados del calendario cuando cambien los filtros
  useEffect(() => {
    if (currentView === 'calendar-analysis' && employees.length > 0) {
      const filteredEmpIds = employees.filter(emp => {
        const projectMatch = empleadoEnAlcanceActual(emp);
        const cargoMatch = filtrosCargosSeleccionados.length === 0 || filtrosCargosSeleccionados.includes(emp.cargo);
        const areaMatch = filtrosAreasSeleccionadas.length === 0 || filtrosAreasSeleccionadas.includes(emp.areas_de_trabajo || 'Sin área');
        return projectMatch && cargoMatch && areaMatch;
      }).map(emp => emp.id);
      
      // Solo actualizar si hay diferencias significativas
      if (filteredEmpIds.length !== calendarSelectedEmployees.length || 
          !filteredEmpIds.every(id => calendarSelectedEmployees.includes(id))) {
        setCalendarSelectedEmployees(filteredEmpIds);
      }
    }
  }, [filtrosCargosSeleccionados, filtrosAreasSeleccionadas, employees, selectedProject, currentView, projects, esTodasSedes]);
  
  const [newAttendanceData, setNewAttendanceData] = useState({
    employeeId: '',
    date: new Date().toISOString().split('T')[0],
    eventType: 'asistio',
    theoreticalCheckIn: '',
    theoreticalCheckOut: '',
    checkIn: '',
    checkOut: '',
    // Nuevos campos para tareo por horas
    horasTeoricas: 8,
    horasReales: 8,
    advances: 0,
    income: 0,
    discounts: 0,
    notes: '',
    concepts: []
  });

  // Eventos para los que las horas reales deben estar vacías (consistencia con backend)
  const EVENTOS_SIN_HORAS_REALES = [
    'falta', 'feriado', 'descanso', 'permiso', 'suspension', 'vacaciones',
    'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad'
  ];

  // Estados para registro masivo
  const [showMassiveModal, setShowMassiveModal] = useState(false);
  const [massiveDate, setMassiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [massiveEmployees, setMassiveEmployees] = useState([]);
  const [isCreatingMassive, setIsCreatingMassive] = useState(false);

  const [showTareoPerfectoModal, setShowTareoPerfectoModal] = useState(false);
  const [tareoPerfectoData, setTareoPerfectoData] = useState({
    empleadoIds: [], fechaInicio: '', fechaFin: '', validando: false, error: null, omitidos: [], creados: 0
  });

  // Ingresos 1001-1020 con nombre (para fallback y para asegurar que aparezcan en el modal)
  const INGRESOS_1001_1020 = useMemo(() => {
    return Array.from({ length: 20 }, (_, i) => {
      const codigo = String(1001 + i);
      return { value: 100000 + (i + 1), label: `OTROS CONCEPTOS ${i + 1}`, type: 'ingreso', codigo };
    });
  }, []);

  // Tipos de conceptos disponibles - se cargan dinámicamente desde el backend (incluye 1001-1020 como ingresos)
  const conceptTypes = useMemo(() => {
    const fallbackDescuentos = [
      { value: 1, label: 'ADELANTO', type: 'adelanto', codigo: '0701' },
      { value: 2, label: 'CUOTA SINDICAL', type: 'descuento', codigo: '0702' },
      { value: 3, label: 'DESCUENTO AUTORIZADO U ORDENADO POR MANDATO JUDICIAL', type: 'descuento', codigo: '0703' },
      { value: 4, label: 'TARDANZAS', type: 'descuento', codigo: '0704' },
      { value: 5, label: 'INASISTENCIAS', type: 'descuento', codigo: '0705' },
      { value: 6, label: 'OTROS DESCUENTOS NO DEDUCIBLES DE LA BASE IMPONIBLE', type: 'descuento', codigo: '0706' },
      { value: 7, label: 'OTROS DESCUENTOS DEDUCIBLES DE LA BASE IMPONIBLE', type: 'descuento', codigo: '0707' },
    ];

    if (!Array.isArray(plameConceptos) || plameConceptos.length === 0) {
      return [...fallbackDescuentos, ...INGRESOS_1001_1020];
    }

    // Backend: clasificación tareos + descuentos 070x + códigos 1001-1020
    const fromApi = plameConceptos.map((concepto) => ({
      value: concepto.id,
      label: concepto.nombre,
      type:
        concepto.tipo_concepto === 'ingreso' ||
        (concepto.codigo && /^10(0[1-9]|1[0-9]|20)$/.test(concepto.codigo))
          ? 'ingreso'
          : (concepto.tipo_concepto || 'descuento'),
      codigo: concepto.codigo,
    }));

    // Si la API solo trajo ingresos (p. ej. 1001-1020), reponer descuentos faltantes
    const codigosApi = new Set(fromApi.map((c) => String(c.codigo || '')));
    const faltanDescuentos = fallbackDescuentos.some((d) => !codigosApi.has(d.codigo));
    if (faltanDescuentos) {
      const extras = fallbackDescuentos.filter((d) => !codigosApi.has(d.codigo));
      return [...extras, ...fromApi];
    }

    return fromApi;
  }, [plameConceptos, INGRESOS_1001_1020]);



  // Datos de ejemplo de asistencia
  const [attendanceRecords, setAttendanceRecords] = useState([
    {
      id: 1,
      employeeId: 1,
      date: '2024-06-10',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '17:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8,
      status: 'presente',
      advances: 0,
      discounts: 0,
      notes: ''
    },
    {
      id: 2,
      employeeId: 2,
      date: '2024-06-10',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:15',
      checkOut: '17:30',
      breakStart: '12:30',
      breakEnd: '13:30',
      hoursWorked: 8.25,
      status: 'tarde',
      advances: 0,
      discounts: 50,
      concepts: [
        {
          type: 'tardanzas',
          amount: 50,
          description: 'Descuento por llegada tardía de 15 minutos'
        }
      ],
      notes: 'Llegó 15 minutos tarde'
    },
    {
      id: 3,
      employeeId: 3,
      date: '2024-06-10',
      eventType: 'falta',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '',
      checkOut: '',
      breakStart: '',
      breakEnd: '',
      hoursWorked: 0,
      status: 'falta',
      advances: 0,
      discounts: 120,
      concepts: [
        {
          type: 'inasistencias',
          amount: 100,
          description: 'Descuento por día completo de ausencia'
        },
        {
          type: 'cuota_sindical',
          amount: 20,
          description: 'Cuota mensual del sindicato'
        }
      ],
      notes: 'Ausencia injustificada'
    },
    {
      id: 4,
      employeeId: 4,
      date: '2024-06-10',
      eventType: 'descanso_medico',
      theoreticalCheckIn: '09:00',
      theoreticalCheckOut: '18:00',
      checkIn: '',
      checkOut: '',
      breakStart: '',
      breakEnd: '',
      hoursWorked: 0,
      status: 'permiso',
      advances: 0,
      discounts: 0,
      notes: 'Descanso médico autorizado'
    },
    {
      id: 5,
      employeeId: 5,
      date: '2024-06-10',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '19:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 10,
      status: 'presente',
      advances: 0,
      discounts: 0,
      notes: 'Trabajó hasta tarde'
    },
    {
      id: 6,
      employeeId: 1,
      date: '2024-06-11',
      eventType: 'feriado_laborado',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '17:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8,
      status: 'presente',
      advances: 0,
      discounts: 0,
      notes: 'Día feriado trabajado'
    },
    {
      id: 7,
      employeeId: 2,
      date: '2024-06-12',
      eventType: 'descanso',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '',
      checkOut: '',
      breakStart: '',
      breakEnd: '',
      hoursWorked: 0,
      status: 'presente',
      advances: 0,
      discounts: 0,
      notes: 'Día de descanso semanal'
    },
    {
      id: 8,
      employeeId: 3,
      date: '2024-06-13',
      eventType: 'descanso_laborado',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '17:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8,
      status: 'presente',
      advances: 150,
      discounts: 0,
      income: 100,
      concepts: [
        {
          type: 'adelanto',
          amount: 150,
          description: 'Adelanto quincenal solicitado por el empleado'
        },
        {
          type: 'otros_conceptos',
          amount: 100,
          description: 'Bonificación por trabajo en día de descanso'
        }
      ],
      notes: 'Trabajó en día de descanso'
    },
    // Registros adicionales para demostrar filtros
    {
      id: 9,
      employeeId: 1,
      date: '2024-12-15',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:05',
      checkOut: '17:15',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8.17,
      status: 'presente',
      advances: 0,
      income: 0,
      discounts: 0,
      notes: 'Registro actual'
    },
    {
      id: 10,
      employeeId: 2,
      date: '2024-11-28',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '17:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8,
      status: 'presente',
      advances: 0,
      income: 0,
      discounts: 0,
      notes: 'Registro mes anterior'
    },
    {
      id: 11,
      employeeId: 3,
      date: '2023-12-15',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '17:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8,
      status: 'presente',
      advances: 0,
      income: 0,
      discounts: 0,
      notes: 'Registro año anterior'
    },
    // Registro de prueba para verificar cálculo de horas
    {
      id: 12,
      employeeId: 1,
      date: '2024-12-16',
      eventType: 'asistio',
      theoreticalCheckIn: '08:00',
      theoreticalCheckOut: '17:00',
      checkIn: '08:00',
      checkOut: '17:00',
      breakStart: '12:00',
      breakEnd: '13:00',
      hoursWorked: 8, // 9 horas totales - 1 hora de descanso = 8 horas
      status: 'presente',
      advances: 0,
      income: 0,
      discounts: 0,
      notes: 'Prueba de cálculo de horas'
    }
  ]);

  // Cargar proyectos al montar el componente y cuando cambie la empresa o el usuario
  useEffect(() => {
    if (selectedCompany?.id && user) {
      loadProjects();
      loadConfiguracionTasaNocturna();
      loadConfiguracionPlanillaAlternativa();
    }
  }, [selectedCompany, user]);

  // Cargar registros al cambiar proyecto, vista o mes (al cambiar el mes en el input se consulta la API)
  useEffect(() => {
    if (selectedCompany?.id && selectedProject && currentView === 'tareos') {
      loadAttendanceRecords();
    }
  }, [selectedCompany, selectedProject, currentView, filterValue, filterPeriod]);

  const loadProjects = async () => {
    setLoading(true);
    try {
      // Obtener todos los proyectos de la empresa
      const allProjectsResponse = await atributoService.obtenerSubAtributosPorEmpresa(selectedCompany.id);
      
      if (!allProjectsResponse.success || !allProjectsResponse.data.Proyectos) {
        console.error('Error al cargar proyectos:', allProjectsResponse.error);
        setProjects([]);
        return;
      }

      const todosLosProyectos = allProjectsResponse.data.Proyectos;

      // Si no hay usuario autenticado, mostrar todos los proyectos
      if (!user?.id) {
        setProjects(todosLosProyectos);
        return;
      }

      const asignacionesResponse = await asignacionUsuarioProyectoService.obtenerAsignacionesPorUsuario(
        selectedCompany.id, 
        user.id
      );

      if (asignacionesResponse.success && asignacionesResponse.data && asignacionesResponse.data.length > 0) {
        const proyectosAsignados = asignacionesResponse.data.map(asignacion => asignacion.proyecto_id);
        const proyectosFiltrados = todosLosProyectos.filter(proyecto => 
          proyectosAsignados.includes(proyecto.id)
        );
        setProjects(proyectosFiltrados);
      } else {
        setProjects(todosLosProyectos);
      }

    } catch (error) {
      console.error('❌ Error al cargar proyectos:', error);
      
      // En caso de error con asignaciones, intentar cargar todos los proyectos como fallback
      try {
        const fallbackResponse = await atributoService.obtenerSubAtributosPorEmpresa(selectedCompany.id);
        if (fallbackResponse.success && fallbackResponse.data.Proyectos) {
          setProjects(fallbackResponse.data.Proyectos);
        } else {
          setProjects([]);
        }
      } catch (fallbackError) {
        console.error('❌ Error en fallback:', fallbackError);
        setProjects([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Cargar empleados desde el backend
  const loadEmployees = async () => {
    try {
      const response = await tareoService.obtenerEmpleados(selectedCompany.id);
      if (response.success) {
        const list = Array.isArray(response.data) ? response.data : [];
        // Un empleado por id: evita filas/calendario duplicados si el API devuelve repeticiones
        const byId = new Map();
        for (const emp of list) {
          if (emp && emp.id != null && !byId.has(emp.id)) byId.set(emp.id, emp);
        }
        setEmployees([...byId.values()]);
      } else {
        console.error('❌ Error al cargar empleados:', response.message);
      }
    } catch (error) {
      console.error('💥 Error exception al cargar empleados:', error);
    }
  };

  // Cargar conceptos PLAME específicos para tareos (clasificación_id = 21)
  const loadPlameConceptos = async () => {
    try {
      const response = await plameConfiguracionService.obtenerConfiguracionesTareos();
      if (response.success && Array.isArray(response.data)) {
        setPlameConceptos(response.data);
      } else {
        console.error('Error al cargar conceptos PLAME:', response.message || 'Datos no válidos');
        setPlameConceptos(getPlameConceptosFallback());
      }
    } catch (error) {
      console.error('Error al cargar conceptos PLAME:', error);
      setPlameConceptos(getPlameConceptosFallback());
    }
  };

  // Fallback de conceptos PLAME para tareos (incluye ingresos 1001-1020 con nombre)
  const getPlameConceptosFallback = () => {
    const ingresos1001_1020 = Array.from({ length: 20 }, (_, i) => ({
      id: 100000 + (i + 1),
      nombre: `OTROS CONCEPTOS ${i + 1}`,
      tipo_concepto: 'ingreso',
      codigo: String(1001 + i)
    }));
    return [
        { id: 1, nombre: 'ADELANTO', tipo_concepto: 'adelanto', codigo: '0701' },
        { id: 2, nombre: 'CUOTA SINDICAL', tipo_concepto: 'descuento', codigo: '0702' },
        { id: 3, nombre: 'DESCUENTO AUTORIZADO U ORDENADO POR MANDATO JUDICIAL', tipo_concepto: 'descuento', codigo: '0703' },
        { id: 4, nombre: 'TARDANZAS', tipo_concepto: 'descuento', codigo: '0704' },
        { id: 5, nombre: 'INASISTENCIAS', tipo_concepto: 'descuento', codigo: '0705' },
        { id: 6, nombre: 'OTROS DESCUENTOS NO DEDUCIBLES DE LA BASE IMPONIBLE', tipo_concepto: 'descuento', codigo: '0706' },
        { id: 7, nombre: 'OTROS DESCUENTOS DEDUCIBLES DE LA BASE IMPONIBLE', tipo_concepto: 'descuento', codigo: '0707' },
      ...ingresos1001_1020
    ];
  };

  // Función para cargar configuración de tareo
  const loadConfiguracionTareo = async () => {
    try {
      const response = await configuracionTareoService.obtenerConfiguracionTareo(selectedCompany.id);
      
      if (response.success) {
        setConfiguracionTareo(response.data);
      } else {
        console.error('Error al cargar configuración de tareo:', response.message);
        // Configuración por defecto (detallado)
        setConfiguracionTareo({
          empresa_id: selectedCompany.id,
          tipo_tareo: 2,
          es_simple: false,
          es_detallado: true,
          nombre_tipo: 'Detallado',
          descontar_descansos: true // Activado por defecto
        });
      }
    } catch (error) {
      console.error('Error al cargar configuración de tareo:', error);
      // Configuración por defecto en caso de error
      setConfiguracionTareo({
        empresa_id: selectedCompany.id,
        tipo_tareo: 2,
        es_simple: false,
        es_detallado: true,
        nombre_tipo: 'Detallado',
        descontar_descansos: true // Activado por defecto
      });
    }
  };

  const loadConfiguracionTasaNocturna = async () => {
    if (!selectedCompany?.id) {
      setTasaNocturnaActiva(false);
      return;
    }

    try {
      const response = await configuracionTasaNocturnaService.obtenerConfiguracion(selectedCompany.id);
      if (response?.success && response.data) {
        setTasaNocturnaActiva(Boolean(response.data.activo));
      } else {
        setTasaNocturnaActiva(false);
      }
    } catch (error) {
      console.error('Error al cargar configuración de tasa nocturna:', error);
      setTasaNocturnaActiva(false);
    }
  };

  const loadConfiguracionPlanillaAlternativa = async () => {
    if (!selectedCompany?.id) {
      setPlanillaAlternativaActiva(false);
      return;
    }
    try {
      const response = await configuracionPlanillaAlternativaService.verificarEstado(selectedCompany.id);
      const activo = Boolean(
        response?.success && (response.data?.planilla_alternativa_activa || response.data?.activo)
      );
      setPlanillaAlternativaActiva(activo);
    } catch (error) {
      console.error('Error al cargar planilla alternativa:', error);
      setPlanillaAlternativaActiva(false);
    }
  };

  // Cargar registros de asistencia desde el backend
  // monthOverride: opcional YYYY-MM (ej. desde vista calendario); si no se pasa, usa filterPeriod + filterValue
  const loadAttendanceRecords = async (monthOverride = null) => {
    if (!selectedProject) return;
    let fecha_inicio, fecha_fin;
    if (monthOverride) {
      // Vista calendario: rango de 3 meses
      const [y, m] = (monthOverride || '').split('-').map(Number);
      const year = y || new Date().getFullYear();
      const month = m || (new Date().getMonth() + 1);
      const firstPrev = new Date(year, month - 2, 1);
      const lastNext = new Date(year, month + 1, 0);
      fecha_inicio = `${firstPrev.getFullYear()}-${String(firstPrev.getMonth() + 1).padStart(2, '0')}-${String(firstPrev.getDate()).padStart(2, '0')}`;
      fecha_fin = `${lastNext.getFullYear()}-${String(lastNext.getMonth() + 1).padStart(2, '0')}-${String(lastNext.getDate()).padStart(2, '0')}`;
    } else {
      if (!filterValue) return;
      if (filterPeriod === 'day') {
        fecha_inicio = filterValue;
        fecha_fin = filterValue;
      } else if (filterPeriod === 'week') {
        const range = getWeekRangeFromDate(filterValue);
        fecha_inicio = range.start;
        fecha_fin = range.end;
      } else {
        const [y, m] = (filterValue || '').split('-').map(Number);
        const year = y || new Date().getFullYear();
        const month = m || (new Date().getMonth() + 1);
        const firstPrev = new Date(year, month - 2, 1);
        const lastNext = new Date(year, month + 1, 0);
        fecha_inicio = `${firstPrev.getFullYear()}-${String(firstPrev.getMonth() + 1).padStart(2, '0')}-${String(firstPrev.getDate()).padStart(2, '0')}`;
        fecha_fin = `${lastNext.getFullYear()}-${String(lastNext.getMonth() + 1).padStart(2, '0')}-${String(lastNext.getDate()).padStart(2, '0')}`;
      }
    }
    setLoadingRecords(true);
    try {

      const cargarTodosLosTareos = async () => {
        let todosLosTareos = [];
        let pagina = 1;
        let hayMasPaginas = true;
        
        while (hayMasPaginas) {
      const filtros = {
        empresa_id: selectedCompany.id,
        fecha_inicio,
        fecha_fin,
            per_page: 1000,
            page: pagina
      };
      if (selectedProject?.id) {
        filtros.proyecto_id = selectedProject.id;
      }

      const response = await tareoService.obtenerTareos(filtros);
          if (response.success && response.data?.data) {
            const tareosDeEstaPagina = response.data.data;
            todosLosTareos = [...todosLosTareos, ...tareosDeEstaPagina];
            
            // Verificar si hay más páginas
            const paginationInfo = response.data;
            hayMasPaginas = paginationInfo.current_page < paginationInfo.last_page;
            
            if (hayMasPaginas) {
              pagina++;
              // Pequeña pausa para no sobrecargar el servidor
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } else {
            hayMasPaginas = false;
          }
        }
        return {
          success: true,
          data: {
            data: todosLosTareos,
            total: todosLosTareos.length
          }
        };
      };

      const response = await cargarTodosLosTareos();
      if (response.success) {
        const recordsTransformed = response.data.data.map(tareo => {
          const fechaExtraida = tareo.fecha.includes('T') ? tareo.fecha.split('T')[0] : tareo.fecha;
          let hoursWorked = parseFloat(tareo.horas_trabajadas || 0);
          if (tareo.tipo_evento === 'descanso') hoursWorked = 0;
          if (tareo.tipo_evento === 'descanso_medico') hoursWorked = 8;
          if (tareo.tipo_evento === 'reposo_medico') hoursWorked = 0;
          if (tareo.tipo_evento === 'subsidio_por_enfermedad') hoursWorked = 8;
          if (tareo.tipo_evento === 'licencia_con_goce_de_haber') hoursWorked = 8;
          if (tareo.tipo_evento === 'falta') hoursWorked = 0;
          
          return {
          id: tareo.id,
          employeeId: tareo.empleado_id,
          proyectoId: tareo.proyecto_id,
          proyectoNombre: tareo.proyecto?.nombre || tareo.proyecto?.name || '',
          date: fechaExtraida,
          eventType: tareo.tipo_evento,
          theoreticalCheckIn: tareo.hora_entrada_teorica,
          theoreticalCheckOut: tareo.hora_salida_teorica,
          checkIn: tareo.hora_entrada_real,
          checkOut: tareo.hora_salida_real,
          hoursWorked: hoursWorked,
          status: tareo.estado,
          observaciones: tareo.observaciones,
          // Calcular totales de conceptos
          advances: tareo.conceptos?.filter(c => c.tipo_concepto === 'adelanto').reduce((sum, c) => sum + parseFloat(c.monto), 0) || 0,
          income: tareo.conceptos?.filter(c => c.tipo_concepto === 'ingreso').reduce((sum, c) => sum + parseFloat(c.monto), 0) || 0,
          discounts: tareo.conceptos?.filter(c => c.tipo_concepto === 'descuento').reduce((sum, c) => sum + parseFloat(c.monto), 0) || 0,
          // Preservar conceptos del backend directamente
          concepts: tareo.conceptos || [],
          // También mantener formato antiguo para compatibilidad
          conceptsOld: tareo.conceptos?.map(concepto => ({
            type: concepto.plame_configuracion?.codigo || 'otros',
            amount: parseFloat(concepto.monto),
            description: concepto.observaciones,
            plame_configuracion_id: concepto.plame_configuracion_id
          })) || [],
          considerarTasaNocturna: Boolean(tareo.considerar_tasa_nocturna),
          };
        });
        // Un tareo por id: al paginar o fusionar respuestas no debe repetirse el mismo registro
        const seenTareoIds = new Set();
        const uniqueRecords = recordsTransformed.filter((r) => {
          if (r.id == null) return true;
          if (seenTareoIds.has(r.id)) return false;
          seenTareoIds.add(r.id);
          return true;
        });
        setAttendanceRecords(uniqueRecords);
      } else {
        console.error('❌ Error al cargar registros:', response.message);
        setAttendanceRecords([]); // Limpiar en caso de error
      }
    } catch (error) {
      console.error('💥 Error exception al cargar registros:', error);
      setAttendanceRecords([]);
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);
    setFiltroSedeId('');
    setCurrentView('tareos');
    
    // Cargar datos específicos del proyecto
    setLoading(true);
    try {
      await Promise.all([
        loadEmployees(),
        loadPlameConceptos(),
        loadConfiguracionTareo(),
        loadConfiguracionTasaNocturna(),
        loadConfiguracionPlanillaAlternativa(),
      ]);
    } catch (error) {
      console.error('Error al cargar datos del proyecto:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTodasSedes = async () => {
    await handleProjectSelect({
      id: null,
      nombre: 'Todos los proyectos',
      _todasSedes: true,
    });
  };

  const handleOpenGlobalRegularizacion = async () => {
    setImportadorModoGlobal(true);
    setImportadorInitialMode('regularizar');
    setShowImportarTareosModal(true);

    try {
      const tareas = [];
      if (!employees.length) tareas.push(loadEmployees());
      if (!plameConceptos.length) tareas.push(loadPlameConceptos());
      if (tareas.length) await Promise.all(tareas);
    } catch (error) {
      console.error('Error al preparar regularización global:', error);
    }
  };

  const handleOpenGlobalImportacion = async () => {
    setImportadorModoGlobal(true);
    setImportadorInitialMode('importar');
    setShowImportarTareosModal(true);

    try {
      const tareas = [];
      if (!employees.length) tareas.push(loadEmployees());
      if (!plameConceptos.length) tareas.push(loadPlameConceptos());
      if (!configuracionTareo) tareas.push(loadConfiguracionTareo());
      if (tareas.length) await Promise.all(tareas);
    } catch (error) {
      console.error('Error al preparar importación global:', error);
    }
  };

  const handleBackToProjects = () => {
    setCurrentView('project-selection');
    setSelectedProject(null);
    setFiltroSedeId('');
  };

  // ===== FUNCIONES PARA VISTA DE CALENDARIO HORIZONTAL =====
  
  // Función para cambiar a vista de calendario
  const handleOpenCalendarAnalysis = () => {
    setCurrentView('calendar-analysis');
    if (employees.length > 0) {
      setCalendarSelectedEmployees(employees.map(emp => emp.id));
    }
    // Consultar API para el mes que muestra el calendario (mes anterior, actual, siguiente)
    if (selectedCompany?.id && selectedProject) {
      const monthStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
      loadAttendanceRecords(monthStr);
    }
  };

  // Función para volver desde calendario a tareos
  const handleBackToTareos = () => {
    setCurrentView('tareos');
  };

  // Función para generar rango de fechas (manipulación directa de strings)
  const generateDateRange = (startDate, endDate) => {
    // Usar manipulación de fechas sin conversión a Date para evitar problemas de zona horaria
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    
    const dateArray = [];
    let currentDate = new Date(startYear, startMonth - 1, startDay); // Mes 0-indexed
    const finalDate = new Date(endYear, endMonth - 1, endDay);
    
    while (currentDate <= finalDate) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;
      dateArray.push(formattedDate);
      
      // Incrementar día
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dateArray;
  };

  // Función para generar dateRange con columnas de resumen semanal
  const generateDateRangeWithWeeklySummary = (startDate, endDate) => {
    const originalRange = generateDateRange(startDate, endDate);
    const extendedRange = [];
    
    originalRange.forEach((date, index) => {
      // Agregar la fecha normal
      extendedRange.push({ type: 'date', date, isWeeklySummary: false });
      
      // Si es domingo o es el último día, agregar resumen semanal
      const [year, month, day] = date.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      const isDomingo = dateObj.getDay() === 0;
      const isLastDay = index === originalRange.length - 1;
      
      if (isDomingo || isLastDay) {
        extendedRange.push({ 
          type: 'summary', 
          date: `summary_${date}`, 
          isWeeklySummary: true,
          weekEndDate: date 
        });
      }
    });
    
    return extendedRange;
  };

  // Función para calcular resumen semanal
  const calculateWeeklySummary = (group, weekEndDate, extendedRange) => {
    // Encontrar el inicio de la semana
    const weekDates = [];
    let foundEnd = false;
    
    for (let i = extendedRange.length - 1; i >= 0; i--) {
      const item = extendedRange[i];
      if (item.type === 'date') {
        if (item.date === weekEndDate) {
          foundEnd = true;
        }
        if (foundEnd) {
          weekDates.unshift(item.date);
          // Si llegamos al lunes o al inicio, parar
          const [year, month, day] = item.date.split('-').map(Number);
          const dateObj = new Date(year, month - 1, day);
          if (dateObj.getDay() === 1 || i === 0) {
            break;
          }
        }
      }
    }
    
    // Calcular totales de la semana
    const weeklyHours = weekDates.reduce((total, date) => {
      const dayData = group.dailyData.find(d => d.date === date);
      if (dayData) {
        const hours = calendarGroupBy === 'employee' ? dayData.totalHours : dayData.avgHours;
        return total + hours;
      }
      return total;
    }, 0);
    
    // 🆕 NUEVO: Contar faltas en la semana
    const faltasSemana = weekDates.reduce((totalFaltas, date) => {
      const dayData = group.dailyData.find(d => d.date === date);
      if (dayData && dayData.records) {
        const tieneFalta = dayData.records.some(record => record.eventType === 'falta');
        return totalFaltas + (tieneFalta ? 1 : 0);
      }
      return totalFaltas;
    }, 0);
    
    // 🆕 NUEVO: Calcular horas de descanso proporcional
    const horasDescanso = 8 * ((7 - faltasSemana) / 7);
    const proporcionDescanso = (horasDescanso / 8) * 100;
    
    const expectedWeeklyHours = 40; // 8 horas x 5 días laborales
    const balance = weeklyHours - expectedWeeklyHours;
    
    return {
      weeklyHours,
      balance,
      weekDates,
      // 🆕 NUEVO: Agregar datos de descanso
      faltasSemana,
      horasDescanso,
      proporcionDescanso
    };
  };

  // Función para procesar datos del calendario
  const processCalendarData = () => {
    const originalDateRange = generateDateRange(calendarStartDate, calendarEndDate);
    const extendedRange = generateDateRangeWithWeeklySummary(calendarStartDate, calendarEndDate);
    
    let filteredEmployees = employees.filter(emp => 
      calendarSelectedEmployees.includes(emp.id) && empleadoEnAlcanceActual(emp)
    );

    // Aplicar filtros de cargo y área de trabajo
    if (filtrosCargosSeleccionados.length > 0) {
      filteredEmployees = filteredEmployees.filter(emp => 
        filtrosCargosSeleccionados.includes(emp.cargo)
      );
    }

    if (filtrosAreasSeleccionadas.length > 0) {
      filteredEmployees = filteredEmployees.filter(emp => 
        filtrosAreasSeleccionadas.includes(emp.areas_de_trabajo || 'Sin área')
      );
    }

    // Un empleado por id: la vista calendario crea una fila por ítem; duplicados en `employees` duplican filas
    const empleadosUnicosPorId = new Map();
    filteredEmployees.forEach((emp) => {
      if (emp && emp.id != null && !empleadosUnicosPorId.has(emp.id)) {
        empleadosUnicosPorId.set(emp.id, emp);
      }
    });
    filteredEmployees = [...empleadosUnicosPorId.values()];

    // Agrupar datos por empleado o por cargo
    const groupedData = calendarGroupBy === 'employee' 
      ? filteredEmployees.map(employee => ({
          id: employee.id,
          name: employee.nombre_completo,
          position: employee.cargo,
          area: employee.areas_de_trabajo || 'Sin área',
          groupKey: `emp_${employee.id}`,
          groupLabel: employee.nombre_completo,
          groupSubLabel: `${employee.cargo} - ${employee.areas_de_trabajo || 'Sin área'}`
        }))
      : [...new Set(filteredEmployees.map(emp => emp.cargo))].map(cargo => ({
          id: `pos_${cargo}`,
          name: cargo,
          position: cargo,
          groupKey: `pos_${cargo}`,
          groupLabel: cargo,
          groupSubLabel: `${filteredEmployees.filter(emp => emp.cargo === cargo).length} empleados`,
          employees: filteredEmployees.filter(emp => emp.cargo === cargo)
        }));

    // Procesar registros de asistencia para cada grupo
    const calendarData = groupedData.map(group => {
      const dailyData = originalDateRange.map(date => {
        let dayRecords = [];
        
        console.log(`🔍 Procesando fecha del calendario: ${date}`);
        
        if (calendarGroupBy === 'employee') {
          // Para vista por empleado
          dayRecords = attendanceRecords.filter(record => {
            // Las fechas ya vienen como string YYYY-MM-DD desde la transformación
            const match = record.employeeId === group.id && record.date === date;
            if (match) {
              console.log(`✅ Encontrado tareo para empleado ${group.id} en fecha ${date}:`, record);
            }
            return match;
          });
        } else {
          // Para vista por cargo
          const employeesInGroup = group.employees || [];
          dayRecords = attendanceRecords.filter(record => {
            const employee = employees.find(emp => emp.id === record.employeeId);
            // Las fechas ya vienen como string YYYY-MM-DD desde la transformación
            return employee && 
                   employeesInGroup.some(emp => emp.id === record.employeeId) && 
                   record.date === date;
          });
        }

        // Calcular estadísticas del día
        // Para las faltas, las horas deben ser 0, independientemente del valor en hoursWorked
        const totalHours = dayRecords.reduce((sum, record) => {
          const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
          return sum + (status === 'falta' ? 0 : (record.hoursWorked || 0));
        }, 0);
        const avgHours = dayRecords.length > 0 ? totalHours / dayRecords.length : 0;
        const presentCount = dayRecords.filter(record => {
          const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
          return status === 'presente';
        }).length;
        const absentCount = dayRecords.filter(record => {
          const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
          return status === 'falta';
        }).length;
        const tasaNocturnaCount = dayRecords.filter((record) => record.considerarTasaNocturna).length;

        return {
          date,
          records: dayRecords,
          totalHours: calendarGroupBy === 'employee' ? totalHours : totalHours,
          avgHours: calendarGroupBy === 'employee' ? (dayRecords.length > 0 ? totalHours : 0) : avgHours,
          presentCount,
          absentCount,
          recordCount: dayRecords.length,
          hasData: dayRecords.length > 0,
          hasTasaNocturna: tasaNocturnaCount > 0,
          tasaNocturnaCount,
        };
      });

      // Filtrar días que pertenecen al mes seleccionado (excluyendo días de otros meses)
      const daysInSelectedMonth = dailyData.filter(day => {
        const [year, month] = day.date.split('-').map(Number);
        return year === selectedYear && month === selectedMonth + 1;
      });

      // Calcular estadísticas solo para el mes seleccionado
      const workingDaysInSelectedMonth = daysInSelectedMonth.filter(day => day.hasData).length;
      const totalHoursInSelectedMonth = daysInSelectedMonth.reduce((sum, day) => sum + day.totalHours, 0);
      const avgDailyHoursInSelectedMonth = workingDaysInSelectedMonth > 0
        ? daysInSelectedMonth.reduce((sum, day) => sum + (calendarGroupBy === 'employee' ? day.totalHours : day.avgHours), 0) / workingDaysInSelectedMonth
        : 0;

      return {
        ...group,
        dailyData,
        totalHours: dailyData.reduce((sum, day) => sum + day.totalHours, 0),
        avgDailyHours: dailyData.filter(day => day.hasData).length > 0 
          ? dailyData.reduce((sum, day) => sum + day.avgHours, 0) / dailyData.filter(day => day.hasData).length 
          : 0,
        workingDays: dailyData.filter(day => day.hasData).length,
        workingDaysInSelectedMonth,
        totalHoursInSelectedMonth, // Horas totales solo del mes seleccionado
        avgDailyHoursInSelectedMonth, // Promedio diario solo del mes seleccionado
        daysInSelectedMonth, // Array de días del mes seleccionado
        totalPresent: dailyData.reduce((sum, day) => sum + day.presentCount, 0),
        totalAbsent: dailyData.reduce((sum, day) => sum + day.absentCount, 0)
      };
    });

    // Filtrar solo los grupos que tienen al menos un registro en el mes seleccionado
    // (excluyendo días de otros meses que se muestran para completar semanas)
    const filteredCalendarData = calendarData.filter(group => group.workingDaysInSelectedMonth > 0);

    return { calendarData: filteredCalendarData, dateRange: extendedRange, originalDateRange };
  };

  // Función para exportar calendario a Excel
  const exportCalendarToExcel = async () => {
    try {
      const { calendarData, dateRange } = processCalendarData();
      
      // Crear un nuevo workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Calendario de Tareos');

      // Estilos para el header
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4F46E5' } // Color indigo
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const headerDateStyle = {
        font: { bold: true, size: 10 },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFE5E7EB' } // Color gris claro
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const headerWeekStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF0D9488' } // Color teal
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      // Crear header de fechas (se agregará título después de calcular todas las columnas)
      const headerRow = worksheet.addRow([]);
      headerRow.height = 30;

      // Primera columna: Empleado/Cargo
      const firstHeaderCell = headerRow.getCell(1);
      firstHeaderCell.value = calendarGroupBy === 'employee' ? 'Empleado' : 'Cargo';
      Object.assign(firstHeaderCell, headerStyle);
      worksheet.getColumn(1).width = 30;

      // Agregar columnas de fechas y resúmenes semanales
      let colIndex = 2;
      dateRange.forEach((item, index) => {
        const cell = headerRow.getCell(colIndex);
        
        if (item.type === 'date') {
          const formattedDate = formatDateForCalendar(item.date, selectedMonth);
          cell.value = `${formattedDate.day}\n${formattedDate.weekday}\n${formattedDate.month}`;
          Object.assign(cell, headerDateStyle);
          
          // Colorear fin de semana
          if (formattedDate.isWeekend) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFDBEAFE' } // Azul claro
            };
          }
          
          // Colorear días fuera del período
          if (formattedDate.isOutsideSelectedPeriod) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF3F4F6' } // Gris claro
            };
            cell.font = { ...cell.font, color: { argb: 'FF9CA3AF' } };
          }
          
          worksheet.getColumn(colIndex).width = 12;
        } else {
          // Resumen semanal
          cell.value = '📊\nSemana';
          Object.assign(cell, headerWeekStyle);
          worksheet.getColumn(colIndex).width = 15;
        }
        
        colIndex++;
      });

      // ===== CALCULAR RESUMEN DE EVENTOS Y HORAS ANTES DE AGREGAR COLUMNAS =====
      // Mapeo de tipos de eventos a etiquetas
      const eventTypeLabels = {
        'asistio': '✅ Asistió',
        'falta': '❌ Falta',
        'permiso': '📝 Permiso',
        'suspension': '⛔ Suspensión',
        'vacaciones': '🏖️ Vacaciones',
        'feriado': '🎉 Feriado',
        'feriado_laborado': '💼 Feriado Laborado',
        'descanso': '😴 Descanso',
        'descanso_laborado': '💪 Descanso Laborado',
        'descanso_medico': '🏥 Descanso Médico',
        'reposo_medico': '🛏️ Reposo Médico',
        'subsidio_por_enfermedad': '💊 Subsidio',
        'licencia_con_goce_de_haber': '🧾 Lic. Goce',
        'tardanza': '⏰ Tardanza'
      };

      // Calcular resumen de eventos y horas por trabajador
      const eventsSummary = calendarData.map(group => {
        const eventCounts = {};
        const eventHours = {}; // Nuevo: horas por tipo de evento
        
        // Inicializar todos los tipos de eventos en 0
        Object.keys(eventTypeLabels).forEach(eventType => {
          eventCounts[eventType] = 0;
          eventHours[eventType] = 0; // Inicializar horas
        });

        // Contar días y sumar horas por tipo de evento SOLO DEL MES SELECCIONADO
        // Usar daysInSelectedMonth si existe, sino filtrar por fecha
        const daysToProcess = group.daysInSelectedMonth || group.dailyData.filter(day => {
          const [year, month] = day.date.split('-').map(Number);
          return year === selectedYear && month === selectedMonth + 1;
        });

        daysToProcess.forEach(day => {
          if (day.records && day.records.length > 0) {
            // Obtener tipos de eventos únicos del día
            const dayEventTypes = new Set();
            
            day.records.forEach(record => {
              const eventType = record.eventType || 'asistio';
              dayEventTypes.add(eventType);
            });
            
            // Por cada tipo de evento único del día, sumar las horas de ese tipo
            dayEventTypes.forEach(eventType => {
              // Contar el día
              if (eventCounts.hasOwnProperty(eventType)) {
                eventCounts[eventType]++;
              } else {
                eventCounts[eventType] = 1;
              }
              
              // Sumar horas de todos los registros de este tipo en el día
              const hoursForThisType = day.records
                .filter(record => (record.eventType || 'asistio') === eventType)
                .reduce((sum, record) => {
                  const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
                  return sum + (status === 'falta' ? 0 : (record.hoursWorked || 0));
                }, 0);
              
              eventHours[eventType] += hoursForThisType;
            });
          }
        });

        return {
          events: eventCounts,
          hours: eventHours // Agregar horas por evento
        };
      });

      // Obtener todos los tipos de eventos únicos encontrados
      const allEventTypes = new Set();
      eventsSummary.forEach(summary => {
        Object.keys(summary.events).forEach(eventType => {
          if (summary.events[eventType] > 0) {
            allEventTypes.add(eventType);
          }
        });
      });

      // Ordenar tipos de eventos en un orden lógico
      const orderedEventTypes = [
        'asistio',
        'falta',
        'tardanza',
        'permiso',
        'suspension',
        'vacaciones',
        'descanso',
        'descanso_laborado',
        'descanso_medico',
        'reposo_medico',
        'subsidio_por_enfermedad',
        'licencia_con_goce_de_haber',
        'feriado',
        'feriado_laborado'
      ].filter(type => allEventTypes.has(type));

      // Agregar otros tipos de eventos no incluidos en la lista
      allEventTypes.forEach(type => {
        if (!orderedEventTypes.includes(type)) {
          orderedEventTypes.push(type);
        }
      });

      // Filtrar eventos que siempre tienen 0 horas (falta, permiso, descanso) para el resumen de horas
      const eventTypesWithHours = orderedEventTypes.filter(eventType => 
        !['falta', 'permiso', 'suspension', 'descanso'].includes(eventType)
      );

      // Calcular total de columnas para el título: 1 (empleado) + dateRange.length (fechas/semanas) + eventTypesWithHours.length (resumen horas por evento) + 1 (resumen total) + tasa nocturna + orderedEventTypes.length (eventos días)
      const totalColumns = 1 + dateRange.length + eventTypesWithHours.length + 1 + (tasaNocturnaActiva ? 1 : 0) + orderedEventTypes.length;

      // Agregar título (antes de crear el headerRow, pero necesitamos saber las columnas)
      // El título se agregará después del headerRow
      
      // Agregar headers de resumen de horas por evento ANTES del resumen total (solo eventos con horas)
      eventTypesWithHours.forEach(eventType => {
        const hoursHeaderCell = headerRow.getCell(colIndex);
        hoursHeaderCell.value = `Hrs ${eventTypeLabels[eventType] || eventType}`;
        Object.assign(hoursHeaderCell, headerStyle);
        worksheet.getColumn(colIndex).width = 16;
        colIndex++;
      });

      // Columna de resumen total
      const summaryHeaderCell = headerRow.getCell(colIndex);
      summaryHeaderCell.value = 'Resumen Total';
      Object.assign(summaryHeaderCell, headerStyle);
      worksheet.getColumn(colIndex).width = 20;
      colIndex++;

      if (tasaNocturnaActiva) {
        const tasaNocturnaHeaderCell = headerRow.getCell(colIndex);
        tasaNocturnaHeaderCell.value = '🌙 T. Noct.';
        Object.assign(tasaNocturnaHeaderCell, headerStyle);
        worksheet.getColumn(colIndex).width = 14;
        colIndex++;
      }

      // Agregar headers de eventos (días) después del resumen total
      orderedEventTypes.forEach(eventType => {
        const eventHeaderCell = headerRow.getCell(colIndex);
        eventHeaderCell.value = eventTypeLabels[eventType] || eventType;
        Object.assign(eventHeaderCell, headerStyle);
        worksheet.getColumn(colIndex).width = 16;
        colIndex++;
      });

      // Ahora que tenemos todas las columnas, agregar el título y subtítulo al inicio
      // Insertar filas al inicio
      worksheet.insertRow(1, [`Calendario de Tareos - ${selectedProject?.nombre || 'Proyecto'}`]);
      worksheet.insertRow(2, [`Período: ${formatDate(calendarStartDate)} - ${formatDate(calendarEndDate)}`]);
      worksheet.insertRow(3, []); // Fila vacía
      
      // Estilizar título
      const titleCell = worksheet.getCell(1, 1);
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' }
      };
      worksheet.mergeCells(1, 1, 1, totalColumns);
      worksheet.getRow(1).height = 30;

      // Estilizar subtítulo
      const subtitleCell = worksheet.getCell(2, 1);
      subtitleCell.font = { size: 11, italic: true };
      subtitleCell.alignment = { vertical: 'middle', horizontal: 'left' };
      worksheet.mergeCells(2, 1, 2, totalColumns);
      worksheet.getRow(2).height = 20;

      // Agregar datos de cada grupo (empleado o cargo)
      calendarData.forEach((group) => {
        const dataRow = worksheet.addRow([]);
        dataRow.height = 25;

        // Primera columna: Nombre del empleado/cargo
        const nameCell = dataRow.getCell(1);
        nameCell.value = group.groupLabel;
        nameCell.font = { bold: true };
        nameCell.alignment = { vertical: 'middle' };
        nameCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };

        // Agregar sublabel como nota
        if (group.groupSubLabel) {
          nameCell.note = group.groupSubLabel;
        }

        // Agregar datos de cada día
        colIndex = 2;
        dateRange.forEach((item) => {
          const cell = dataRow.getCell(colIndex);
          
          if (item.type === 'date') {
            const day = group.dailyData.find(d => d.date === item.date);
            
            if (day && day.hasData) {
              const hours = calendarGroupBy === 'employee' ? day.totalHours : day.avgHours;
              const hoursText = hours.toFixed(2);
              if (tasaNocturnaActiva && day.hasTasaNocturna) {
                const tasaLabel = calendarGroupBy === 'position' && day.tasaNocturnaCount > 1
                  ? `🌙 ${day.tasaNocturnaCount}`
                  : '🌙 Sí';
                cell.value = `${hoursText}\n${tasaLabel}`;
              } else {
                cell.value = hoursText;
              }
              
              // Aplicar colores según las horas (similar a getCellColor)
              if (day.records && day.records.some(r => r.eventType === 'descanso')) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9D5FF' } };
              } else if (day.records && day.records.some(r => r.eventType === 'descanso_medico' || r.eventType === 'subsidio_por_enfermedad' || r.eventType === 'licencia_con_goce_de_haber')) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4CC' } };
              } else if (day.records && day.records.some(r => r.eventType === 'permiso' || r.eventType === 'suspension')) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFDE68A' } };
              } else if (day.records && day.records.some(r => r.eventType === 'feriado')) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBAE6FD' } };
              } else if (day.records && day.records.some(r => r.eventType === 'falta')) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEF4444' } };
              } else if (hours === 0) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
              } else if (hours < 2) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCA5A5' } };
              } else if (hours < 4) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFD89E' } };
              } else if (hours < 6) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF59D' } };
              } else if (hours < 8) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBEF264' } };
              } else if (hours === 8) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4ADE80' } };
              } else if (hours > 10) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3B82F6' } };
              }

              // Agregar información adicional como nota
              const eventTypes = [...new Set(day.records.map(r => r.eventType))];
              const eventLabels = eventTypes.map(et => {
                if (et === 'asistio') return '✅ Asistió';
                if (et === 'falta') return '❌ Falta';
                if (et === 'permiso') return '📝 Permiso';
                if (et === 'suspension') return '⛔ Suspensión';
                if (et === 'vacaciones') return '🏖️ Vacaciones';
                if (et === 'feriado') return '🎉 Feriado';
                if (et === 'feriado_laborado') return '💼 Feriado Laborado';
                if (et === 'descanso') return '😴 Descanso';
                if (et === 'descanso_laborado') return '💪 Descanso Laborado';
                if (et === 'descanso_medico') return '🏥 Descanso Médico';
                if (et === 'reposo_medico') return '🛏️ Reposo Médico';
                if (et === 'subsidio_por_enfermedad') return '💊 Subsidio';
                if (et === 'licencia_con_goce_de_haber') return '🧾 Lic. Goce';
                if (et === 'tardanza') return '⏰ Tardanza';
                return et;
              }).join(', ');

              const tasaNocturnaNote = tasaNocturnaActiva && day.hasTasaNocturna
                ? `\nTasa nocturna: ${day.tasaNocturnaCount} registro(s)`
                : '';

              cell.note = `${day.date}\nHoras: ${hours.toFixed(2)}h\nPresentes: ${day.presentCount} | Ausentes: ${day.absentCount}\nEventos: ${eventLabels}${tasaNocturnaNote}`;
            } else {
              cell.value = '-';
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
            }
          } else {
            // Resumen semanal
            const weeklySummary = calculateWeeklySummary(group, item.weekEndDate, dateRange);
            cell.value = `${weeklySummary.weeklyHours.toFixed(2)}h`;
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFCCFBF1' } };
            cell.font = { bold: true };
            
            if (configuracionTareo?.descontar_descansos) {
              cell.note = `Semana terminada el ${item.weekEndDate}\nHoras trabajadas: ${weeklySummary.weeklyHours.toFixed(2)}h\nFaltas: ${weeklySummary.faltasSemana} día(s)\nDescanso ganado: ${weeklySummary.horasDescanso.toFixed(2)}h de 8.0h\nProporción: ${weeklySummary.proporcionDescanso.toFixed(1)}%`;
            } else {
              cell.note = `Semana terminada el ${item.weekEndDate}\nHoras trabajadas: ${weeklySummary.weeklyHours.toFixed(2)}h\nFaltas: ${weeklySummary.faltasSemana} día(s)`;
            }
          }
          
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          
          colIndex++;
        });

        // Agregar columnas de resumen de HORAS por evento (ANTES del resumen total)
        // Solo eventos que pueden tener horas (excluir falta, permiso, descanso)
        let currentColIndex = colIndex;
        const eventSummaryData = eventsSummary[calendarData.indexOf(group)];
        eventTypesWithHours.forEach(eventType => {
          const hoursCell = dataRow.getCell(currentColIndex);
          const hours = eventSummaryData?.hours[eventType] || 0;
          hoursCell.value = hours > 0 ? `${hours.toFixed(2)}h` : '-';
          
          // Colorear celdas según el tipo de evento
          if (hours > 0) {
            if (eventType === 'asistio') {
              hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } }; // Verde claro
            } else if (eventType === 'vacaciones') {
              hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }; // Azul claro
            } else if (eventType === 'descanso_medico' || eventType === 'reposo_medico' || eventType === 'subsidio_por_enfermedad' || eventType === 'licencia_con_goce_de_haber') {
              hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4CC' } }; // Naranja claro
            } else if (eventType === 'descanso_laborado') {
              hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9D5FF' } }; // Morado claro
            } else if (eventType === 'feriado' || eventType === 'feriado_laborado') {
              hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }; // Azul claro
            } else {
              hoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }; // Gris claro
            }
          }
          
          hoursCell.alignment = { horizontal: 'center', vertical: 'middle' };
          hoursCell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          
          currentColIndex++;
        });

        // Columna de resumen total (SOLO DEL MES SELECCIONADO)
        const summaryCell = dataRow.getCell(currentColIndex);
        const totalHoursMonth = group.totalHoursInSelectedMonth || 0;
        const avgDailyHoursMonth = group.avgDailyHoursInSelectedMonth || 0;
        const workingDaysMonth = group.workingDaysInSelectedMonth || 0;
        summaryCell.value = `${totalHoursMonth.toFixed(2)}h\nPromedio: ${avgDailyHoursMonth.toFixed(2)}h/día\nDías: ${workingDaysMonth}`;
        summaryCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        summaryCell.font = { bold: true };
        summaryCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } };
        summaryCell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };

        if (calendarGroupBy === 'position') {
          summaryCell.note = `Presentes: ${group.totalPresent} | Ausentes: ${group.totalAbsent}`;
        }
        currentColIndex++;

        if (tasaNocturnaActiva) {
          const tasaNocturnaCell = dataRow.getCell(currentColIndex);
          const diasConTasaNocturna = (group.daysInSelectedMonth || group.dailyData).filter((day) => {
            const [year, month] = day.date.split('-').map(Number);
            const enMesSeleccionado = year === selectedYear && month === selectedMonth + 1;
            return enMesSeleccionado && day.hasTasaNocturna;
          }).length;
          tasaNocturnaCell.value = diasConTasaNocturna > 0 ? diasConTasaNocturna : '-';
          if (diasConTasaNocturna > 0) {
            tasaNocturnaCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } };
            tasaNocturnaCell.font = { color: { argb: 'FF3730A3' }, bold: true };
          }
          tasaNocturnaCell.alignment = { horizontal: 'center', vertical: 'middle' };
          tasaNocturnaCell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          tasaNocturnaCell.note = 'Días del mes con tasa nocturna considerada';
          currentColIndex++;
        }

        // Agregar columnas de resumen de eventos (días)
        // Reutilizar eventSummaryData ya declarado arriba
        orderedEventTypes.forEach(eventType => {
          const eventCell = dataRow.getCell(currentColIndex);
          const count = eventSummaryData?.events[eventType] || 0;
          eventCell.value = count;
          
          // Colorear celdas según el tipo de evento
          if (count > 0) {
            if (eventType === 'asistio') {
              eventCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } }; // Verde claro
            } else if (eventType === 'falta') {
              eventCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // Rojo claro
            } else if (eventType === 'vacaciones') {
              eventCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }; // Azul claro
            } else if (eventType === 'descanso_medico' || eventType === 'reposo_medico' || eventType === 'subsidio_por_enfermedad' || eventType === 'licencia_con_goce_de_haber') {
              eventCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4CC' } }; // Naranja claro
            } else if (eventType === 'descanso') {
              eventCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9D5FF' } }; // Morado claro
            } else {
              eventCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }; // Gris claro
            }
          }
          
          eventCell.alignment = { horizontal: 'center', vertical: 'middle' };
          eventCell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          
          currentColIndex++;
        });
      });

      // Agregar fila de totales
      const totalsRow = worksheet.addRow([]);
      totalsRow.height = 30;
      const totalsLabelCell = totalsRow.getCell(1);
      totalsLabelCell.value = 'TOTALES';
      totalsLabelCell.font = { bold: true, size: 12 };
      totalsLabelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      totalsLabelCell.font.color = { argb: 'FFFFFFFF' };
      totalsLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
      totalsLabelCell.border = {
        top: { style: 'medium' },
        left: { style: 'thin' },
        bottom: { style: 'medium' },
        right: { style: 'thin' }
      };

      colIndex = 2;
      dateRange.forEach((item) => {
        const cell = totalsRow.getCell(colIndex);
        
        if (item.type === 'date') {
          // Solo sumar horas si el día pertenece al mes seleccionado
          const [year, month] = item.date.split('-').map(Number);
          const isInSelectedMonth = year === selectedYear && month === selectedMonth + 1;
          
          const totalHours = isInSelectedMonth 
            ? calendarData.reduce((sum, group) => {
                const day = group.dailyData.find(d => d.date === item.date);
                if (day && day.hasData) {
                  return sum + (calendarGroupBy === 'employee' ? day.totalHours : day.avgHours);
                }
                return sum;
              }, 0)
            : 0; // Si no es del mes seleccionado, mostrar 0
          
          cell.value = totalHours.toFixed(2);
        } else {
          // Total de resumen semanal
          const totalWeeklyHours = calendarData.reduce((sum, group) => {
            const weeklySummary = calculateWeeklySummary(group, item.weekEndDate, dateRange);
            return sum + weeklySummary.weeklyHours;
          }, 0);
          
          cell.value = `${totalWeeklyHours.toFixed(2)}h`;
        }
        
        cell.font = { bold: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
        
        colIndex++;
      });

      // Totales de horas por tipo de evento (ANTES del resumen total)
      // Solo eventos que pueden tener horas (excluir falta, permiso, descanso)
      eventTypesWithHours.forEach(eventType => {
        const totalHoursCell = totalsRow.getCell(colIndex);
        const totalHours = eventsSummary.reduce((sum, summary) => sum + (summary.hours[eventType] || 0), 0);
        totalHoursCell.value = totalHours > 0 ? `${totalHours.toFixed(2)}h` : '-';
        totalHoursCell.font = { bold: true };
        totalHoursCell.alignment = { horizontal: 'center', vertical: 'middle' };
        totalHoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
        totalHoursCell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
        colIndex++;
      });

      // Total general (resumen total) - SOLO DEL MES SELECCIONADO
      const grandTotalCell = totalsRow.getCell(colIndex);
      const grandTotal = calendarData.reduce((sum, group) => sum + (group.totalHoursInSelectedMonth || 0), 0);
      grandTotalCell.value = `${grandTotal.toFixed(2)}h`;
      grandTotalCell.font = { bold: true, size: 12 };
      grandTotalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      grandTotalCell.font.color = { argb: 'FFFFFFFF' };
      grandTotalCell.alignment = { horizontal: 'center', vertical: 'middle' };
      grandTotalCell.border = {
        top: { style: 'medium' },
        left: { style: 'thin' },
        bottom: { style: 'medium' },
        right: { style: 'thin' }
      };
      colIndex++;

      if (tasaNocturnaActiva) {
        const totalTasaNocturnaCell = totalsRow.getCell(colIndex);
        const totalDiasTasaNocturna = calendarData.reduce((sum, group) => {
          const dias = (group.daysInSelectedMonth || group.dailyData).filter((day) => {
            const [year, month] = day.date.split('-').map(Number);
            return year === selectedYear && month === selectedMonth + 1 && day.hasTasaNocturna;
          }).length;
          return sum + dias;
        }, 0);
        totalTasaNocturnaCell.value = totalDiasTasaNocturna > 0 ? totalDiasTasaNocturna : '-';
        totalTasaNocturnaCell.font = { bold: true };
        totalTasaNocturnaCell.alignment = { horizontal: 'center', vertical: 'middle' };
        totalTasaNocturnaCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
        totalTasaNocturnaCell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
        colIndex++;
      }

      // Totales por tipo de evento (días)
      orderedEventTypes.forEach(eventType => {
        const totalCell = totalsRow.getCell(colIndex);
        const total = eventsSummary.reduce((sum, summary) => sum + (summary.events[eventType] || 0), 0);
        totalCell.value = total;
        totalCell.font = { bold: true };
        totalCell.alignment = { horizontal: 'center', vertical: 'middle' };
        totalCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
        totalCell.border = {
          top: { style: 'medium' },
          left: { style: 'thin' },
          bottom: { style: 'medium' },
          right: { style: 'thin' }
        };
        colIndex++;
      });

      // Los datos del resumen de eventos ya están incluidos en la hoja principal

      // Generar el archivo Excel
      const buffer = await workbook.xlsx.writeBuffer();
      
      // Crear un blob y descargar
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `Calendario_Tareos_${selectedProject?.nombre || 'Proyecto'}_${formatDate(calendarStartDate)}_${formatDate(calendarEndDate)}.xlsx`;
      link.download = fileName.replace(/[^a-z0-9]/gi, '_');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      alert('Error al exportar el calendario a Excel. Por favor, intente nuevamente.');
    }
  };

  // Función para exportar registros de tareos a Excel
  const exportRecordsToExcel = async () => {
    try {
      // Calcular los registros filtrados actuales (aplicando todos los filtros)
      const recordsToExport = filterRecordsByDate(attendanceRecords);
      
      if (recordsToExport.length === 0) {
        alert('No hay registros para exportar.');
        return;
      }

      // Crear un nuevo workbook
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Registros de Tareos');

      // Estilos
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF4F46E5' } // Color indigo
        },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      // Título
      const titleRow = worksheet.addRow([`Registros de Tareos - ${selectedProject?.nombre || 'Proyecto'}`]);
      titleRow.height = 30;
      const titleCell = titleRow.getCell(1);
      titleCell.font = { bold: true, size: 14 };
      titleCell.alignment = { vertical: 'middle', horizontal: 'left' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF3F4F6' }
      };

      // Subtítulo con información del filtro
      const periodLabel = filterValue ? (filterPeriod === 'day' ? ` | Día: ${filterValue}` : filterPeriod === 'week' ? (() => { const r = getWeekRangeFromDate(filterValue); return ` | Semana: ${r.start} a ${r.end}`; })() : ` | Mes: ${filterValue}`) : '';
      const subtitleText = `Total de registros: ${recordsToExport.length}${periodLabel}`;
      const exportHeaders = [
        'Empleado',
        'Cargo',
        'Fecha',
        'Tipo de Evento',
        'Horario Teórico',
        'Horario Real',
        'Horas Trabajadas',
        ...(tasaNocturnaActiva ? ['T. Noct.'] : []),
        'Conceptos',
        'Notas'
      ];
      const totalExportColumns = exportHeaders.length;
      const conceptosColIndex = tasaNocturnaActiva ? 9 : 8;
      const notasColIndex = tasaNocturnaActiva ? 10 : 9;
      const tasaNocturnaColIndex = 8;
      const subtitleRow = worksheet.addRow([subtitleText]);
      subtitleRow.height = 20;
      const subtitleCell = subtitleRow.getCell(1);
      subtitleCell.font = { size: 11, italic: true };
      subtitleCell.alignment = { vertical: 'middle', horizontal: 'left' };
      worksheet.mergeCells(2, 1, 2, totalExportColumns);

      // Fila vacía
      worksheet.addRow([]);

      // Headers
      const headerRow = worksheet.addRow(exportHeaders);
      headerRow.height = 30;

      // Aplicar estilo a headers
      headerRow.eachCell((cell, colNumber) => {
        Object.assign(cell, headerStyle);
      });

      // Anchos de columna
      worksheet.getColumn(1).width = 35; // Empleado
      worksheet.getColumn(2).width = 25; // Cargo
      worksheet.getColumn(3).width = 12; // Fecha
      worksheet.getColumn(4).width = 18; // Tipo
      worksheet.getColumn(5).width = 18; // Horario Teórico
      worksheet.getColumn(6).width = 18; // Horario Real
      worksheet.getColumn(7).width = 15; // Horas
      if (tasaNocturnaActiva) {
        worksheet.getColumn(tasaNocturnaColIndex).width = 12; // T. Noct.
      }
      worksheet.getColumn(conceptosColIndex).width = 30; // Conceptos
      worksheet.getColumn(notasColIndex).width = 30; // Notas

      // Mapeo de tipos de eventos
      const eventTypeLabels = {
        'asistio': 'Asistió',
        'falta': 'Falta',
        'permiso': 'Permiso',
        'suspension': 'Suspensión',
        'vacaciones': 'Vacaciones',
        'feriado': 'Feriado',
        'feriado_laborado': 'Feriado Laborado',
        'descanso': 'Descanso',
        'descanso_laborado': 'Descanso Laborado',
        'descanso_medico': 'Descanso Médico',
        'reposo_medico': 'Reposo Médico',
        'subsidio_por_enfermedad': 'Subsidio por Enfermedad',
        'licencia_con_goce_de_haber': 'Licencia con Goce de Haber',
        'tardanza': 'Tardanza'
      };

      // Agregar datos
      recordsToExport.forEach((record) => {
        const employee = getEmployeeInfo(record.employeeId);
        const eventTypeLabel = eventTypeLabels[record.eventType] || record.eventType;
        
        // Formatear conceptos
        let conceptosText = '-';
        if (record.concepts && record.concepts.length > 0) {
          conceptosText = record.concepts.map(concept => {
            const conceptInfo = getConceptInfo(concept.plame_configuracion_id, concept);
            const sign = concept.tipo_concepto === 'adelanto' || concept.tipo_concepto === 'ingreso' ? '+' : '-';
            const extra = [
              conceptInfo?.codigo ? `[${conceptInfo.codigo}]` : null,
              concept.tipo_boleta && concept.tipo_boleta !== 'plame' ? `(${concept.tipo_boleta})` : null,
              concept.observaciones || null,
            ].filter(Boolean).join(' ');
            return `${conceptInfo?.nombre || 'Concepto'}${extra ? ` ${extra}` : ''}: ${sign}S/ ${parseFloat(concept.monto || 0).toFixed(2)}`;
          }).join('\n');
        }

        const dataRow = worksheet.addRow([
          employee?.name || 'N/A',
          employee?.position || 'N/A',
          formatDate(record.date),
          eventTypeLabel,
          record.theoreticalCheckIn && record.theoreticalCheckOut 
            ? `${record.theoreticalCheckIn} - ${record.theoreticalCheckOut}` 
            : '-',
          record.checkIn && record.checkOut 
            ? `${record.checkIn} - ${record.checkOut}` 
            : '-',
          getDisplayHours(record),
          ...(tasaNocturnaActiva ? [record.considerarTasaNocturna ? 'Sí' : 'No'] : []),
          conceptosText,
          record.observaciones || '-'
        ]);

        dataRow.height = 25;

        // Aplicar estilos y colores según tipo de evento
        dataRow.eachCell((cell, colNumber) => {
          cell.alignment = { 
            horizontal: colNumber === conceptosColIndex || colNumber === notasColIndex ? 'left' : 'center', 
            vertical: 'middle',
            wrapText: colNumber === conceptosColIndex || colNumber === notasColIndex
          };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };

          // Colorear según tipo de evento (columna 4)
          if (colNumber === 4) {
            if (record.eventType === 'asistio') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD1FAE5' } }; // Verde claro
            } else if (record.eventType === 'falta') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // Rojo claro
            } else if (record.eventType === 'vacaciones') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }; // Azul claro
            } else if (record.eventType === 'descanso_medico' || record.eventType === 'reposo_medico' || record.eventType === 'subsidio_por_enfermedad' || record.eventType === 'licencia_con_goce_de_haber') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE4CC' } }; // Naranja claro
            } else if (record.eventType === 'descanso') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE9D5FF' } }; // Morado claro
            }
          }

          // Colorear horas si hay diferencia entre teórico y real
          if (colNumber === 6 && (record.checkIn !== record.theoreticalCheckIn || record.checkOut !== record.theoreticalCheckOut)) {
            cell.font = { color: { argb: 'FFDC2626' }, bold: true }; // Rojo
          }

          if (tasaNocturnaActiva && colNumber === tasaNocturnaColIndex && record.considerarTasaNocturna) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E7FF' } };
            cell.font = { color: { argb: 'FF3730A3' }, bold: true };
          }
        });
      });

      // Fila de totales
      const totalsRow = worksheet.addRow([]);
      totalsRow.height = 30;

      const totalsLabelCell = totalsRow.getCell(1);
      totalsLabelCell.value = 'TOTALES';
      totalsLabelCell.font = { bold: true, size: 12 };
      totalsLabelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      totalsLabelCell.font.color = { argb: 'FFFFFFFF' };
      totalsLabelCell.alignment = { horizontal: 'center', vertical: 'middle' };
      totalsLabelCell.border = {
        top: { style: 'medium' },
        left: { style: 'thin' },
        bottom: { style: 'medium' },
        right: { style: 'thin' }
      };

      // Total de horas
      const totalHours = recordsToExport.reduce((sum, record) => {
        const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
        return sum + (status === 'falta' ? 0 : (record.hoursWorked || 0));
      }, 0);

      const totalHoursCell = totalsRow.getCell(7);
      totalHoursCell.value = `${totalHours.toFixed(2)}h`;
      totalHoursCell.font = { bold: true, size: 12 };
      totalHoursCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F46E5' } };
      totalHoursCell.font.color = { argb: 'FFFFFFFF' };
      totalHoursCell.alignment = { horizontal: 'center', vertical: 'middle' };
      totalHoursCell.border = {
        top: { style: 'medium' },
        left: { style: 'thin' },
        bottom: { style: 'medium' },
        right: { style: 'thin' }
      };

      // Total de registros
      const totalRecordsCell = totalsRow.getCell(3);
      totalRecordsCell.value = `Total: ${recordsToExport.length} registros`;
      totalRecordsCell.font = { bold: true };
      totalRecordsCell.alignment = { horizontal: 'center', vertical: 'middle' };
      totalRecordsCell.border = {
        top: { style: 'medium' },
        left: { style: 'thin' },
        bottom: { style: 'medium' },
        right: { style: 'thin' }
      };

      // Generar el archivo Excel
      const buffer = await workbook.xlsx.writeBuffer();
      
      // Crear un blob y descargar
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `Registros_Tareos_${selectedProject?.nombre || 'Proyecto'}_${formatDate(selectedDate)}.xlsx`;
      link.download = fileName.replace(/[^a-z0-9]/gi, '_');
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      alert('Error al exportar los registros a Excel. Por favor, intente nuevamente.');
    }
  };

  // Función para obtener color de celda basado en horas y tipo de evento
  const getCellColor = (hours, isGroupView = false, dayRecords = []) => {
    // Verificar si hay descansos médicos o subsidios por enfermedad en los registros (8 horas)
    const hasMedicalRecord = dayRecords && dayRecords.some(record => 
      record.eventType === 'descanso_medico' || record.eventType === 'subsidio_por_enfermedad' || record.eventType === 'licencia_con_goce_de_haber'
    );
    
    // Color específico para descansos médicos y subsidios por enfermedad (8 horas)
    if (hasMedicalRecord) {
      return 'bg-orange-200 text-orange-800'; // Color naranja para registros médicos
    }
    
    // Verificar si hay días de descanso normal en los registros (0 horas)
    const hasDescanso = dayRecords && dayRecords.some(record => 
      record.eventType === 'descanso'
    );
    
    // Color específico para días de descanso normal
    if (hasDescanso) {
      return 'bg-purple-200 text-purple-800'; // Color morado para descanso
    }

    // Permiso: 0h pero es día justificado; evitar confundir con "sin datos" (gris)
    const hasPermisoOSuspension = dayRecords && dayRecords.some(record =>
      record.eventType === 'permiso' || record.eventType === 'suspension'
    );
    if (hasPermisoOSuspension) {
      return 'bg-amber-200 text-amber-900';
    }

    // Feriado no laborado (0h): distinto de permiso y de celda vacía
    const hasFeriado = dayRecords && dayRecords.some(record => record.eventType === 'feriado');
    if (hasFeriado) {
      return 'bg-sky-200 text-sky-900';
    }

    // Verificar si hay faltas en los registros del día
    const hasFalta = dayRecords && dayRecords.some(record => {
      const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
      return status === 'falta';
    });
    
    // Color rojo específico para faltas (siempre debe mostrar 0 horas)
    if (hasFalta) {
      return 'bg-red-500 text-white'; // Color rojo intenso para faltas
    }
    
    // Si no hay faltas pero hours es 0, es que no hay datos
    if (hours === 0) return 'bg-gray-100 text-gray-400';
    
    if (isGroupView) {
      // Para vista por cargo (promedio de horas)
      if (hours >= 8) return 'bg-green-500 text-white';
      if (hours >= 6) return 'bg-green-300 text-green-900';
      if (hours >= 4) return 'bg-yellow-300 text-yellow-900';
      if (hours >= 2) return 'bg-orange-300 text-orange-900';
      return 'bg-red-300 text-red-900';
    } else {
      // Para vista individual
      if (hours >= 10) return 'bg-blue-500 text-white'; // Horas extra
      if (hours >= 8) return 'bg-green-500 text-white'; // Jornada completa
      if (hours >= 6) return 'bg-green-300 text-green-900'; // Casi completa
      if (hours >= 4) return 'bg-yellow-300 text-yellow-900'; // Media jornada
      if (hours >= 2) return 'bg-orange-300 text-orange-900'; // Pocas horas
      return 'bg-red-300 text-red-900'; // Muy pocas horas
    }
  };

  // Función para formatear fecha para mostrar (usando strings para evitar zona horaria)
  const formatDateForCalendar = (dateString, selectedPeriodMonth = null) => {
    // Extraer año, mes, día directamente del string YYYY-MM-DD
    const [year, month, day] = dateString.split('-').map(Number);
    
    // Crear fecha con zona horaria local explícita
    const date = new Date(year, month - 1, day); // month es 0-indexed
    
    const monthNames = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 
                       'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
    const weekdayNames = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
    
    // Determinar si la fecha está fuera del período seleccionado (para vista de mes)
    const isOutsideSelectedPeriod = selectedPeriodMonth !== null && month !== selectedPeriodMonth + 1;
    
    console.log(`📅 Formateando fecha: ${dateString} -> Día: ${day}, WeekDay: ${date.getDay()} (${weekdayNames[date.getDay()]}), Outside period: ${isOutsideSelectedPeriod}`);
    
    return {
      day: day.toString().padStart(2, '0'),
      month: monthNames[month - 1],
      weekday: weekdayNames[date.getDay()],
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      isOutsideSelectedPeriod
    };
  };

  // Función para obtener el número de semana del año
  const getWeekOfYear = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Función para obtener el rango de fechas de una semana específica
  const getWeekDateRange = (year, weekNumber) => {
    // Crear fecha del 4 de enero (que siempre está en la primera semana del año según ISO)
    const jan4 = new Date(year, 0, 4);
    // Encontrar el lunes de la primera semana
    const firstMonday = new Date(jan4);
    firstMonday.setDate(jan4.getDate() - jan4.getDay() + 1);
    
    // Calcular el inicio de la semana específica
    const weekStart = new Date(firstMonday);
    weekStart.setDate(firstMonday.getDate() + (weekNumber - 1) * 7);
    
    // El final es 6 días después del inicio
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    return { start: weekStart, end: weekEnd };
  };

  // Función para obtener la etiqueta descriptiva de una semana
  const getCurrentWeekLabel = (year, weekNumber) => {
    const { start, end } = getWeekDateRange(year, weekNumber);
    const formatDate = (date) => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      return `${day}/${month}`;
    };
    return `(${formatDate(start)} - ${formatDate(end)})`;
  };

  // Función para manejar selección de mes y año específicos
  const handleMonthYearSelection = (year, month) => {
    // Actualizar estados de selección
    setSelectedYear(year);
    setSelectedMonth(month);
    
    // Calcular fechas del mes - empezar desde el lunes de la primera semana
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    
    // Encontrar el lunes de la semana que contiene el primer día del mes
    const dayOfWeek = firstDayOfMonth.getDay(); // 0=domingo, 1=lunes, etc.
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Si es domingo, restar 6; sino restar (day-1)
    const startOfWeek = new Date(firstDayOfMonth);
    startOfWeek.setDate(firstDayOfMonth.getDate() - daysToSubtract);
    
    // Encontrar el domingo de la semana que contiene el último día del mes
    const lastDayOfWeek = lastDayOfMonth.getDay();
    const daysToAdd = lastDayOfWeek === 0 ? 0 : 7 - lastDayOfWeek; // Si es domingo, no agregar; sino agregar hasta domingo
    const endOfWeek = new Date(lastDayOfMonth);
    endOfWeek.setDate(lastDayOfMonth.getDate() + daysToAdd);
    
    const startStr = `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`;
    const endStr = `${endOfWeek.getFullYear()}-${String(endOfWeek.getMonth() + 1).padStart(2, '0')}-${String(endOfWeek.getDate()).padStart(2, '0')}`;
    
    console.log(`📅 Mes seleccionado: ${year}-${month+1}`);
    console.log(`📅 Primer día del mes: ${firstDayOfMonth.toDateString()} (día de semana: ${dayOfWeek})`);
    console.log(`📅 Último día del mes: ${lastDayOfMonth.toDateString()} (día de semana: ${lastDayOfWeek})`);
    console.log(`📅 Inicio semana completa: ${startStr} (${startOfWeek.toDateString()})`);
    console.log(`📅 Fin semana completa: ${endStr} (${endOfWeek.toDateString()})`);
    
    setCalendarStartDate(startStr);
    setCalendarEndDate(endStr);
    // Consultar API con el mes seleccionado (mes anterior, actual, siguiente) para que el calendario tenga toda la data
    if (selectedCompany?.id && selectedProject && currentView === 'calendar-analysis') {
      const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
      loadAttendanceRecords(monthStr);
    }
  };

  // Función para manejar selección de semana específica
  const handleWeekSelection = (year, weekNumber) => {
    // Actualizar estados de selección
    setSelectedWeekYear(year);
    setSelectedWeek(weekNumber);
    
    // Calcular fechas de la semana (del lunes al domingo)
    const { start, end } = getWeekDateRange(year, weekNumber);
    
    // Asegurar que la semana vaya de lunes a domingo
    const startOfWeek = new Date(start);
    const endOfWeek = new Date(end);
    
    const startStr = `${startOfWeek.getFullYear()}-${String(startOfWeek.getMonth() + 1).padStart(2, '0')}-${String(startOfWeek.getDate()).padStart(2, '0')}`;
    const endStr = `${endOfWeek.getFullYear()}-${String(endOfWeek.getMonth() + 1).padStart(2, '0')}-${String(endOfWeek.getDate()).padStart(2, '0')}`;
    
    console.log(`📅 Semana seleccionada: ${year} - Semana ${weekNumber}`);
    console.log(`📅 Inicio semana: ${startStr} (${startOfWeek.toDateString()})`);
    console.log(`📅 Fin semana: ${endStr} (${endOfWeek.toDateString()})`);
    
    setCalendarStartDate(startStr);
    setCalendarEndDate(endStr);
  };

  // Resto de funciones originales del tareo
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? employee.nombre_completo : 'N/A';
  };

  const getEmployeeInfo = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? {
      id: employee.id,
      name: employee.nombre_completo,
      position: employee.cargo,
      area: employee.areas_de_trabajo || 'Sin área',
      proyecto: employee.proyecto
    } : null;
  };

  // 🔍 FUNCIÓN PARA OBTENER CARGOS ÚNICOS DE LOS EMPLEADOS
  const obtenerCargosUnicos = () => {
    if (!employees || employees.length === 0) {
      return [];
    }

    const cargosUnicos = [...new Set(employees.map(emp => emp.cargo).filter(Boolean))];
    return cargosUnicos.sort();
  };

  // 🔍 FUNCIÓN PARA OBTENER ÁREAS DE TRABAJO ÚNICAS DE LOS EMPLEADOS
  const obtenerAreasUnicas = () => {
    if (!employees || employees.length === 0) {
      return [];
    }

    const areasUnicas = [...new Set(
      employees.map(emp => emp.areas_de_trabajo || 'Sin área').filter(Boolean)
    )];
    return areasUnicas.sort((a, b) => {
      // Poner "Sin área" al final
      if (a === 'Sin área') return 1;
      if (b === 'Sin área') return -1;
      return a.localeCompare(b);
    });
  };

  // ========== FUNCIONES PARA TAREO POR HORAS ==========
  
  // Detectar si es tareo por horas
  const esTareoPorHoras = () => {
    return configuracionTareo && (configuracionTareo.tipo_tareo === 3 || configuracionTareo.es_por_horas);
  };

  // Calcular hora de salida basándose en hora de entrada y horas trabajadas (solo este evento, sin logs)
  const calcularHoraSalida = (horaEntrada, horasTrabajadas) => {
    if (!horaEntrada || !horasTrabajadas) return '';
    const [horas, minutos] = horaEntrada.split(':').map(Number);
    const horasDecimales = horas + (minutos / 60);
    const horasTrabajadasNum = parseFloat(horasTrabajadas);
    const aplicaDescanso = horasTrabajadasNum > 6;
    const horasConDescanso = aplicaDescanso ? horasTrabajadasNum + 1 : horasTrabajadasNum;
    const horaSalidaDecimal = horasDecimales + horasConDescanso;
    const horasSalida = Math.floor(horaSalidaDecimal);
    const minutosSalida = Math.round((horaSalidaDecimal - horasSalida) * 60);
    const horasSalidaFinal = horasSalida >= 24 ? horasSalida - 24 : horasSalida;
    return `${String(horasSalidaFinal).padStart(2, '0')}:${String(minutosSalida).padStart(2, '0')}`;
  };

  // Calcular horas trabajadas basándose en horarios (para mostrar en campos)
  const calcularHorasDesdeHorarios = (horaEntrada, horaSalida) => {
    console.log('⚡ === CALCULANDO HORAS DESDE HORARIOS ===');
    console.log('🕐 Hora entrada:', horaEntrada);
    console.log('🕐 Hora salida:', horaSalida);
    
    if (!horaEntrada || !horaSalida) {
      console.log('❌ Horarios incompletos, retornando 0');
      return 0;
    }
    
    const [horasEnt, minutosEnt] = horaEntrada.split(':').map(Number);
    const [horasSal, minutosSal] = horaSalida.split(':').map(Number);
    console.log('🔢 Entrada - Horas:', horasEnt, 'Minutos:', minutosEnt);
    console.log('🔢 Salida - Horas:', horasSal, 'Minutos:', minutosSal);
    
    const horasEntradaDecimal = horasEnt + (minutosEnt / 60);
    const horasSalidaDecimal = horasSal + (minutosSal / 60);
    console.log('🔢 Entrada decimal:', horasEntradaDecimal);
    console.log('🔢 Salida decimal:', horasSalidaDecimal);
    
    let horasTotales = horasSalidaDecimal - horasEntradaDecimal;
    console.log('⏱️ Horas totales inicial:', horasTotales);
    
    // Manejar cruce de medianoche
    if (horasTotales < 0) {
      console.log('🌙 Detectado cruce de medianoche, sumando 24 horas');
      horasTotales += 24;
      console.log('⏱️ Horas totales después del ajuste:', horasTotales);
    }
    
    // Descontar 1 hora de descanso si el total es > 6 horas (igual que el backend)
    const aplicaDescuentoDescanso = horasTotales > 6;
    const horasTrabajadas = aplicaDescuentoDescanso ? horasTotales - 1 : horasTotales;
    
    console.log('🍽️ Aplica descuento descanso (>6h total):', aplicaDescuentoDescanso);
    console.log('⏱️ Horas trabajadas antes redondeo:', horasTrabajadas);
    
    const horasFinales = Math.round(horasTrabajadas * 100) / 100; // Redondear a 2 decimales
    console.log('✅ HORAS TRABAJADAS CALCULADAS:', horasFinales);
    console.log('⚡ === FIN CÁLCULO DESDE HORARIOS ===\n');
    
    return horasFinales;
  };

  // 🔍 FUNCIÓN PARA MANEJAR SELECCIÓN/DESELECCIÓN DE CARGOS
  const toggleCargo = (cargo) => {
    setFiltrosCargosSeleccionados(prev => {
      if (prev.includes(cargo)) {
        return prev.filter(c => c !== cargo);
      } else {
        return [...prev, cargo];
      }
    });
  };

  // 🔍 FUNCIÓN PARA SELECCIONAR/DESELECCIONAR TODOS LOS CARGOS
  const toggleTodosCargos = () => {
    const todosLosCargos = obtenerCargosUnicos();
    if (filtrosCargosSeleccionados.length === todosLosCargos.length) {
      setFiltrosCargosSeleccionados([]);
    } else {
      setFiltrosCargosSeleccionados(todosLosCargos);
    }
  };

  // 🔍 FUNCIÓN PARA MANEJAR SELECCIÓN/DESELECCIÓN DE ÁREAS DE TRABAJO
  const toggleArea = (area) => {
    setFiltrosAreasSeleccionadas(prev => {
      if (prev.includes(area)) {
        return prev.filter(a => a !== area);
      } else {
        return [...prev, area];
      }
    });
  };

  // 🔍 FUNCIÓN PARA SELECCIONAR/DESELECCIONAR TODAS LAS ÁREAS DE TRABAJO
  const toggleTodasAreas = () => {
    const todasLasAreas = obtenerAreasUnicas();
    if (filtrosAreasSeleccionadas.length === todasLasAreas.length) {
      setFiltrosAreasSeleccionadas([]);
    } else {
      setFiltrosAreasSeleccionadas(todasLasAreas);
    }
  };

  // 🔍 COMPONENTE MULTIFILTRO DE CARGOS PARA TAREOS
  const MultiFiltroCargoComponent = () => {
    const cargosUnicos = obtenerCargosUnicos();
    const [mostrarDropdown, setMostrarDropdown] = useState(false);
    
    if (cargosUnicos.length === 0) {
      return null; // No mostrar si no hay cargos
    }
    
    return (
      <div className="relative">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Filtrar cargo:
          </label>
          
          {/* Botón principal del dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMostrarDropdown(!mostrarDropdown)}
              className="flex items-center justify-between w-36 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <span className="truncate">
                {filtrosCargosSeleccionados.length === 0 
                  ? "Todos"
                  : filtrosCargosSeleccionados.length === 1
                  ? filtrosCargosSeleccionados[0]
                  : `${filtrosCargosSeleccionados.length} cargos`
                }
              </span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown con checkboxes */}
            {mostrarDropdown && (
              <div className="absolute z-20 w-48 mt-1 bg-white border border-gray-300 rounded-md shadow-lg right-0">
                <div className="p-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={toggleTodosCargos}
                    className="w-full text-left px-2 py-1 text-sm text-teal-600 hover:bg-teal-50 rounded"
                  >
                    {filtrosCargosSeleccionados.length === cargosUnicos.length 
                      ? "Deseleccionar todos" 
                      : "Seleccionar todos"
                    }
                  </button>
                </div>
                
                <div className="max-h-40 overflow-y-auto">
                  {cargosUnicos.map(cargo => (
                    <label
                      key={cargo}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filtrosCargosSeleccionados.includes(cargo)}
                        onChange={() => toggleCargo(cargo)}
                        className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">{cargo}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Overlay para cerrar dropdown */}
        {mostrarDropdown && (
          <div
            className="fixed inset-0 z-10"
            onClick={() => setMostrarDropdown(false)}
          />
        )}
      </div>
    );
  };

  // 🔍 COMPONENTE MULTIFILTRO DE ÁREAS DE TRABAJO PARA TAREOS
  const MultiFiltroAreaComponent = () => {
    const areasUnicas = obtenerAreasUnicas();
    const [mostrarDropdown, setMostrarDropdown] = useState(false);
    
    if (areasUnicas.length === 0) {
      return null; // No mostrar si no hay áreas
    }
    
    return (
      <div className="relative">
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Filtrar área:
          </label>
          
          {/* Botón principal del dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMostrarDropdown(!mostrarDropdown)}
              className="flex items-center justify-between w-36 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              <span className="truncate">
                {filtrosAreasSeleccionadas.length === 0 
                  ? "Todas"
                  : filtrosAreasSeleccionadas.length === 1
                  ? filtrosAreasSeleccionadas[0]
                  : `${filtrosAreasSeleccionadas.length} áreas`
                }
              </span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Dropdown con checkboxes */}
            {mostrarDropdown && (
              <div className="absolute z-20 w-48 mt-1 bg-white border border-gray-300 rounded-md shadow-lg right-0">
                <div className="p-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={toggleTodasAreas}
                    className="w-full text-left px-2 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded"
                  >
                    {filtrosAreasSeleccionadas.length === areasUnicas.length 
                      ? "Deseleccionar todas" 
                      : "Seleccionar todas"
                    }
                  </button>
                </div>
                
                <div className="max-h-40 overflow-y-auto">
                  {areasUnicas.map(area => (
                    <label
                      key={area}
                      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={filtrosAreasSeleccionadas.includes(area)}
                        onChange={() => toggleArea(area)}
                        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      />
                      <span className={`ml-2 text-sm ${area === 'Sin área' ? 'text-gray-500 italic' : 'text-gray-700'}`}>
                        {area}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Overlay para cerrar dropdown */}
        {mostrarDropdown && (
          <div
            className="fixed inset-0 z-10"
            onClick={() => setMostrarDropdown(false)}
          />
        )}
      </div>
    );
  };

  /**
   * Resuelve nombre/tipo de un concepto de tareo.
   * Prioridad: relación PLAME embebida en el concepto → lista de tareos → fallback legible.
   * (Viáticos suele usar códigos fuera de la lista 070x/1001-1020; por eso no basta con plameConceptos.)
   */
  const getConceptInfo = (plameConfigId, concept = null) => {
    const idNum = plameConfigId != null && plameConfigId !== '' ? Number(plameConfigId) : NaN;
    const tipoGuardado = concept?.tipo_concepto || null;

    const rel = concept?.plame_configuracion || concept?.plameConfiguracion || null;
    if (rel) {
      const codigo = rel.codigo_plame || rel.codigo || null;
      const nombre = rel.descripcion || rel.nombre || null;
      if (nombre || codigo) {
        return {
          nombre: nombre || `Código ${codigo}`,
          tipo: tipoGuardado || 'ingreso',
          codigo: codigo || undefined,
        };
      }
    }

    const hardcodedConcept = conceptTypes.find((ct) => Number(ct.value) === idNum);
    if (hardcodedConcept) {
      return {
        nombre: hardcodedConcept.label,
        tipo: tipoGuardado || hardcodedConcept.type,
        codigo: hardcodedConcept.codigo,
      };
    }

    const plameConcept = (plameConceptos || []).find((pc) => Number(pc.id) === idNum);
    if (plameConcept) {
      return {
        nombre: plameConcept.nombre,
        tipo: tipoGuardado || plameConcept.tipo_concepto,
        codigo: plameConcept.codigo || plameConcept.codigo_plame,
      };
    }

    // No está en la lista de tareos (p. ej. viáticos 0905) ni vino la relación
    if (!Number.isNaN(idNum)) {
      return {
        nombre: `Concepto PLAME #${idNum}`,
        tipo: tipoGuardado || 'ingreso',
      };
    }

    return { nombre: 'Concepto', tipo: tipoGuardado || 'descuento' };
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      presente: 'bg-green-100 text-green-800',
      falta: 'bg-red-100 text-red-800',
      permiso: 'bg-blue-100 text-blue-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getEventTypeBadge = (eventType) => {
    const eventClasses = {
      asistio: 'bg-green-100 text-green-800',
      falta: 'bg-red-100 text-red-800',
      feriado: 'bg-purple-100 text-purple-800',
      feriado_laborado: 'bg-purple-200 text-purple-900',
      descanso: 'bg-blue-100 text-blue-800',
      descanso_laborado: 'bg-blue-200 text-blue-900',
      descanso_medico: 'bg-orange-100 text-orange-800',
      reposo_medico: 'bg-orange-50 text-orange-700',
      subsidio_por_enfermedad: 'bg-orange-200 text-orange-900',
      licencia_con_goce_de_haber: 'bg-indigo-100 text-indigo-800',
      permiso: 'bg-yellow-100 text-yellow-800',
      suspension: 'bg-amber-100 text-amber-800',
      vacaciones: 'bg-cyan-100 text-cyan-800'
    };
    return eventClasses[eventType] || 'bg-gray-100 text-gray-800';
  };

  const getEventTypeLabel = (eventType) => {
    const eventLabels = {
      asistio: 'Asistió',
      falta: 'Falta',
      feriado: 'Feriado',
      feriado_laborado: 'Feriado Laborado',
      descanso: 'Descanso',
      descanso_laborado: 'Descanso Laborado',
      descanso_medico: 'Descanso Médico',
      reposo_medico: 'Reposo Médico',
      subsidio_por_enfermedad: 'Subsidio por Enfermedad',
      licencia_con_goce_de_haber: 'Licencia con Goce de Haber',
      permiso: 'Permiso',
      suspension: 'Suspensión',
      vacaciones: 'Vacaciones'
    };
    return eventLabels[eventType] || 'Sin especificar';
  };

  // Etiqueta corta para no saturar la celda del calendario.
  const getCalendarEventShortLabel = (eventType) => {
    const eventLabels = {
      asistio: '✅ Asistió',
      falta: '❌ Falta',
      permiso: '📝 Permiso',
      suspension: '⛔ Susp.',
      vacaciones: '🏖️ Vacac.',
      feriado: '🎉 Feriado',
      feriado_laborado: '💼 F.Lab',
      descanso: '😴 Desc.',
      descanso_laborado: '💪 D.Lab',
      descanso_medico: '🏥 D.Med',
      reposo_medico: '🛏️ Reposo',
      subsidio_por_enfermedad: '💊 Subs.',
      licencia_con_goce_de_haber: '🧾 Lic.Goce',
      tardanza: '⏰ Tard.'
    };
    return eventLabels[eventType] || getEventTypeLabel(eventType);
  };

  const getStatusFromEventType = (eventType, checkIn, checkOut, theoreticalCheckIn, theoreticalCheckOut) => {
    // Si es falta, siempre es falta
    if (eventType === 'falta') return 'falta';
    
    // Si es feriado, descanso, descanso médico, reposo médico, subsidio por enfermedad, permiso o vacaciones, son justificados
    if (['feriado', 'descanso', 'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad', 'licencia_con_goce_de_haber', 'permiso', 'suspension', 'vacaciones'].includes(eventType)) return 'justificado';
    
    // Si es feriado laborado o descanso laborado, es presente
    if (['feriado_laborado', 'descanso_laborado'].includes(eventType)) return 'presente';
    
    // Si asistió, verificar si llegó tarde
    if (eventType === 'asistio') {
      if (checkIn && theoreticalCheckIn && checkIn > theoreticalCheckIn) {
        return 'tarde';
      }
      return 'presente';
    }
    
    return 'presente';
  };

  const getStatusBadgeFromEventType = (eventType, checkIn, checkOut, theoreticalCheckIn, theoreticalCheckOut) => {
    const status = getStatusFromEventType(eventType, checkIn, checkOut, theoreticalCheckIn, theoreticalCheckOut);
    
    const statusClasses = {
      presente: 'bg-green-100 text-green-800',
      falta: 'bg-red-100 text-red-800',
      justificado: 'bg-blue-100 text-blue-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabelFromEventType = (eventType, checkIn, checkOut, theoreticalCheckIn, theoreticalCheckOut) => {
    const status = getStatusFromEventType(eventType, checkIn, checkOut, theoreticalCheckIn, theoreticalCheckOut);
    
    const statusLabels = {
      presente: 'Presente',
      falta: 'Falta',
      justificado: 'Justificado'
    };
    return statusLabels[status] || 'N/A';
  };

  // Función para mostrar horas trabajadas (0 si es falta)
  const getDisplayHours = (record) => {
    const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
    return status === 'falta' ? '0.00' : record.hoursWorked.toFixed(2);
  };

  // Misma lógica que planilla: descanso médico, subsidio y licencia con goce = horas médicas (8 h); el resto efectivas
  const TIPOS_EVENTO_HORAS_MEDICAS = [
    'descanso_medico',
    'subsidio_por_enfermedad',
    'licencia_con_goce_de_haber'
  ];

  const horasRegistroParaResumen = (record) => {
    const status = getStatusFromEventType(
      record.eventType,
      record.checkIn,
      record.checkOut,
      record.theoreticalCheckIn,
      record.theoreticalCheckOut
    );
    if (status === 'falta') {
      return { efectivas: 0, medicas: 0 };
    }
    const h = parseFloat(record.hoursWorked || 0);
    if (TIPOS_EVENTO_HORAS_MEDICAS.includes(record.eventType)) {
      return { efectivas: 0, medicas: h > 0 ? h : 8 };
    }
    return { efectivas: h, medicas: 0 };
  };

  // Función para mostrar horas en el calendario (0 si hay faltas)
  const getDisplayHoursCalendar = (hours, dayRecords = []) => {
    const hasFalta = dayRecords && dayRecords.some(record => {
      const status = getStatusFromEventType(record.eventType, record.checkIn, record.checkOut, record.theoreticalCheckIn, record.theoreticalCheckOut);
      return status === 'falta';
    });
    
    // Si hay faltas, mostrar 0
    if (hasFalta) {
      return '0.00';
    }
    
    return hours.toFixed(2);
  };

  // Función para determinar si un registro se puede editar/eliminar completamente
  const canEditRecord = (record) => {
    // Todos los registros se pueden "editar" ahora, pero algunos solo para conceptos
    return true;
  };

  // Función para determinar si un registro es automático (solo edición de conceptos)
  const isAutomaticRecord = (record) => {
    const autoCreatedTypes = ['permiso', 'suspension', 'descanso_medico', 'reposo_medico', 'subsidio_por_enfermedad', 'licencia_con_goce_de_haber', 'vacaciones'];
    
    return autoCreatedTypes.includes(record.eventType) && 
        record.observaciones && 
           record.observaciones.includes('Creado automáticamente por autorización');
  };
    
  // Función para determinar si un registro se puede eliminar
  const canDeleteRecord = (record) => {
    // Los registros automáticos no se pueden eliminar
    return !isAutomaticRecord(record);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    console.log('Formateando fecha:', dateString);
    
    // Manejar fechas UTC correctamente
    const date = new Date(dateString);
    
    // Usar UTC para evitar problemas de zona horaria
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    
    const formattedDate = `${day}/${month}/${year}`;
    console.log('Fecha original:', dateString, '-> Fecha formateada:', formattedDate);
    
    return formattedDate;
  };

  // Función para filtrar empleados por fecha seleccionada y proyecto
  const filterEmployeesByDate = (selectedDate) => {
    if (!selectedDate || !employees || employees.length === 0) {
      console.log('🔍 Sin fecha seleccionada o sin empleados, devolviendo lista vacía');
      return [];
    }

    if (!selectedProject) {
      console.log('🔍 Sin proyecto seleccionado, devolviendo lista vacía');
      return [];
    }

    console.log('=== FILTRANDO EMPLEADOS POR FECHA Y PROYECTO ===');
    console.log('📅 Fecha seleccionada:', selectedDate);
    console.log('🏗️ Proyecto seleccionado:', selectedProject.nombre);
    console.log('👥 Total empleados disponibles:', employees.length);

    const empleadosFiltrados = employees.filter(empleado => {
      console.log(`\n--- EVALUANDO EMPLEADO: ${empleado.nombre_completo} ---`);
      
      // Filtro 1: Verificar que el empleado pertenece al proyecto/sede seleccionado(a)
      const perteneceAlProyecto = empleadoEnAlcanceActual(empleado);
      
      console.log('🏗️ Proyecto del empleado:', empleado.proyecto);
      console.log('🏗️ Pertenece al alcance seleccionado?', perteneceAlProyecto);
      
      if (!perteneceAlProyecto) {
        console.log('❌ NO pertenece al proyecto - EXCLUIDO');
        return false;
      }
      
      // Filtro 2: PLAME o historial interno (ingreso interno puede ser anterior al alta PLAME)
      if (empleado.ultimo_historial?.motivo_cese === 'No se presentó a laborar') {
        console.log('❌ Empleado con motivo de baja "No se presentó a laborar" - EXCLUIDO');
        return false;
      }

      return empleadoActivoEnFechaPlameOInterno(empleado, selectedDate);
    });

    console.log('✅ EMPLEADOS FILTRADOS FINALES (FECHA + PROYECTO):', empleadosFiltrados.length);
    empleadosFiltrados.forEach((emp, index) => {
      console.log(`${index + 1}. ${emp.nombre_completo} (${emp.estado}) - Proyecto: ${emp.proyecto}`);
    });

    return empleadosFiltrados;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setNewAttendanceData(prev => {
      const newData = { ...prev };
      // Números para horas teóricas/reales; el resto como viene
      if (name === 'horasTeoricas') newData.horasTeoricas = parseFloat(value) || 0;
      else if (name === 'horasReales') newData.horasReales = parseFloat(value) || 0;
      else newData[name] = value;
      
      // Si cambió la fecha, limpiar el empleado seleccionado
      if (name === 'date') {
        newData.employeeId = '';
        newData.theoreticalCheckIn = '';
        newData.theoreticalCheckOut = '';
        newData.checkIn = '';
        newData.checkOut = '';
      }
      
      // Si cambió el empleado, cargar sus horarios teóricos
      if (name === 'employeeId' && value && newData.date) {
        const empleadosFiltrados = filterEmployeesByDate(newData.date);
        const empleadoSeleccionado = empleadosFiltrados.find(emp => emp.id === parseInt(value));
        
        if (empleadoSeleccionado) {
          newData.theoreticalCheckIn = empleadoSeleccionado.hora_entrada_formatted || '';
          if (esTareoPorHoras()) {
            if (!newData.horasTeoricas || newData.horasTeoricas === 0) newData.horasTeoricas = 8;
            if (!newData.horasReales || newData.horasReales === 0) newData.horasReales = 8;
            newData.theoreticalCheckOut = calcularHoraSalida(newData.theoreticalCheckIn, newData.horasTeoricas);
            newData.checkIn = newData.theoreticalCheckIn;
            newData.checkOut = calcularHoraSalida(newData.theoreticalCheckIn, newData.horasReales);
          } else {
            newData.theoreticalCheckOut = empleadoSeleccionado.hora_salida_formatted || '';
          }
        } else {
          newData.theoreticalCheckIn = '';
          newData.theoreticalCheckOut = '';
        }
      }
      // Recálculo de Salida Teórica/Real en HoursConfigInputs (onBlur), no en cada tecla
      return newData;
    });
  };

  // Recalcular salidas solo al salir del campo; recibe valores del subcomponente (no re-renderiza toda la página al escribir)
  const handleHoursConfigCommit = (entrada, hT, hR) => {
    setNewAttendanceData(prev => {
      const newData = { ...prev, theoreticalCheckIn: entrada, horasTeoricas: hT, horasReales: hR };
      if (!entrada) return newData;
      newData.theoreticalCheckOut = calcularHoraSalida(entrada, hT || 8);
      newData.checkIn = entrada;
      newData.checkOut = calcularHoraSalida(entrada, hR || 8);
      return newData;
    });
  };

  const addConcept = () => {
    setNewAttendanceData(prev => ({
      ...prev,
      concepts: [...prev.concepts, { type: '', amount: 0, description: '', conceptType: '', destino_boleta: 'plame' }]
    }));
  };

  const removeConcept = (index) => {
    setNewAttendanceData(prev => ({
      ...prev,
      concepts: prev.concepts.filter((_, i) => i !== index)
    }));
  };

  const updateConcept = (index, field, value) => {
    setNewAttendanceData(prev => ({
      ...prev,
      concepts: prev.concepts.map((concept, i) => {
        if (i !== index) return concept;
          const updatedConcept = { ...concept, [field]: value };
          if (field === 'type') {
          const ct = conceptTypes.find(c => c.value === value);
          if (ct) updatedConcept.conceptType = ct.type;
          else {
            const pc = plameConceptos.find(p => p.id === parseInt(value));
            if (pc) updatedConcept.conceptType = pc.tipo_concepto === 'adelanto' ? 'advance' : pc.tipo_concepto === 'ingreso' ? 'income' : 'discount';
          }
        }
          return updatedConcept;
      })
    }));
  };

  // Commit de una fila de concepto desde el subcomponente (solo en onBlur, no en cada tecla)
  const updateConceptRow = (index, data) => {
    setNewAttendanceData(prev => ({
      ...prev,
      concepts: prev.concepts.map((concept, i) => {
        if (i !== index) return concept;
        const updated = { ...concept, ...data };
        if (data.amount != null && data.amount !== '') {
          updated.amount = data.amount;
          updated.monto = getConceptoMonto(updated);
        }
        if (data.destino_boleta != null) {
          updated.destino_boleta = data.destino_boleta;
        }
        if (data.type != null) {
          updated.plame_configuracion_id = data.type;
          const ct = conceptTypes.find(c => c.value === data.type);
          if (ct) updated.conceptType = ct.type;
          else {
            const pc = plameConceptos.find(p => p.id === parseInt(data.type));
            if (pc) updated.conceptType = pc.tipo_concepto === 'adelanto' ? 'advance' : pc.tipo_concepto === 'ingreso' ? 'income' : 'discount';
          }
        }
        return updated;
      })
    }));
  };

  // Funciones para manejar "No marcó entrada/salida"
  const handleNoCheckIn = () => {
    // Calcular media hora después de la hora teórica de entrada
    const theoreticalTime = newAttendanceData.theoreticalCheckIn || '08:00';
    const [hours, minutes] = theoreticalTime.split(':').map(Number);
    const lateTime = new Date();
    lateTime.setHours(hours, minutes + 30, 0, 0);
    
    const lateTimeString = lateTime.toTimeString().slice(0, 5);
    
    setNewAttendanceData({
      ...newAttendanceData,
      checkIn: lateTimeString
    });
  };

  const handleNoCheckOut = () => {
    // Calcular media hora antes de la hora teórica de salida
    const theoreticalTime = newAttendanceData.theoreticalCheckOut || '17:00';
    const [hours, minutes] = theoreticalTime.split(':').map(Number);
    const earlyTime = new Date();
    earlyTime.setHours(hours, minutes - 30, 0, 0);
    
    const earlyTimeString = earlyTime.toTimeString().slice(0, 5);
    
    setNewAttendanceData({
      ...newAttendanceData,
      checkOut: earlyTimeString
    });
  };

  const handleAddAttendance = async () => {
    if (!newAttendanceData.employeeId || !newAttendanceData.date) {
      alert('Por favor completa los campos requeridos');
      return;
    }

    setIsCreating(true);
    try {
      // Preparar conceptos para envío al backend (con destino boleta si PA activa)
      const conceptosParaBackend = (newAttendanceData.concepts || []).flatMap((concepto) =>
        mapearConceptoAPayloadsBackend(concepto, conceptTypes, plameConceptos, planillaAlternativaActiva)
      );

      // Preparar datos para envío
      let tareoData;
      
      if (esTareoPorHoras()) {
        console.log('🎯 === PROCESANDO TAREO POR HORAS ===');
        console.log('📊 Datos recibidos del formulario:', newAttendanceData);
        
        // Para tareo por horas: calcular horarios automáticamente
        const horaEntrada = newAttendanceData.theoreticalCheckIn;
        console.log('🕐 Hora entrada base:', horaEntrada);
        console.log('⏰ Horas teóricas a trabajar:', newAttendanceData.horasTeoricas);
        console.log('⏰ Horas reales a trabajar:', newAttendanceData.horasReales);
        
        const horaSalidaTeorica = calcularHoraSalida(horaEntrada, newAttendanceData.horasTeoricas);
        const horaSalidaReal = calcularHoraSalida(horaEntrada, newAttendanceData.horasReales);
        
        console.log('📋 RESULTADO FINAL DE CÁLCULOS:');
        console.log('  🕐 Entrada teórica:', horaEntrada);
        console.log('  🕕 Salida teórica:', horaSalidaTeorica);
        console.log('  🕐 Entrada real:', horaEntrada);
        console.log('  🕕 Salida real:', horaSalidaReal);
        
        tareoData = {
          empresa_id: selectedCompany.id,
          empleado_id: parseInt(newAttendanceData.employeeId),
          proyecto_id: selectedProject.id,
          fecha: newAttendanceData.date,
          tipo_evento: newAttendanceData.eventType,
          hora_entrada_teorica: horaEntrada,
          hora_salida_teorica: horaSalidaTeorica,
          hora_entrada_real: horaEntrada,
          hora_salida_real: horaSalidaReal,
          observaciones: newAttendanceData.notes || null,
          conceptos: conceptosParaBackend
        };
        
        console.log('📦 Datos finales para enviar al backend:', tareoData);
        console.log('🎯 === FIN PROCESAMIENTO TAREO POR HORAS ===\n');
      } else {
        // Para tareo simple/detallado: usar lógica existente
        tareoData = {
          empresa_id: selectedCompany.id,
          empleado_id: parseInt(newAttendanceData.employeeId),
          proyecto_id: selectedProject.id,
          fecha: newAttendanceData.date,
          tipo_evento: newAttendanceData.eventType,
          hora_entrada_teorica: newAttendanceData.theoreticalCheckIn,
          hora_salida_teorica: newAttendanceData.theoreticalCheckOut,
          hora_entrada_real: configuracionTareo && configuracionTareo.es_simple 
            ? newAttendanceData.theoreticalCheckIn 
            : (newAttendanceData.checkIn || null),
          hora_salida_real: configuracionTareo && configuracionTareo.es_simple 
            ? newAttendanceData.theoreticalCheckOut 
            : (newAttendanceData.checkOut || null),
          observaciones: newAttendanceData.notes || null,
          conceptos: conceptosParaBackend
        };
      }

      tareoData = aplicarFlagTasaNocturna(
        tareoData,
        tasaNocturnaActiva,
        considerarTasaNocturnaIndividual
      );

      // Validar datos
      const validacion = tareoService.validarTareo(tareoData);
      if (!validacion.esValido) {
        alert('Errores de validación:\n' + validacion.errores.join('\n'));
        return;
      }

      // Enviar al backend
      const response = await tareoService.crearTareo(tareoData);
      
      if (response.success) {
        // Actualizar lista local
        await loadAttendanceRecords();
        
        // Reset form
    setNewAttendanceData({
      employeeId: '',
      date: new Date().toISOString().split('T')[0],
          eventType: 'asistio',
          theoreticalCheckIn: '',
          theoreticalCheckOut: '',
      checkIn: '',
      checkOut: '',
          advances: 0,
          income: 0,
          discounts: 0,
          notes: '',
          concepts: []
        });
        
        setShowAddModal(false);
        setConsiderarTasaNocturnaIndividual(false);
        alert('Tareo registrado exitosamente');
      } else {
        alert('Error al registrar tareo: ' + response.message);
      }
    } catch (error) {
      console.error('Error al crear tareo:', error);
      alert('Error al registrar tareo. Por favor intente nuevamente.');
    } finally {
      setIsCreating(false);
    }
  };

  const calculateHours = (checkIn, checkOut) => {
    console.log('🔢 === CALCULANDO HORAS TRABAJADAS ===');
    console.log('⏰ Hora entrada:', checkIn);
    console.log('⏰ Hora salida:', checkOut);
    
    if (!checkIn || !checkOut) {
      console.log('❌ Sin horarios válidos, retornando 0');
      return 0;
    }
    
    const start = new Date(`2024-01-01T${checkIn}`);
    let end = new Date(`2024-01-01T${checkOut}`);
    console.log('📅 Fecha/hora inicio:', start);
    console.log('📅 Fecha/hora fin inicial:', end);
    
    // Manejar cruce de medianoche
    if (end < start) {
      console.log('🌙 Detectado cruce de medianoche - la salida es al día siguiente');
      end = new Date(`2024-01-02T${checkOut}`);
      console.log('📅 Fecha/hora fin ajustada:', end);
    }
    
    let totalMinutes = (end - start) / (1000 * 60);
    console.log('⏱️ Total minutos inicial:', totalMinutes);
    
    // Asumimos un descanso estándar de 1 hora si la jornada es mayor a 6 horas
    const totalHours = totalMinutes / 60;
    console.log('⏱️ Total horas antes del descanso:', totalHours);
    
    if (totalHours > 6) {
      console.log('🍽️ Jornada > 6 horas, descontando 1 hora de descanso');
      totalMinutes -= 60; // Restar 1 hora de descanso
      console.log('⏱️ Total minutos después del descanso:', totalMinutes);
    } else {
      console.log('⏱️ Jornada ≤ 6 horas, sin descuento de descanso');
    }
    
    const finalHours = Math.max(0, totalMinutes / 60);
    console.log('✅ HORAS FINALES CALCULADAS:', finalHours);
    console.log('🔢 === FIN CÁLCULO HORAS ===\n');
    
    return finalHours;
  };

  const getAttendanceStatus = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 'falta';
    
    const checkInTime = new Date(`2024-01-01T${checkIn}`);
    const expectedTime = new Date('2024-01-01T08:00');
    
    if (checkInTime > expectedTime) return 'tarde';
    return 'presente';
  };

  // Funciones para filtros de fecha
  const getWeekNumber = (date) => {
    // Parsear la fecha de forma segura evitando problemas de zona horaria
    const [year, month, day] = date.split('-').map(Number);
    const d = new Date(year, month - 1, day); // Usar constructor con parámetros locales
    const yearStart = new Date(year, 0, 1);
    const weekNumber = Math.ceil(((d - yearStart) / 86400000 + yearStart.getDay() + 1) / 7);
    return `${year}-W${weekNumber.toString().padStart(2, '0')}`;
  };

  const getMonthValue = (date) => {
    // Parsear la fecha de forma segura evitando problemas de zona horaria
    const [year, month] = date.split('-').map(Number);
    return `${year}-${month.toString().padStart(2, '0')}`;
  };

  const getYearValue = (date) => {
    const [year] = date.split('-').map(Number);
    return year.toString();
  };

  const getCurrentMonthValue = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  };

  // Lunes de la semana actual (YYYY-MM-DD)
  const getMondayOfCurrentWeek = () => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.getFullYear(), d.getMonth(), diff);
    return `${monday.getFullYear()}-${String(monday.getMonth() + 1).padStart(2, '0')}-${String(monday.getDate()).padStart(2, '0')}`;
  };

  // Dada una fecha YYYY-MM-DD, devuelve { start: lunes, end: domingo } en YYYY-MM-DD
  const getWeekRangeFromDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(y, m - 1, diff);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: fmt(monday), end: fmt(sunday) };
  };

  const filterRecordsByDate = (records) => {
    const filtered = records.filter(record => {
      let dateMatch = true;
      if (filterValue) {
        if (filterPeriod === 'day') {
            dateMatch = record.date === filterValue;
        } else if (filterPeriod === 'week') {
          const { start, end } = getWeekRangeFromDate(filterValue);
          dateMatch = record.date >= start && record.date <= end;
        } else {
            const monthNum = getMonthValue(record.date);
            dateMatch = monthNum === filterValue;
        }
      }
      
      // Filtro por nombre (usa valor aplicado al pulsar Buscar, no al escribir)
      let nameMatch = true;
      if (appliedSearchName.trim()) {
        const employee = getEmployeeInfo(record.employeeId);
        const employeeName = employee?.name?.toLowerCase() || '';
        nameMatch = employeeName.includes(appliedSearchName.toLowerCase().trim());
      }
      
      // 🔍 Filtro por cargo
      let cargoMatch = true;
      if (filtrosCargosSeleccionados.length > 0) {
        const employee = getEmployeeInfo(record.employeeId);
        const employeeCargo = employee?.position || '';
        cargoMatch = filtrosCargosSeleccionados.includes(employeeCargo);
      }

      // 🔍 Filtro por área de trabajo
      let areaMatch = true;
      if (filtrosAreasSeleccionadas.length > 0) {
        const employee = getEmployeeInfo(record.employeeId);
        const employeeArea = employee?.area || 'Sin área';
        areaMatch = filtrosAreasSeleccionadas.includes(employeeArea);
      }
      
      // 🔍 Filtro por tipo de evento
      let eventTypeMatch = true;
      if (eventTypeFilter.trim()) {
        eventTypeMatch = record.eventType === eventTypeFilter;
      }
      
      // 🔍 Filtro por horas (usa valor aplicado al pulsar Buscar, no al escribir)
      let hoursMatch = true;
      if (appliedHoursFilter.trim()) {
        const hours = record.hoursWorked || 0;
        const filterHours = parseFloat(appliedHoursFilter);
        if (!isNaN(filterHours)) {
          const tolerance = 0.25;
          hoursMatch = Math.abs(hours - filterHours) <= tolerance;
        } else {
          hoursMatch = true;
        }
      }
      
      // 🔍 Filtro por sede (solo en modo Todas las sedes)
      let sedeMatch = true;
      if (esTodasSedes && filtroSedeId) {
        sedeMatch = String(record.proyectoId) === String(filtroSedeId);
      }
      
      return dateMatch && nameMatch && cargoMatch && areaMatch && eventTypeMatch && hoursMatch && sedeMatch;
    });
    return filtered;
  };

  const filteredRecords = filterRecordsByDate(attendanceRecords);

  const getAttendanceSummary = () => {
    const total = filteredRecords.length;
    
    // Calcular estados basados en tipo de evento
    const presente = filteredRecords.filter(r => {
      const status = getStatusFromEventType(r.eventType, r.checkIn, r.checkOut, r.theoreticalCheckIn, r.theoreticalCheckOut);
      return status === 'presente';
    }).length;
    
    const tarde = filteredRecords.filter(r => {
      const status = getStatusFromEventType(r.eventType, r.checkIn, r.checkOut, r.theoreticalCheckIn, r.theoreticalCheckOut);
      return status === 'tarde';
    }).length;
    
    const falta = filteredRecords.filter(r => {
      const status = getStatusFromEventType(r.eventType, r.checkIn, r.checkOut, r.theoreticalCheckIn, r.theoreticalCheckOut);
      return status === 'falta';
    }).length;
    
    let horasEfectivas = 0;
    let horasDescansoMedico = 0;
    filteredRecords.forEach((r) => {
      const parte = horasRegistroParaResumen(r);
      horasEfectivas += parte.efectivas;
      horasDescansoMedico += parte.medicas;
    });
    const totalHours = horasEfectivas + horasDescansoMedico;

    return { total, presente, tarde, falta, totalHours, horasEfectivas, horasDescansoMedico };
  };


  // Función para limpiar todos los filtros (mantiene período actual: día/semana/mes)
  const clearAllFilters = () => {
    setAppliedSearchName('');
    setAppliedHoursFilter('');
    setEventTypeFilter('');
    setHoursFilter('');
    setFiltrosCargosSeleccionados([]);
    setFiltrosAreasSeleccionadas([]);
    if (filterPeriod === 'day') {
      const d = new Date();
      setFilterValue(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    } else if (filterPeriod === 'week') {
      setFilterValue(getMondayOfCurrentWeek());
    } else {
      setFilterValue(getCurrentMonthValue());
    }
  };

  // Aplicar filtros de búsqueda/horas solo al pulsar (no al escribir)
  const applySearchAndHoursFilters = () => {
    setAppliedSearchName(searchInputRef.current?.getValue?.() ?? '');
    setAppliedHoursFilter(hoursFilter);
  };

  // Funciones para acciones de editar y eliminar
  const handleEditRecord = (record) => {
    // Formatear la fecha para el input (solo YYYY-MM-DD)
    // Las fechas ya vienen en formato YYYY-MM-DD correcto
    const dateForInput = record.date || '';
    
    // Transformar conceptos del backend al formato del frontend
    const conceptsForFrontend = record.concepts ? record.concepts.map(concept => {
      // Determinar el tipo basado en tipo_concepto
      let conceptTypeString = '';
      if (concept.tipo_concepto === 'adelanto') {
        conceptTypeString = 'advance';
      } else if (concept.tipo_concepto === 'ingreso') {
        conceptTypeString = 'income';
      } else {
        conceptTypeString = 'discount';
      }
      
      return {
        type: concept.plame_configuracion_id ? concept.plame_configuracion_id.toString() : '',
        amount: parseFloat(concept.monto || 0),
        description: concept.observaciones || '',
        conceptType: conceptTypeString,
        plame_configuracion_id: concept.plame_configuracion_id,
        // Agregar campos para el modal de conceptos automáticos
        tipo_concepto: concept.tipo_concepto,
        monto: parseFloat(concept.monto || 0),
        observaciones: concept.observaciones || ''
      };
    }) : [];
    
    // Determinar qué modal abrir según el tipo de registro
    if (isAutomaticRecord(record)) {
      console.log('🔒 Abriendo modal de edición de conceptos automáticos');
      
             // Para registros automáticos, usar los conceptos tal como vienen
       const conceptsForAutoModal = record.concepts || [];
       

       
       // Actualizar el registro con los conceptos preparados
       setEditingRecord({
         ...record,
         concepts: conceptsForAutoModal
       });
       
       setShowAutomaticEditModal(true);
    } else {
      console.log('✏️ Abriendo modal de edición normal');
      
    setEditingRecord(record);
    setConsiderarTasaNocturnaIndividual(Boolean(record.considerarTasaNocturna));
    
      // Normalizar hora a HH:MM para inputs type="time" (el backend puede devolver "08:00:00")
      const toTimeInput = (v) => {
        if (v == null || v === '') return '';
        const s = String(v).trim();
        const match = s.match(/^(\d{1,2}):(\d{2})/);
        return match ? `${String(match[1]).padStart(2, '0')}:${match[2]}` : s;
      };

      if (esTareoPorHoras()) {
    let horasTeoricas = 8;
    let horasReales = 8;
        if (record.theoreticalCheckIn && record.theoreticalCheckOut) {
      horasTeoricas = calcularHorasDesdeHorarios(record.theoreticalCheckIn, record.theoreticalCheckOut);
    }
        if (record.checkIn && record.checkOut) {
      horasReales = calcularHorasDesdeHorarios(record.checkIn, record.checkOut);
    }
    setNewAttendanceData({
      employeeId: record.employeeId.toString(),
      date: dateForInput,
      eventType: record.eventType,
          theoreticalCheckIn: toTimeInput(record.theoreticalCheckIn),
          theoreticalCheckOut: toTimeInput(record.theoreticalCheckOut),
          checkIn: toTimeInput(record.checkIn) || '',
          checkOut: toTimeInput(record.checkOut) || '',
          horasTeoricas,
          horasReales,
          advances: record.advances || 0,
          income: record.income || 0,
          discounts: record.discounts || 0,
          notes: record.observaciones || '',
          concepts: conceptsForFrontend
        });
      } else {
        // Tareo normal (por días): también hay que llenar newAttendanceData para que el modal muestre los datos
        setNewAttendanceData({
          employeeId: record.employeeId != null ? record.employeeId.toString() : '',
          date: dateForInput,
          eventType: record.eventType || 'asistio',
          theoreticalCheckIn: toTimeInput(record.theoreticalCheckIn) || '',
          theoreticalCheckOut: toTimeInput(record.theoreticalCheckOut) || '',
          checkIn: toTimeInput(record.checkIn) || '',
          checkOut: toTimeInput(record.checkOut) || '',
      advances: record.advances || 0,
      income: record.income || 0,
      discounts: record.discounts || 0,
      notes: record.observaciones || '',
      concepts: conceptsForFrontend
    });
      }
    
    setShowEditModal(true);
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este registro?')) {
      try {
        const response = await tareoService.eliminarTareo(recordId);
        if (response.success) {
          await loadAttendanceRecords();
          alert('Registro eliminado exitosamente');
        } else {
          alert('Error al eliminar registro: ' + response.message);
        }
      } catch (error) {
        console.error('Error al eliminar registro:', error);
        alert('Error al eliminar registro. Por favor intente nuevamente.');
      }
    }
  };

  // Función específica para editar solo conceptos en registros automáticos
  // Actualizar una fila de concepto en el modal de edición (solo en onBlur)
  const updateEditingRecordConcept = (index, data) => {
    if (!editingRecord?.concepts || index >= editingRecord.concepts.length) return;
    const concept = editingRecord.concepts[index];
    const updated = {
      ...concept,
      type: data.type,
      amount: data.amount,
      description: data.description,
      monto: data.amount,
      observaciones: data.description,
      destino_boleta: data.destino_boleta != null ? data.destino_boleta : (concept.destino_boleta || concept.tipo_boleta || 'plame'),
    };
    if (data.type != null && data.type !== '') {
      const ct = conceptTypes.find(c => c.value == data.type);
      const pc = plameConceptos.find(p => p.id === parseInt(data.type));
      if (ct) {
        updated.conceptType = ct.type;
        updated.plame_configuracion_id = data.type;
        updated.tipo_concepto = ct.type;
      } else if (pc) {
        updated.conceptType = pc.tipo_concepto === 'adelanto' ? 'advance' : pc.tipo_concepto === 'ingreso' ? 'income' : 'discount';
        updated.plame_configuracion_id = String(pc.id);
        updated.tipo_concepto = pc.tipo_concepto;
      }
    }
    const updatedConcepts = editingRecord.concepts.map((c, i) => (i === index ? updated : c));
    setEditingRecord({ ...editingRecord, concepts: updatedConcepts });
  };

  const handleEditAutomaticRecord = (record) => {
    console.log('🔒 Editando conceptos de registro automático:', record);
    
    // Crear una copia del registro para editar solo conceptos
    const recordToEdit = {
      ...record,
      concepts: record.concepts || []
    };
    
    console.log('📝 Registro automático a editar preparado:', recordToEdit);
    
    // Inicializar newAttendanceData con los conceptos existentes
    setNewAttendanceData({
      employeeId: record.employeeId,
      date: record.date,
      eventType: record.eventType,
      theoreticalCheckIn: record.theoreticalCheckIn,
      theoreticalCheckOut: record.theoreticalCheckOut,
      checkIn: record.checkIn,
      checkOut: record.checkOut,
      advances: 0,
      income: 0,
      discounts: 0,
      notes: record.notes || record.observations || '',
      concepts: record.concepts || []
    });
    
    setEditingRecord(recordToEdit);
    setShowAutomaticEditModal(true);
  };

  const handleUpdateRecord = async () => {
    // Para conceptos automáticos, solo validar conceptos si existen
    if (isAutomaticRecord(editingRecord)) {
      // Para automáticos, los conceptos están en editingRecord.concepts
      const conceptos = editingRecord.concepts || [];
      
      // Solo validar si hay conceptos (se permite no tener conceptos)
      if (conceptos.length > 0) {
        // Validar que todos los conceptos tengan tipo y monto
        const conceptosIncompletos = conceptos.some(c => {
          // Para editingRecord los campos pueden llamarse diferente
          const tipoValido = (c.type && c.type !== '') || (c.plame_configuracion_id && c.plame_configuracion_id !== '');
          const montoValido = getConceptoMonto(c) > 0;
          return !tipoValido || !montoValido;
        });
        
        if (conceptosIncompletos) {
          alert('Por favor completa todos los conceptos con tipo y monto válido');
          return;
        }
      }
    } else {
      // Para registros normales, validar campos completos
    if (!newAttendanceData.employeeId || !newAttendanceData.date) {
      alert('Por favor completa los campos requeridos');
      return;
      }
    }

    setIsUpdating(true);
    try {
      // Preparar conceptos para envío al backend
      const conceptosSource = isAutomaticRecord(editingRecord) ? editingRecord.concepts : newAttendanceData.concepts;
      const conceptosParaBackend = (conceptosSource || []).flatMap((concepto) =>
        mapearConceptoAPayloadsBackend(concepto, conceptTypes, plameConceptos, planillaAlternativaActiva)
      );

      // Preparar datos para envío
      let tareoData;
      
      if (isAutomaticRecord(editingRecord)) {
        // Para conceptos automáticos: solo enviar conceptos, mantener otros datos del registro original
        tareoData = {
          tipo_evento: editingRecord.eventType,
          hora_entrada_teorica: editingRecord.theoreticalCheckIn,
          hora_salida_teorica: editingRecord.theoreticalCheckOut,
          hora_entrada_real: editingRecord.checkIn,
          hora_salida_real: editingRecord.checkOut,
          observaciones: editingRecord.notes || editingRecord.observations,
          conceptos: conceptosParaBackend
        };
      } else if (esTareoPorHoras()) {
        // Para tareo por horas: calcular horarios automáticamente
        const horaEntrada = newAttendanceData.theoreticalCheckIn;
        const horaSalidaTeorica = calcularHoraSalida(horaEntrada, newAttendanceData.horasTeoricas);
        const horaSalidaReal = calcularHoraSalida(horaEntrada, newAttendanceData.horasReales);
        
        tareoData = {
          tipo_evento: newAttendanceData.eventType,
          hora_entrada_teorica: horaEntrada,
          hora_salida_teorica: horaSalidaTeorica,
          hora_entrada_real: horaEntrada,
          hora_salida_real: horaSalidaReal,
          observaciones: newAttendanceData.notes || null,
          conceptos: conceptosParaBackend
        };
      } else {
        // Para tareo simple/detallado: usar lógica existente
        tareoData = {
          tipo_evento: newAttendanceData.eventType,
          hora_entrada_teorica: newAttendanceData.theoreticalCheckIn,
          hora_salida_teorica: newAttendanceData.theoreticalCheckOut,
          hora_entrada_real: configuracionTareo && configuracionTareo.es_simple 
            ? newAttendanceData.theoreticalCheckIn 
            : (newAttendanceData.checkIn || null),
          hora_salida_real: configuracionTareo && configuracionTareo.es_simple 
            ? newAttendanceData.theoreticalCheckOut 
            : (newAttendanceData.checkOut || null),
          observaciones: newAttendanceData.notes || null,
          conceptos: conceptosParaBackend
        };
      }

      if (tasaNocturnaActiva && !isAutomaticRecord(editingRecord)) {
        tareoData.considerar_tasa_nocturna = considerarTasaNocturnaIndividual;
      }

      // Debug: Ver qué se está enviando al backend
      console.log('🚀 Enviando al backend:', tareoData);
      console.log('📋 Conceptos enviados:', tareoData.conceptos);
      console.log('📏 Tipo de conceptos:', Array.isArray(tareoData.conceptos) ? 'Array' : typeof tareoData.conceptos);

      // Enviar al backend
      const response = await tareoService.actualizarTareo(editingRecord.id, tareoData);
      
      if (response.success) {
        await loadAttendanceRecords();
        
        setShowEditModal(false);
        setEditingRecord(null);
        setConsiderarTasaNocturnaIndividual(false);
        console.log('✅ Registro actualizado exitosamente, limpiando estado');
        setNewAttendanceData({
          employeeId: '',
          date: new Date().toISOString().split('T')[0],
          eventType: 'asistio',
          theoreticalCheckIn: '',
          theoreticalCheckOut: '',
          checkIn: '',
          checkOut: '',
          advances: 0,
          income: 0,
          discounts: 0,
          notes: '',
          concepts: []
        });
        
        alert('Registro actualizado exitosamente');
      } else {
        alert('Error al actualizar registro: ' + response.message);
      }
    } catch (error) {
      console.error('Error al actualizar registro:', error);
      alert('Error al actualizar registro. Por favor intente nuevamente.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ===== FUNCIONES PARA BORRADO MASIVO =====

  // Función para manejar la selección de un registro individual
  const handleSelectRecord = (recordId) => {
    setSelectedRecords(prev => {
      if (prev.includes(recordId)) {
        return prev.filter(id => id !== recordId);
      } else {
        return [...prev, recordId];
      }
    });
  };

  // Función para seleccionar/deseleccionar todos los registros eliminables
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRecords([]);
      setSelectAll(false);
    } else {
      const deletableRecords = filteredRecords.filter(record => canDeleteRecord(record));
      setSelectedRecords(deletableRecords.map(record => record.id));
      setSelectAll(true);
    }
  };

  // Función para manejar el borrado masivo
  const handleMassiveDelete = async () => {
    if (selectedRecords.length === 0) {
      alert('Por favor selecciona al menos un registro para eliminar');
      return;
    }

    const confirmMessage = `¿Estás seguro de que quieres eliminar ${selectedRecords.length} registro(s)?`;
    if (window.confirm(confirmMessage)) {
      try {
        setLoading(true);
        const response = await tareoService.eliminarTareoMasivo(selectedRecords);
        
        if (response.success) {
          await loadAttendanceRecords();
          setSelectedRecords([]);
          setSelectAll(false);
          alert(response.message || 'Registros eliminados exitosamente');
        } else {
          alert('Error al eliminar registros: ' + response.message);
        }
      } catch (error) {
        console.error('Error al eliminar registros:', error);
        alert('Error al eliminar registros. Por favor intente nuevamente.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Función para obtener registros eliminables seleccionables
  const getSelectableRecords = () => {
    return filteredRecords.filter(record => canDeleteRecord(record));
  };

  // ===== FUNCIONES PARA REGISTRO MASIVO =====
  
  // Función para abrir modal masivo y cargar empleados activos
  const handleOpenMassiveModal = () => {
    console.log('🚀 Abriendo modal de registro masivo');
    setMassiveDate(new Date().toISOString().split('T')[0]);
    setShowMassiveModal(true);
    loadMassiveEmployees(new Date().toISOString().split('T')[0]);
  };

  // Función para cargar empleados activos automáticamente (filtrados por proyecto)
  const loadMassiveEmployees = (selectedDate) => {
    console.log('📅 Cargando empleados para registro masivo, fecha:', selectedDate);
    console.log('🏗️ Proyecto seleccionado:', selectedProject?.nombre);
    console.log('🕐 Es tareo por horas:', esTareoPorHoras());
    console.log('🔍 Filtros de cargos aplicados:', filtrosCargosSeleccionados);
    console.log('🔍 Filtros de áreas aplicados:', filtrosAreasSeleccionadas);
    
    // Usar la función de filtro que ya incluye proyecto y fecha
    let empleadosActivos = filterEmployeesByDate(selectedDate);
    
    // Aplicar filtros adicionales de cargo y área
    if (filtrosCargosSeleccionados.length > 0) {
      empleadosActivos = empleadosActivos.filter(emp => 
        filtrosCargosSeleccionados.includes(emp.cargo)
      );
      console.log('👥 Después del filtro de cargos:', empleadosActivos.length);
    }
    
    if (filtrosAreasSeleccionadas.length > 0) {
      empleadosActivos = empleadosActivos.filter(emp => 
        filtrosAreasSeleccionadas.includes(emp.areas_de_trabajo || 'Sin área')
      );
      console.log('👥 Después del filtro de áreas:', empleadosActivos.length);
    }
    
    // Crear estructura de datos para el registro masivo
    const empleadosMasivos = empleadosActivos.map(empleado => {
      const baseData = {
        id: empleado.id,
        nombre_completo: empleado.nombre_completo,
        cargo: empleado.cargo,
        area_trabajo: empleado.areas_de_trabajo || 'Sin área',
        proyecto: empleado.proyecto,
        theoreticalCheckIn: empleado.hora_entrada_formatted || '08:00',
        theoreticalCheckOut: empleado.hora_salida_formatted || '17:00',
        eventType: 'asistio',
        concepts: [],
        considerarTasaNocturna: false,
      };

      if (esTareoPorHoras()) {
        // Para tareo por horas: agregar campos de horas
        return {
          ...baseData,
          horasTeoricas: 8,
          horasReales: 8,
          checkIn: empleado.hora_entrada_formatted || '08:00',
          checkOut: calcularHoraSalida(empleado.hora_entrada_formatted || '08:00', 8)
        };
      } else {
        // Para tareo simple/detallado: usar horarios fijos
        return {
          ...baseData,
          checkIn: empleado.hora_entrada_formatted || '08:00',
          checkOut: empleado.hora_salida_formatted || '17:00'
        };
      }
    });
    
    console.log('👥 Empleados cargados para registro masivo (del proyecto):', empleadosMasivos.length);
    setMassiveEmployees(empleadosMasivos);
  };

  // Función para actualizar la fecha del registro masivo
  const handleMassiveDateChange = (newDate) => {
    setMassiveDate(newDate);
    loadMassiveEmployees(newDate);
  };

  // Función para eliminar empleado de la lista masiva
  const removeMassiveEmployee = (employeeId) => {
    setMassiveEmployees(prev => prev.filter(emp => emp.id !== employeeId));
  };

  // Evento único para cambio de hora (solo ese evento): actualiza solo el campo de hora del empleado, sin más lógica.
  const handleMassiveHourFieldChange = (employeeId, field, value) => {
    setMassiveEmployees(prev =>
      prev.map(emp => (emp.id !== employeeId ? emp : { ...emp, [field]: value }))
    );
  };

  // Función para actualizar datos de empleado individual en lista masiva
  const updateMassiveEmployee = (employeeId, field, value) => {
    setMassiveEmployees(prev => 
      prev.map(emp => {
        if (emp.id !== employeeId) return emp;
        
        const updatedEmp = { ...emp, [field]: value };
        
        // Eventos sin horas reales: dejar horas reales en 0 y horarios reales vacíos
        if (field === 'eventType' && EVENTOS_SIN_HORAS_REALES.includes(value)) {
          updatedEmp.horasReales = 0;
          updatedEmp.checkIn = '';
          updatedEmp.checkOut = '';
          return updatedEmp;
        }
        
        // Eventos con horas: al volver a asistio u otro, restaurar horas reales a 8 y horarios
        if (field === 'eventType' && !EVENTOS_SIN_HORAS_REALES.includes(value)) {
          updatedEmp.horasReales = 8;
          const entrada = updatedEmp.theoreticalCheckIn || '08:00';
          updatedEmp.checkIn = entrada;
          updatedEmp.checkOut = calcularHoraSalida(entrada, 8);
          return updatedEmp;
        }
        
        // Tareo por horas: Entrada/H.Teóricas/H.Reales solo actualizan el valor; recálculo en onBlur
        if (esTareoPorHoras() && field !== 'theoreticalCheckIn' && field !== 'horasTeoricas' && field !== 'horasReales') {
          if (field === 'checkIn') {
            updatedEmp.checkOut = calcularHoraSalida(value, updatedEmp.horasReales || 8);
          }
        }
        
        return updatedEmp;
      })
    );
  };

  // Recalcular salidas (teórica y real) al salir del campo en edición masiva, no al escribir
  const updateMassiveEmployeeHoursBlur = (employeeId) => {
    if (!esTareoPorHoras()) return;
    setMassiveEmployees(prev =>
      prev.map(emp => {
        if (emp.id !== employeeId) return emp;
        const entrada = emp.theoreticalCheckIn || '08:00';
        const hT = emp.horasTeoricas ?? 8;
        const hR = emp.horasReales ?? 8;
        return {
          ...emp,
          theoreticalCheckOut: calcularHoraSalida(entrada, hT),
          checkIn: entrada,
          checkOut: calcularHoraSalida(entrada, hR)
        };
      })
    );
  };

  // Commit de los 3 campos desde subcomponente (estado local): un solo setState, no eventos en cada tecla
  const commitMassiveEmployeeHours = (employeeId, entrada, hT, hR) => {
    if (!esTareoPorHoras()) return;
    setMassiveEmployees(prev =>
      prev.map(emp => {
        if (emp.id !== employeeId) return emp;
        const ent = entrada || '08:00';
        const t = hT ?? 8;
        const r = hR ?? 8;
        return {
          ...emp,
          theoreticalCheckIn: ent,
          horasTeoricas: t,
          horasReales: r,
          theoreticalCheckOut: calcularHoraSalida(ent, t),
          checkIn: ent,
          checkOut: calcularHoraSalida(ent, r)
        };
      })
    );
  };

  // Función para cambiar evento de TODOS los empleados masivamente
  const updateAllMassiveEmployeesEvent = (newEventType) => {
    const sinHorasReales = EVENTOS_SIN_HORAS_REALES.includes(newEventType);
    setMassiveEmployees(prev => 
      prev.map(emp => {
        const base = { ...emp, eventType: newEventType };
        if (sinHorasReales) {
          return { ...base, horasReales: 0, checkIn: '', checkOut: '' };
        }
        // Evento con horas: restaurar a 8h y horarios
        const entrada = emp.theoreticalCheckIn || '08:00';
        return {
          ...base,
          horasReales: 8,
          checkIn: entrada,
          checkOut: calcularHoraSalida(entrada, 8)
        };
      })
    );
  };

  // Función para actualizar masivamente las horas de todos los empleados
  const updateAllMassiveEmployeesHours = (field, value) => {
    setMassiveEmployees(prev => 
      prev.map(emp => {
        const updatedEmp = { ...emp, [field]: value };
        
        // Recalcular horarios según el campo que cambió
        if (field === 'horasTeoricas') {
          updatedEmp.theoreticalCheckOut = calcularHoraSalida(updatedEmp.theoreticalCheckIn, parseFloat(value) || 8);
        } else if (field === 'horasReales') {
          updatedEmp.checkOut = calcularHoraSalida(updatedEmp.checkIn || updatedEmp.theoreticalCheckIn, parseFloat(value) || 8);
        }
        
        return updatedEmp;
      })
    );
  };

  // Función para inicializar valores de tareo por horas
  const inicializarValoresTareoPorHoras = (data) => {
    if (esTareoPorHoras()) {
      console.log('🕐 Inicializando valores para tareo por horas');
      return {
        ...data,
        horasTeoricas: data.horasTeoricas && data.horasTeoricas > 0 ? data.horasTeoricas : 8,
        horasReales: data.horasReales && data.horasReales > 0 ? data.horasReales : 8
      };
    }
    return data;
  };

  // ===== FUNCIONES PARA CONCEPTOS EN REGISTRO MASIVO =====
  
  // Agregar concepto a un empleado específico
  const addMassiveConcept = (employeeId) => {
    console.log('➕ Agregando concepto al empleado:', employeeId);
    setMassiveEmployees(prev => 
      prev.map(emp => 
        emp.id === employeeId 
          ? { 
              ...emp, 
              concepts: [...emp.concepts, { type: '', amount: 0, description: '', conceptType: '', destino_boleta: 'plame' }] 
            }
          : emp
      )
    );
  };

  // Eliminar concepto de un empleado específico
  const removeMassiveConcept = (employeeId, conceptIndex) => {
    setMassiveEmployees(prev => 
      prev.map(emp => 
        emp.id === employeeId 
          ? { 
              ...emp, 
              concepts: emp.concepts.filter((_, i) => i !== conceptIndex) 
            }
          : emp
      )
    );
  };

  // Actualizar concepto de un empleado (se usa desde subcomponente en onBlur)
  const updateMassiveConcept = (employeeId, conceptIndex, field, value) => {
    setMassiveEmployees(prev => 
      prev.map(emp => 
        emp.id === employeeId 
          ? {
              ...emp,
              concepts: emp.concepts.map((concept, i) => {
                if (i === conceptIndex) {
                  const updatedConcept = { ...concept, [field]: value };
                  
                  // Si se está actualizando el tipo, también actualizar el tipo de concepto
                  if (field === 'type') {
                    // Buscar en conceptTypes primero
                    const conceptType = conceptTypes.find(ct => ct.value === value);
                    if (conceptType) {
                      updatedConcept.conceptType = conceptType.type;
                    } else {
                      // Buscar en plameConceptos
                      const plameConcepto = plameConceptos.find(pc => pc.id === parseInt(value));
                      if (plameConcepto) {
                        // Determinar tipo basado en el tipo_concepto del PLAME
                        if (plameConcepto.tipo_concepto === 'adelanto') {
                          updatedConcept.conceptType = 'advance';
                        } else if (plameConcepto.tipo_concepto === 'ingreso') {
                          updatedConcept.conceptType = 'income';
                        } else {
                          updatedConcept.conceptType = 'discount';
                        }
                      }
                    }
                  }
                  
                  return updatedConcept;
                }
                return concept;
              })
            }
          : emp
      )
    );
  };

  // Commit de una fila de concepto en edición masiva (solo en onBlur)
  const commitMassiveConcept = (employeeId, conceptIndex, data) => {
    setMassiveEmployees(prev =>
      prev.map(emp => {
        if (emp.id !== employeeId || !emp.concepts || conceptIndex >= emp.concepts.length) return emp;
        const concept = emp.concepts[conceptIndex];
        const updated = { ...concept, ...data };
        if (data.amount != null && data.amount !== '') {
          updated.amount = data.amount;
          updated.monto = getConceptoMonto(updated);
        }
        if (data.destino_boleta != null) {
          updated.destino_boleta = data.destino_boleta;
        }
        if (data.type != null) {
          const ct = conceptTypes.find(c => c.value === data.type);
          if (ct) updated.conceptType = ct.type;
          else {
            const pc = plameConceptos.find(p => p.id === parseInt(data.type));
            if (pc) updated.conceptType = pc.tipo_concepto === 'adelanto' ? 'advance' : pc.tipo_concepto === 'ingreso' ? 'income' : 'discount';
          }
        }
        return {
          ...emp,
          concepts: emp.concepts.map((c, i) => (i === conceptIndex ? updated : c))
        };
      })
    );
  };

  // Función para registro masivo (usando servicio masivo)
  const handleMassiveRegister = async () => {
    console.log('🚀 Iniciando registro masivo de tareos - usando servicio masivo');
    
    if (massiveEmployees.length === 0) {
      alert('No hay empleados para registrar');
      return;
    }

    setIsCreatingMassive(true);
    
    try {
      console.log(`📊 Preparando ${massiveEmployees.length} tareos para registro masivo...`);
      
      // Preparar array de todos los tareos para envío masivo
      const tareosParaEnvio = [];
      const erroresValidacion = [];
      
      for (let i = 0; i < massiveEmployees.length; i++) {
        const empleado = massiveEmployees[i];
        console.log(`\n--- PREPARANDO EMPLEADO ${i + 1}/${massiveEmployees.length}: ${empleado.nombre_completo} ---`);
        
        try {
          // Preparar conceptos para envío al backend (con destino boleta si PA activa)
          const conceptosParaBackend = (empleado.concepts || []).flatMap((concepto) =>
            mapearConceptoAPayloadsBackend(concepto, conceptTypes, plameConceptos, planillaAlternativaActiva)
          );

          // Preparar datos para envío (misma lógica que handleAddAttendance)
          let tareoData;
          
          const eventoRequiereHorasRealesVacias = EVENTOS_SIN_HORAS_REALES.includes(empleado.eventType);
          
          if (esTareoPorHoras()) {
            // Para tareo por horas: calcular horarios automáticamente desde las horas
            const horaEntrada = empleado.theoreticalCheckIn || '08:00';
            const horaSalidaTeorica = calcularHoraSalida(horaEntrada, empleado.horasTeoricas || 8);
            const horaSalidaReal = eventoRequiereHorasRealesVacias ? null : calcularHoraSalida(horaEntrada, empleado.horasReales || 8);
            
            tareoData = {
              empresa_id: selectedCompany.id,
              empleado_id: empleado.id,
              proyecto_id: selectedProject.id,
              fecha: massiveDate,
              tipo_evento: empleado.eventType,
              hora_entrada_teorica: horaEntrada,
              hora_salida_teorica: horaSalidaTeorica,
              hora_entrada_real: eventoRequiereHorasRealesVacias ? null : horaEntrada,
              hora_salida_real: horaSalidaReal,
              observaciones: null,
              conceptos: conceptosParaBackend
            };
          } else {
            // Para tareo simple/detallado: usar lógica original
            tareoData = {
              empresa_id: selectedCompany.id,
              empleado_id: empleado.id,
              proyecto_id: selectedProject.id,
              fecha: massiveDate,
              tipo_evento: empleado.eventType,
              hora_entrada_teorica: empleado.theoreticalCheckIn,
              hora_salida_teorica: empleado.theoreticalCheckOut,
              hora_entrada_real: eventoRequiereHorasRealesVacias ? null : (configuracionTareo && configuracionTareo.es_simple 
                ? empleado.theoreticalCheckIn 
                : (empleado.checkIn || null)),
              hora_salida_real: eventoRequiereHorasRealesVacias ? null : (configuracionTareo && configuracionTareo.es_simple 
                ? empleado.theoreticalCheckOut 
                : (empleado.checkOut || null)),
              observaciones: null,
              conceptos: conceptosParaBackend
            };
          }

          tareoData = aplicarFlagTasaNocturna(
            tareoData,
            tasaNocturnaActiva,
            empleado.considerarTasaNocturna
          );

          // Validar datos antes de agregar al array
          const validacion = tareoService.validarTareo(tareoData);
          if (validacion.esValido) {
            tareosParaEnvio.push(tareoData);
            console.log(`✅ Tareo preparado para ${empleado.nombre_completo}`);
          } else {
            console.log(`❌ Error de validación para ${empleado.nombre_completo}:`, validacion.errores);
            erroresValidacion.push(`${empleado.nombre_completo}: ${validacion.errores.join(', ')}`);
          }
        } catch (empleadoError) {
          console.error(`💥 Error al preparar empleado ${empleado.nombre_completo}:`, empleadoError);
          erroresValidacion.push(`${empleado.nombre_completo}: Error al preparar datos`);
        }
      }

      console.log(`\n📊 RESUMEN DE PREPARACIÓN:`);
      console.log(`✅ Tareos válidos preparados: ${tareosParaEnvio.length}`);
      console.log(`❌ Tareos con errores: ${erroresValidacion.length}`);
      
      if (erroresValidacion.length > 0) {
        console.log(`📋 Errores de validación:`, erroresValidacion);
      }

      // Si no hay tareos válidos para enviar, mostrar error
      if (tareosParaEnvio.length === 0) {
        alert(`❌ No hay tareos válidos para registrar.\n\nErrores:\n${erroresValidacion.join('\n')}`);
        return;
      }

      console.log(`\n🚀 Enviando ${tareosParaEnvio.length} tareos al backend usando servicio masivo...`);
      
      // Enviar todos los tareos de una vez usando el servicio masivo
      const response = await tareoService.crearTareoMasivo(tareosParaEnvio);
      
      console.log('📨 Respuesta del servicio masivo:', response);

      if (response.success) {
        const exitosos = response.data?.exitosos || tareosParaEnvio.length;
        const fallidos = response.data?.fallidos || 0;
        const errores = response.data?.errores || [];

        console.log('\n=== RESUMEN DEL REGISTRO MASIVO ===');
        console.log('✅ Exitosos:', exitosos);
        console.log('❌ Fallidos:', fallidos);
        console.log('📋 Errores:', errores);

        // Recargar registros
        await loadAttendanceRecords();
        
        // Cerrar modal y limpiar
        setShowMassiveModal(false);
        setMassiveEmployees([]);
        
        // Combinar errores de validación con errores del backend
        const todosLosErrores = [...erroresValidacion, ...errores];
        
        // Mostrar resumen al usuario
        if (fallidos === 0 && erroresValidacion.length === 0) {
          alert(`✅ ¡Éxito total! Se registraron exitosamente todos los ${exitosos} tareos`);
        } else if (exitosos === 0) {
          alert(`❌ No se pudo registrar ningún tareo.\n\nErrores:\n${todosLosErrores.join('\n')}`);
        } else {
          alert(`⚠️ Registro parcial completado:\n\n✅ Exitosos: ${exitosos}\n❌ Fallidos: ${fallidos + erroresValidacion.length}\n\nErrores:\n${todosLosErrores.slice(0, 10).join('\n')}${todosLosErrores.length > 10 ? `\n... y ${todosLosErrores.length - 10} errores más` : ''}`);
        }
      } else {
        console.error('❌ Error en el servicio masivo:', response.message);
        alert(`❌ Error al realizar el registro masivo: ${response.message}`);
      }
      
    } catch (error) {
      console.error('💥 Error crítico en registro masivo:', error);
      alert('Error crítico al realizar el registro masivo. Por favor intente nuevamente.');
    } finally {
      setIsCreatingMassive(false);
    }
  };

  // Función para cerrar modal masivo
  const handleCloseMassiveModal = () => {
    console.log('❌ Cerrando modal de registro masivo');
    setShowMassiveModal(false);
    setMassiveEmployees([]);
    setMassiveDate(new Date().toISOString().split('T')[0]);
  };

  const empleadoActivoEnRango = (emp, fechaInicio, fechaFin) => {
    return empleadoActivoDuranteRangoPlameOInterno(emp, fechaInicio, fechaFin);
  };

  const empleadosParaTareoPerfecto = useMemo(() => {
    if (!employees?.length || !selectedProject) return [];
    return employees.filter(emp => {
      const pertenece = emp.proyecto === selectedProject.nombre || emp.proyecto_id === selectedProject.id ||
        (emp.proyecto && String(emp.proyecto) === String(selectedProject.id));
      if (!pertenece) return false;
      if (!emp.ultimo_historial) return false;
      if (emp.ultimo_historial.motivo_cese === 'No se presentó a laborar') return false;
      return emp.estado === 'Activo' && !emp.ultimo_historial.fecha_cese;
    });
  }, [employees, selectedProject]);

  const verificarTareoPerfectoYGenerar = async () => {
    const { empleadoIds, fechaInicio, fechaFin } = tareoPerfectoData;
    if (!selectedCompany?.id || !selectedProject?.id || !empleadoIds?.length || !fechaInicio || !fechaFin) {
      setTareoPerfectoData(prev => ({ ...prev, error: 'Seleccione al menos un empleado y el rango de fechas.' }));
      return;
    }
    setTareoPerfectoData(prev => ({ ...prev, validando: true, error: null, omitidos: [], creados: 0 }));

    const proyectoId = selectedProject.id;
    const [yI, mI, dI] = fechaInicio.split('-').map(Number);
    const [yF, mF, dF] = fechaFin.split('-').map(Number);
    const fin = new Date(yF, mF - 1, dF);
    const omitidos = [];
    const tareos = [];

    for (const empleadoId of empleadoIds) {
      const empId = typeof empleadoId === 'string' ? parseInt(empleadoId, 10) : empleadoId;
      const empleado = employees.find(e => e.id === empId);
      if (!empleado) {
        omitidos.push({ nombre: `ID ${empId}`, motivo: 'No encontrado' });
        continue;
      }
      if (!empleadoActivoEnRango(empleado, fechaInicio, fechaFin)) {
        omitidos.push({ nombre: empleado.nombre_completo, motivo: 'No activo en todo el rango (ingreso/cese)' });
        continue;
      }
      try {
        const res = await tareoService.obtenerTareos({
          empresa_id: selectedCompany.id,
          empleado_id: empId,
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        });
        const lista = res?.data?.data ?? (Array.isArray(res?.data) ? res.data : []);
        if (Array.isArray(lista) && lista.length > 0) {
          omitidos.push({ nombre: empleado.nombre_completo, motivo: 'Ya tiene registros en el período' });
          continue;
        }
      } catch (e) {
        omitidos.push({ nombre: empleado.nombre_completo, motivo: 'Error al verificar registros' });
        continue;
      }
      const entrada = empleado.hora_entrada_formatted || '08:00';
      const salida = calcularHoraSalida(entrada, 8) || '17:00';
      let d = new Date(yI, mI - 1, dI);
      while (d <= fin) {
        const año = d.getFullYear();
        const mes = String(d.getMonth() + 1).padStart(2, '0');
        const dia = String(d.getDate()).padStart(2, '0');
        const fechaStr = `${año}-${mes}-${dia}`;
        const esDomingo = d.getDay() === 0;
        tareos.push({
          empresa_id: selectedCompany.id,
          empleado_id: empId,
          proyecto_id: proyectoId,
          fecha: fechaStr,
          tipo_evento: esDomingo ? 'descanso' : 'asistio',
          hora_entrada_teorica: entrada,
          hora_salida_teorica: salida,
          hora_entrada_real: esDomingo ? null : entrada,
          hora_salida_real: esDomingo ? null : salida,
          observaciones: esDomingo ? 'Descanso semanal (tareo perfecto)' : 'Tareo perfecto'
        });
        d.setDate(d.getDate() + 1);
      }
    }

    if (tareos.length === 0) {
      setTareoPerfectoData(prev => ({
        ...prev,
        validando: false,
        error: omitidos.length ? 'Ningún empleado pudo ser procesado.' : null,
        omitidos
      }));
      return;
    }

    try {
      const result = await tareoService.crearTareoMasivo({ tareos });
      if (result?.success) {
        const exitosos = result.data?.exitosos ?? tareos.length;
        const msg = omitidos.length
          ? `Tareo perfecto: ${exitosos} registros creados para ${empleadoIds.length - omitidos.length} empleado(s). Omitidos: ${omitidos.map(o => `${o.nombre} (${o.motivo})`).join('; ')}`
          : `Tareo perfecto generado: ${exitosos} registros para ${empleadoIds.length} empleado(s).`;
        alert(msg);
        setShowTareoPerfectoModal(false);
        setTareoPerfectoData({ empleadoIds: [], fechaInicio: '', fechaFin: '', validando: false, error: null, omitidos: [], creados: 0 });
        loadAttendanceRecords();
      } else {
        setTareoPerfectoData(prev => ({ ...prev, validando: false, error: result?.message || 'Error al crear tareos.', omitidos }));
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Error al crear tareos.';
      setTareoPerfectoData(prev => ({ ...prev, validando: false, error: msg, omitidos }));
    }
  };

  const toggleTareoPerfectoEmpleado = (id) => {
    setTareoPerfectoData(prev => {
      const ids = prev.empleadoIds.includes(id) ? prev.empleadoIds.filter(x => x !== id) : [...prev.empleadoIds, id];
      return { ...prev, empleadoIds: ids, error: null, omitidos: [] };
    });
  };

  const seleccionarTodosTareoPerfecto = (checked) => {
    setTareoPerfectoData(prev => ({
      ...prev,
      empleadoIds: checked ? empleadosParaTareoPerfecto.map(e => e.id) : [],
      error: null,
      omitidos: []
    }));
  };

  const summary = getAttendanceSummary();

  // Si no hay empresa seleccionada, mostrar mensaje
  if (!selectedCompany) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para gestionar los tareos.
          </p>
        </div>
      </div>
    );
  }

  // Renderizar vista de selección de proyecto
  if (currentView === 'project-selection') {
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Seleccionar Proyecto</h1>
            <p className="text-gray-600">Elija un proyecto, o todos juntos para ver e importar tareos masivos</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={handleOpenGlobalImportacion}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-all duration-200 whitespace-nowrap"
              title="Importar tareos Excel de todos los proyectos"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <span>Importar Excel (todos los proyectos)</span>
            </button>
            <button
              type="button"
              onClick={handleOpenGlobalRegularizacion}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-amber-700 transition-all duration-200 whitespace-nowrap"
              title="Regularizar conceptos para todo el personal activo"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Regularizar conceptos (global)</span>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
            <span className="ml-3 text-gray-600">Cargando proyectos...</span>
          </div>
        )}

        {/* Projects Grid */}
        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
            {projects.length > 0 && (
              <div
                onClick={handleSelectTodasSedes}
                className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-6 shadow-sm border-2 border-indigo-200 hover:shadow-md transition-all duration-200 cursor-pointer hover:border-indigo-500 group md:col-span-2 lg:col-span-1"
              >
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center group-hover:from-indigo-600 group-hover:to-indigo-700 transition-all duration-200">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors duration-200">
                      Todos los proyectos
                    </h3>
                    <p className="text-sm text-indigo-600 font-medium">{projects.length} proyecto(s)</p>
                  </div>
                </div>
                <div className="border-t border-indigo-100 pt-4">
                  <p className="text-sm text-gray-600">
                    Ver e importar tareos de todos los proyectos juntos (ideal para carga masiva).
                  </p>
                </div>
                <div className="mt-4 flex items-center justify-end">
                  <svg className="w-5 h-5 text-indigo-400 group-hover:text-indigo-600 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            )}
            {projects.length > 0 ? (
              projects.map((project) => (
                <div
                  key={project.id}
                  onClick={() => handleProjectSelect(project)}
                  className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer hover:border-teal-500 group"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl flex items-center justify-center group-hover:from-teal-600 group-hover:to-teal-700 transition-all duration-200">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600 transition-colors duration-200">
                        {project.nombre}
                      </h3>
                      <p className="text-sm text-gray-500">Proyecto</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Estado:</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                        Activo
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-end">
                    <svg className="w-5 h-5 text-gray-400 group-hover:text-teal-500 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full">
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H5m2 0v-3.5a2 2 0 012-2h1a2 2 0 012 2V21" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay proyectos disponibles</h3>
                  <p className="text-gray-600">No se han encontrado proyectos para gestionar tareos.</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Modal Importador de Tareos/Regularización (también disponible desde selección de proyectos) */}
        {showImportarTareosModal && selectedCompany && (selectedProject || importadorModoGlobal) && (
          <ImportadorTareos
            onClose={() => {
              setShowImportarTareosModal(false);
              setImportadorModoGlobal(false);
              setImportadorInitialMode('importar');
            }}
            onImportSuccess={() => {
              if (selectedProject) {
                loadAttendanceRecords();
              }
              setShowImportarTareosModal(false);
              setImportadorModoGlobal(false);
              setImportadorInitialMode('importar');
            }}
            selectedCompany={selectedCompany}
            selectedProject={importadorModoGlobal || esTodasSedes ? null : selectedProject}
            projects={projects}
            employees={employees}
            plameConceptos={plameConceptos}
            initialMode={importadorModoGlobal ? importadorInitialMode : 'importar'}
            esTareoSimple={Boolean(configuracionTareo?.es_simple)}
            tasaNocturnaActiva={tasaNocturnaActiva}
          />
        )}
      </div>
    );
  }

  // Renderizar vista de calendario horizontal
  if (currentView === 'calendar-analysis') {
    const { calendarData, dateRange, originalDateRange } = processCalendarData();

    return (
      <div className="space-y-4 lg:space-y-6">
        {/* Header de calendario */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={handleBackToTareos}
              className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Volver a Tareos</span>
            </button>
            <div className="border-l border-gray-300 pl-4">
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                📅 Análisis de Calendario - {selectedProject?.nombre}
                {(filtrosCargosSeleccionados.length > 0 || filtrosAreasSeleccionadas.length > 0) && (
                  <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                    🔍 {filtrosCargosSeleccionados.length + filtrosAreasSeleccionadas.length} filtro(s) activo(s)
                  </span>
                )}
              </h1>
              <p className="text-gray-600">Vista horizontal de horas trabajadas para análisis</p>
            </div>
          </div>
          
          {/* Botón de exportación a Excel */}
          <div className="flex items-center space-x-2">
            <button
              onClick={exportCalendarToExcel}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 shadow-sm hover:shadow-md"
              title="Exportar calendario a Excel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Exportar a Excel</span>
            </button>
          </div>
        </div>

                 {/* Panel de filtros */}
           <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
             {/* Indicador del período actual */}
             <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-md">
               <h4 className="text-xs font-semibold text-indigo-900 mb-1">📅 Período Seleccionado:</h4>
               <div className="text-sm font-bold text-indigo-800">
                 {formatDate(calendarStartDate)} - {formatDate(calendarEndDate)}
               </div>
               <div className="text-xs text-indigo-600 mt-1">
                 {originalDateRange.length} días de análisis
               </div>
               <div className="text-xs text-indigo-500 mt-2 italic">
                 ℹ️ Se muestran semanas completas (lunes a domingo). Los días en gris pertenecen a otros meses.
               </div>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
               {/* Selector por Meses */}
               <div className="space-y-2">
                 <h3 className="text-xs font-semibold text-gray-900">🗓️ Seleccionar por Mes</h3>
                 <div className="space-y-2">
                   <div>
                     <label className="block text-xs text-gray-600 mb-1">Año</label>
                     <select
                       value={selectedYear}
                       onChange={(e) => handleMonthYearSelection(parseInt(e.target.value), selectedMonth)}
                       className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                     >
                       {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                         <option key={year} value={year}>{year}</option>
                       ))}
                     </select>
                   </div>
                   <div>
                     <label className="block text-xs text-gray-600 mb-1">Mes</label>
                     <select
                       value={selectedMonth}
                       onChange={(e) => handleMonthYearSelection(selectedYear, parseInt(e.target.value))}
                       className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                     >
                       <option value={0}>Enero</option>
                       <option value={1}>Febrero</option>
                       <option value={2}>Marzo</option>
                       <option value={3}>Abril</option>
                       <option value={4}>Mayo</option>
                       <option value={5}>Junio</option>
                       <option value={6}>Julio</option>
                       <option value={7}>Agosto</option>
                       <option value={8}>Septiembre</option>
                       <option value={9}>Octubre</option>
                       <option value={10}>Noviembre</option>
                       <option value={11}>Diciembre</option>
                     </select>
                   </div>
                   <button
                     onClick={() => {
                       const today = new Date();
                       handleMonthYearSelection(today.getFullYear(), today.getMonth());
                     }}
                     className="w-full px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors duration-200 font-medium"
                   >
                     🗓️ Ir a Este Mes
                   </button>
                 </div>
               </div>

                             {/* Agrupación */}
               <div className="space-y-2">
                 <h3 className="text-xs font-semibold text-gray-900">👥 Agrupar Por</h3>
                 <div className="space-y-1">
                   <label className="flex items-center p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                     <input
                       type="radio"
                       name="groupBy"
                       value="employee"
                       checked={calendarGroupBy === 'employee'}
                       onChange={(e) => setCalendarGroupBy(e.target.value)}
                       className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                     />
                     <div className="ml-2">
                       <span className="text-xs font-medium text-gray-900">👤 Por Empleado</span>
                       <p className="text-xs text-gray-500">Vista individual</p>
                     </div>
                   </label>
                   <label className="flex items-center p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors duration-200">
                     <input
                       type="radio"
                       name="groupBy"
                       value="position"
                       checked={calendarGroupBy === 'position'}
                       onChange={(e) => setCalendarGroupBy(e.target.value)}
                       className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                     />
                     <div className="ml-2">
                       <span className="text-xs font-medium text-gray-900">💼 Por Cargo</span>
                       <p className="text-xs text-gray-500">Vista agrupada</p>
                     </div>
                   </label>
                 </div>
               </div>

               {/* Filtros de Cargo y Área */}
               <div className="space-y-2">
                 <h3 className="text-xs font-semibold text-gray-900">🔍 Filtros de Personal</h3>
                 <div className="space-y-3">
                   {/* Filtro de Cargos */}
                   <div>
                     <MultiFiltroCargoComponent />
                   </div>
                   
                   {/* Filtro de Áreas */}
                   <div>
                     <MultiFiltroAreaComponent />
                   </div>
                   
                   {/* Indicador de filtros activos */}
                   {(filtrosCargosSeleccionados.length > 0 || filtrosAreasSeleccionadas.length > 0) && (
                     <div className="p-2 bg-yellow-100 border border-yellow-300 rounded text-xs">
                       <div className="font-medium text-yellow-800 mb-1">🔍 Filtros Activos:</div>
                       {filtrosCargosSeleccionados.length > 0 && (
                         <div className="text-yellow-700">• {filtrosCargosSeleccionados.length} cargo(s)</div>
                       )}
                       {filtrosAreasSeleccionadas.length > 0 && (
                         <div className="text-yellow-700">• {filtrosAreasSeleccionadas.length} área(s)</div>
                       )}
                     </div>
                   )}
                   
                   {/* Botón de limpiar filtros */}
                   <button
                     onClick={() => {
                       setFiltrosCargosSeleccionados([]);
                       setFiltrosAreasSeleccionadas([]);
                     }}
                     className="w-full px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors duration-200 font-medium"
                     disabled={filtrosCargosSeleccionados.length === 0 && filtrosAreasSeleccionadas.length === 0}
                   >
                     🧹 Limpiar Filtros
                   </button>
                 </div>
               </div>

               {/* Empleados seleccionados */}
               <div className="space-y-2">
                 <h3 className="text-xs font-semibold text-gray-900">👤 Empleados Incluidos</h3>
                 <div className="p-2 bg-indigo-50 rounded">
                   <div className="text-xs font-medium text-indigo-900 mb-2">
                     {calendarSelectedEmployees.length} de {employees.filter(emp => {
                       const matchesCargo = filtrosCargosSeleccionados.length === 0 || filtrosCargosSeleccionados.includes(emp.cargo);
                       const matchesArea = filtrosAreasSeleccionadas.length === 0 || filtrosAreasSeleccionadas.includes(emp.areas_de_trabajo || 'Sin área');
                       return matchesCargo && matchesArea;
                     }).length} empleados seleccionados
                   </div>
                   <div className="flex flex-wrap gap-1">
                     <button
                       onClick={() => {
                         const filteredEmpIds = employees.filter(emp => {
                           const matchesCargo = filtrosCargosSeleccionados.length === 0 || filtrosCargosSeleccionados.includes(emp.cargo);
                           const matchesArea = filtrosAreasSeleccionadas.length === 0 || filtrosAreasSeleccionadas.includes(emp.areas_de_trabajo || 'Sin área');
                           return matchesCargo && matchesArea;
                         }).map(emp => emp.id);
                         setCalendarSelectedEmployees(filteredEmpIds);
                       }}
                       className="px-2 py-1 text-xs bg-indigo-100 text-indigo-700 rounded hover:bg-indigo-200 transition-colors duration-200 font-medium"
                     >
                       ✅ Seleccionar Filtrados
                     </button>
                     <button
                       onClick={() => setCalendarSelectedEmployees([])}
                       className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200 font-medium"
                     >
                       ❌ Deseleccionar Todos
                     </button>
                   </div>
                 </div>
               </div>
            </div>
          </div>

        {/* Leyenda de colores */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">🎨 Leyenda de Colores y Resúmenes</h3>
          
          {/* Leyenda de días */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-700 mb-2">📅 Días:</h4>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-gray-100 rounded"></div>
                <span>Sin datos (0h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-purple-200 rounded"></div>
                <span>😴 Día de descanso (0h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-orange-200 rounded"></div>
                <span>🏥 Descanso médico (8h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-amber-200 rounded border border-amber-300/80"></div>
                <span>📝 Permiso (0h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-amber-200 rounded border border-amber-300/80"></div>
                <span>⛔ Suspensión (0h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-sky-200 rounded border border-sky-300/80"></div>
                <span>🎉 Feriado (0h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-500 rounded"></div>
                <span>❌ Falta (0h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-300 rounded"></div>
                <span>Muy pocas horas (&lt;2h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-orange-300 rounded"></div>
                <span>Pocas horas (2-&lt;4h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-300 rounded"></div>
                <span>Media jornada (4-&lt;6h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-300 rounded"></div>
                <span>Casi completa (6-&lt;8h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-500 rounded"></div>
                <span>Jornada completa (8h)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-blue-500 rounded"></div>
                <span>Horas extra (&gt;10h)</span>
              </div>
              {tasaNocturnaActiva && (
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-semibold">
                    🌙 Sí
                  </span>
                  <span>Tasa nocturna considerada</span>
                </div>
              )}
            </div>
          </div>

          {/* Leyenda de resúmenes semanales */}
          <div>
            <h4 className="text-xs font-semibold text-gray-700 mb-2">📊 Resúmenes Semanales:</h4>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-teal-50 border border-teal-200 rounded"></div>
                <span>Total de horas trabajadas por semana</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabla de calendario horizontal */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-full">
              <table className="w-full">
                {/* Header con fechas */}
                <thead className="bg-gray-50">
                  <tr>
                    <th className="sticky left-0 z-10 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200" style={{minWidth: '200px'}}>
                      {calendarGroupBy === 'employee' ? 'Empleado' : 'Cargo'}
                    </th>
                    {dateRange.map(item => {
                      if (item.type === 'date') {
                        const formattedDate = formatDateForCalendar(item.date, selectedMonth);
                        return (
                          <th
                            key={item.date}
                            className={`px-2 py-3 text-center text-xs font-medium uppercase tracking-wider border-r border-gray-200 min-w-16 ${
                              formattedDate.isWeekend ? 'bg-blue-50 text-blue-600' : 
                              formattedDate.isOutsideSelectedPeriod ? 'bg-gray-100 text-gray-400' : 
                              'text-gray-500'
                            }`}
                          >
                            <div className="space-y-1">
                              <div className={`font-bold ${formattedDate.isOutsideSelectedPeriod ? 'opacity-60' : ''}`}>
                                {formattedDate.day}
                              </div>
                              <div className={`text-xs opacity-75 ${formattedDate.isOutsideSelectedPeriod ? 'opacity-40' : ''}`}>
                                {formattedDate.weekday}
                              </div>
                              {formattedDate.isOutsideSelectedPeriod && (
                                <div className="text-xs opacity-50 font-normal">
                                  {formattedDate.month}
                                </div>
                              )}
                            </div>
                          </th>
                        );
                      } else {
                        // Columna de resumen semanal
                        return (
                          <th
                            key={item.date}
                            className="px-2 py-3 text-center text-xs font-medium text-white uppercase tracking-wider border-r border-gray-200 min-w-24 bg-teal-600"
                          >
                            <div className="space-y-1">
                              <div className="font-bold">📊</div>
                              <div className="text-xs opacity-90">Semana</div>
                            </div>
                          </th>
                        );
                      }
                    })}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100" style={{minWidth: '120px'}}>
                      Resumen
                    </th>
                  </tr>
                </thead>

                {/* Cuerpo de la tabla */}
                <tbody className="bg-white divide-y divide-gray-200">
                  {calendarData.map((group) => (
                    <tr key={group.groupKey} className="hover:bg-gray-50">
                      {/* Columna de empleado/cargo */}
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 border-r border-gray-200">
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">{group.groupLabel}</div>
                          <div className="text-xs text-gray-500">{group.groupSubLabel}</div>
                        </div>
                      </td>

                      {/* Celdas de días y resúmenes semanales */}
                      {dateRange.map((item) => {
                        if (item.type === 'date') {
                          // Celda de fecha normal
                          const day = group.dailyData.find(d => d.date === item.date);
                          if (!day) {
                            return (
                              <td key={item.date} className="px-1 py-2 text-center text-xs border-r border-gray-200 bg-gray-50">
                                <div className="text-gray-400">-</div>
                              </td>
                            );
                          }

                          const hours = calendarGroupBy === 'employee' ? day.totalHours : day.avgHours;
                          const colorClass = getCellColor(hours, calendarGroupBy === 'position', day.records);
                          const formattedDate = formatDateForCalendar(day.date, selectedMonth);

                          const uniqueEventTypes = day.records && day.records.length > 0
                            ? [...new Set(day.records.map(record => record.eventType))]
                            : [];
                          const eventosTooltip = uniqueEventTypes.length > 0
                            ? uniqueEventTypes.map((eventType) => getEventTypeLabel(eventType)).join(', ')
                            : '';
                          const tasaNocturnaTooltip = tasaNocturnaActiva && day.hasTasaNocturna
                            ? ` | Tasa nocturna: ${day.tasaNocturnaCount} registro(s)`
                            : '';

                          return (
                            <td
                              key={day.date}
                              className={`px-1 py-2 text-center text-xs border-r border-gray-200 ${
                                formattedDate.isOutsideSelectedPeriod ? 'opacity-60 bg-gray-50' : colorClass
                              } ${
                                formattedDate.isWeekend ? 'border-l-2 border-l-blue-300' : ''
                              }`}
                              title={`${day.date}: ${hours.toFixed(2)}h | Presentes: ${day.presentCount} | Ausentes: ${day.absentCount}${eventosTooltip ? ` | Eventos: ${eventosTooltip}` : ''}${tasaNocturnaTooltip}`}
                            >
                              <div className="space-y-1">
                                <div className="font-bold">{getDisplayHoursCalendar(hours, day.records)}h</div>
                                {tasaNocturnaActiva && day.hasTasaNocturna && (
                                  <div
                                    className="inline-flex items-center px-1 py-0.5 rounded bg-indigo-100 text-indigo-800 font-semibold text-[10px] leading-tight"
                                    title={
                                      calendarGroupBy === 'position' && day.tasaNocturnaCount > 1
                                        ? `${day.tasaNocturnaCount} empleados con tasa nocturna`
                                        : 'Este día considera tasa nocturna'
                                    }
                                  >
                                    🌙 {calendarGroupBy === 'position' && day.tasaNocturnaCount > 1
                                      ? day.tasaNocturnaCount
                                      : 'Sí'}
                                  </div>
                                )}
                                {/* Mostrar tipos de eventos únicos del día */}
                                {day.records && day.records.length > 0 && (
                                  <div className="text-xs opacity-75">
                                    {uniqueEventTypes.slice(0, 2).map((eventType) => (
                                      <div key={eventType} className="leading-tight text-xs">
                                        {getCalendarEventShortLabel(eventType)}
                                      </div>
                                    ))}
                                    {uniqueEventTypes.length > 2 && (
                                      <div className="text-xs opacity-60">
                                        +{uniqueEventTypes.length - 2} más
                                      </div>
                                    )}
                                  </div>
                                )}
                                {calendarGroupBy === 'position' && day.recordCount > 0 && (
                                  <div className="text-xs opacity-60">
                                    {day.presentCount}/{day.recordCount}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        } else {
                          // Celda de resumen semanal
                          const weeklySummary = calculateWeeklySummary(group, item.weekEndDate, dateRange);
                          
                          return (
                            <td
                              key={item.date}
                              className="px-2 py-2 text-center text-xs border-r border-gray-200 bg-teal-50"
                              title={`Semana terminada el ${item.weekEndDate}:
• Horas trabajadas: ${weeklySummary.weeklyHours.toFixed(2)}h
• Faltas: ${weeklySummary.faltasSemana} día(s)${configuracionTareo?.descontar_descansos ? `
• Descanso ganado: ${weeklySummary.horasDescanso.toFixed(2)}h de 8.00h
• Proporción descanso: ${weeklySummary.proporcionDescanso.toFixed(1)}%` : ''}`}
                            >
                              <div className="space-y-1">
                                {/* Horas trabajadas */}
                                <div className="font-bold text-teal-900">
                                  {weeklySummary.weeklyHours.toFixed(2)}h
                                </div>
                                
                                {/* Horas de descanso con color según proporción - Solo si "Descontar Descansos" está activado */}
                                {configuracionTareo?.descontar_descansos && (
                                  <div className={`text-xs font-medium ${
                                    weeklySummary.proporcionDescanso >= 87.5 ? 'text-green-700' :
                                    weeklySummary.proporcionDescanso >= 62.5 ? 'text-yellow-700' :
                                    weeklySummary.proporcionDescanso >= 37.5 ? 'text-orange-700' :
                                    'text-red-700'
                                  }`}>
                                    💤 {weeklySummary.horasDescanso.toFixed(2)}h
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        }
                      })}

                      {/* Columna de resumen */}
                      <td className="px-4 py-3 text-center bg-gray-50 border-l border-gray-300">
                        <div className="space-y-1 text-xs">
                          <div className="font-bold text-gray-900">
                            {group.totalHours.toFixed(2)}h
                          </div>
                          <div className="text-gray-600">
                            Promedio: {group.avgDailyHours.toFixed(2)}h/día
                          </div>
                          <div className="text-gray-600">
                            Días trabajados: {group.workingDays}
                          </div>
                          {calendarGroupBy === 'position' && (
                            <div className="text-gray-600">
                              P: {group.totalPresent} | F: {group.totalAbsent}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Estadísticas generales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Horas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {calendarData.reduce((sum, group) => sum + group.totalHours, 0).toFixed(2)}h
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Promedio Diario</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(calendarData.reduce((sum, group) => sum + group.avgDailyHours, 0) / Math.max(calendarData.length, 1)).toFixed(2)}h
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Días Analizados</p>
                <p className="text-2xl font-bold text-gray-900">{dateRange.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">
                  {calendarGroupBy === 'employee' ? 'Empleados' : 'Cargos'}
                </p>
                <p className="text-2xl font-bold text-gray-900">{calendarData.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar vista de tareos (vista original)
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Modal de carga al cambiar mes */}
      {loadingRecords && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 flex flex-col items-center gap-4 min-w-[240px]">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-teal-200 border-t-teal-600" />
            <p className="text-gray-700 font-medium">Cargando información...</p>
            <p className="text-sm text-gray-500">Consultando registros del mes</p>
          </div>
        </div>
      )}
      {/* Header con botón de regreso */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToProjects}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Volver a Proyectos</span>
          </button>
          <div className="border-l border-gray-300 pl-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
                Tareos - {selectedProject?.nombre}
              </h1>
              {esTodasSedes && (
                <p className="text-sm text-indigo-600 mt-1">
                  Vista consolidada de {projects.length} proyecto(s). Use Importar Excel para carga masiva.
                </p>
              )}
              {/* Indicador de configuración de tareo */}
              {configuracionTareo && (
                <div className={`mt-2 sm:mt-0 px-3 py-1 rounded-full text-xs font-medium ${
                  configuracionTareo.es_simple 
                    ? 'bg-blue-100 text-blue-800' 
                    : esTareoPorHoras()
                    ? 'bg-purple-100 text-purple-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {configuracionTareo.es_simple 
                    ? '⚡ Tareo Simple' 
                    : esTareoPorHoras()
                    ? '⏰ Tareo por Horas'
                    : '🔧 Tareo Detallado'}
                </div>
              )}
            </div>
            <p className="text-gray-600">Control de asistencia y horas trabajadas</p>
          </div>
        </div>
      </div>

      {/* 🔍 FILTROS COMPACTOS */}
      <div className="bg-gray-50 rounded-lg p-3 mb-4">
        {/* Primera fila de filtros */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mb-3">
          {/* Búsqueda: estado local en el input; solo se aplica al pulsar "Buscar" (no re-render en cada tecla) */}
          <div className="col-span-2 flex items-center gap-2">
            <SearchNameInput
              ref={searchInputRef}
              appliedSearchName={appliedSearchName}
              onClear={() => setAppliedSearchName('')}
            />
                <button
              type="button"
              onClick={applySearchAndHoursFilters}
              className="px-3 py-2 bg-teal-600 text-white rounded-md text-sm font-medium hover:bg-teal-700 whitespace-nowrap"
                >
              Buscar
                </button>
          </div>

          {/* Filtro por período: Día (activo por defecto), Semana o Mes */}
          <div className="col-span-2 flex flex-wrap items-center gap-2">
            <div className="flex rounded-md overflow-hidden border border-gray-300">
              <button
                type="button"
                onClick={() => {
                  setFilterPeriod('day');
                  const d = new Date();
                  setFilterValue(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                }}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap ${filterPeriod === 'day' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Día
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterPeriod('week');
                  setFilterValue(getMondayOfCurrentWeek());
                }}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-gray-300 ${filterPeriod === 'week' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Semana
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilterPeriod('month');
                  setFilterValue(getCurrentMonthValue());
                }}
                className={`px-3 py-2 text-sm font-medium whitespace-nowrap border-l border-gray-300 ${filterPeriod === 'month' ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
              >
                Mes
              </button>
          </div>
            {filterPeriod === 'day' && (
              <input
                type="date"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              />
            )}
            {filterPeriod === 'week' && (
              <input
                type="date"
                value={filterValue}
                onChange={(e) => {
                  const range = getWeekRangeFromDate(e.target.value);
                  setFilterValue(range.start);
                }}
                className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
                title="Selecciona un día; se usará la semana (lunes a domingo) que lo contiene"
              />
            )}
            {filterPeriod === 'month' && (
              <input
                type="month"
                value={filterValue}
                onChange={(e) => setFilterValue(e.target.value)}
                className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              />
            )}
          </div>

          {/* Tipo de evento */}
          <div>
            <select
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 w-full"
            >
              <option value="">Tipo</option>
              <option value="asistio">Asistió</option>
              <option value="falta">Falta</option>
              <option value="permiso">Permiso</option>
              <option value="suspension">Suspensión</option>
              <option value="vacaciones">Vacaciones</option>
              <option value="descanso_medico">D.Médico</option>
              <option value="reposo_medico">🛏️ Reposo</option>
              <option value="subsidio_por_enfermedad">💊 Subsidio</option>
              <option value="licencia_con_goce_de_haber">🧾 Lic. Goce Haber</option>
              <option value="feriado">Feriado</option>
              <option value="feriado_laborado">F.Laborado</option>
              <option value="descanso">Descanso</option>
              <option value="descanso_laborado">D.Laborado</option>
            </select>
          </div>

          {/* Filtro por horas exactas */}
          <div>
            <input
              type="number"
              step="0.5"
              min="0"
              max="24"
              placeholder="Ej: 8.0"
              value={hoursFilter}
              onChange={(e) => setHoursFilter(e.target.value)}
              className="px-2 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500 w-full text-center"
              title="Filtrar por horas exactas trabajadas (±15min de tolerancia)"
            />
          </div>
        </div>

        {/* Segunda fila: Cargo, área y acciones */}
        <div className="flex flex-wrap items-center gap-2">
          <MultiFiltroCargoComponent />
          <MultiFiltroAreaComponent />
          {esTodasSedes && (
            <select
              value={filtroSedeId}
              onChange={(e) => setFiltroSedeId(e.target.value)}
              className="px-2 py-2 border border-indigo-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-indigo-50 text-indigo-900"
              title="Filtrar por proyecto"
            >
              <option value="">Todos los proyectos</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          )}
          
          <div className="flex items-center space-x-2 ml-auto">
            {/* Indicador de filtros activos */}
            {(appliedSearchName || eventTypeFilter || appliedHoursFilter || filtrosCargosSeleccionados.length > 0 || filtrosAreasSeleccionadas.length > 0 || (esTodasSedes && filtroSedeId)) && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                {[
                  appliedSearchName && 'Nombre',
                  eventTypeFilter && 'Tipo',
                  appliedHoursFilter && `${appliedHoursFilter}h`,
                  filtrosCargosSeleccionados.length > 0 && 'Cargos',
                  filtrosAreasSeleccionadas.length > 0 && 'Áreas',
                  esTodasSedes && filtroSedeId && 'Proyecto',
                ].filter(Boolean).length} activo(s)
              </span>
            )}
            
            <button
              onClick={() => {
                if (filterPeriod === 'day') {
                  const d = new Date();
                  setFilterValue(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
                } else if (filterPeriod === 'week') {
                  setFilterValue(getMondayOfCurrentWeek());
                } else {
                  setFilterValue(getCurrentMonthValue());
                }
              }}
              className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-colors duration-200"
              title="Ir al período actual"
            >
              Hoy
            </button>

            <button
              onClick={() => {
                setEventTypeFilter('');
                setHoursFilter('');
              }}
              className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300 transition-colors duration-200"
              title="Limpiar filtros tipo y horas"
            >
              Limpiar
            </button>

            <button
                onClick={clearAllFilters}
                className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 transition-colors duration-200 font-medium"
                title="Limpiar todos los filtros"
              >
                🗑️ Todo
              </button>
            </div>
          </div>
        </div>

        {/* BOTONES DE ACCIÓN */}
        <div className="flex flex-col sm:flex-row justify-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
          {/* Botón de Análisis de Calendario */}
          <button
            onClick={handleOpenCalendarAnalysis}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-indigo-600 text-white rounded-lg font-medium hover:from-indigo-600 hover:to-indigo-700 transition-all duration-200 whitespace-nowrap"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Análisis Calendario</span>
          </button>
          
          {/* Botón de Exportación a Excel */}
          <button
            onClick={exportRecordsToExcel}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-200 whitespace-nowrap"
            title="Exportar registros a Excel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Exportar a Excel</span>
          </button>
          {/* Botón Importar Tareos desde Excel */}
          <button
            onClick={async () => {
              if (esTodasSedes) {
                setImportadorModoGlobal(true);
                setImportadorInitialMode('importar');
              } else {
                setImportadorModoGlobal(false);
                setImportadorInitialMode('importar');
              }
              await loadConfiguracionTasaNocturna();
              setShowImportarTareosModal(true);
            }}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-amber-700 transition-all duration-200 whitespace-nowrap"
            title={esTodasSedes ? 'Importar tareos de todos los proyectos desde Excel' : 'Importar tareos desde Excel'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
            </svg>
            <span>{esTodasSedes ? 'Importar Excel (todos)' : 'Importar Excel'}</span>
          </button>
          {/* Botón Tareo perfecto: genera teórico (asistió días laborables, descanso domingos) */}
          <button
            onClick={() => {
              if (esTodasSedes) {
                alert('En "Todos los proyectos" use Importar Excel para carga masiva, o entre a un proyecto específico para Tareo perfecto.');
                return;
              }
              setTareoPerfectoData({ empleadoIds: [], fechaInicio: '', fechaFin: '', validando: false, error: null, omitidos: [], creados: 0 });
              setShowTareoPerfectoModal(true);
            }}
            className={`flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white rounded-lg font-medium hover:from-cyan-600 hover:to-cyan-700 transition-all duration-200 whitespace-nowrap ${esTodasSedes ? 'opacity-60' : ''}`}
            title={esTodasSedes ? 'Disponible solo en un proyecto específico' : 'Generar tareo perfecto (teórico: asistencias y descansos domingo)'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Tareo perfecto</span>
          </button>
          
          {/* Botones de Registro */}
          <button
            onClick={() => {
              if (esTodasSedes) {
                alert('En "Todos los proyectos" use Importar Excel para carga masiva, o entre a un proyecto específico para Registro Masivo.');
                return;
              }
              handleOpenMassiveModal();
            }}
            className={`flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg font-medium hover:from-purple-600 hover:to-purple-700 transition-all duration-200 whitespace-nowrap ${esTodasSedes ? 'opacity-60' : ''}`}
            title={esTodasSedes ? 'Disponible solo en un proyecto específico' : 'Registro masivo'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span>Registro Masivo</span>
          </button>
          <button
            onClick={() => {
              if (esTodasSedes) {
                alert('En "Todos los proyectos" use Importar Excel, o entre a un proyecto específico para registrar individual.');
                return;
              }
              // Limpiar completamente el estado cuando se abre el modal de agregar
              console.log('🧹 Limpiando estado para nuevo registro');
              setNewAttendanceData({
                employeeId: '',
                date: new Date().toISOString().split('T')[0],
                eventType: 'asistio',
                theoreticalCheckIn: '',
                theoreticalCheckOut: '',
                checkIn: '',
                checkOut: '',
                advances: 0,
                income: 0,
                discounts: 0,
                notes: '',
                concepts: []
              });
              setShowAddModal(true);
            }}
            className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all duration-200 whitespace-nowrap"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Registrar Individual</span>
          </button>

          {/* Botones de Borrado Masivo */}
          {getSelectableRecords().length > 0 && (
            <>
              <button
                onClick={handleSelectAll}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-200 whitespace-nowrap"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
                <span>{selectAll ? 'Deseleccionar Todo' : 'Seleccionar Todo'}</span>
              </button>
              {selectedRecords.length > 0 && (
                <button
                  onClick={handleMassiveDelete}
                  className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-medium hover:from-red-600 hover:to-red-700 transition-all duration-200 whitespace-nowrap"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Borrar Seleccionados ({selectedRecords.length})</span>
                </button>
              )}
            </>
          )}
        </div>

      {/* Statistics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-blue-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Total</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{summary.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Presentes</p>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{summary.presente}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-red-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Faltas</p>
              <p className="text-lg lg:text-2xl font-bold text-red-600">{summary.falta}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-teal-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg className="w-5 h-5 lg:w-6 lg:h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Horas efectivas</p>
              <p className="text-lg lg:text-2xl font-bold text-teal-600">{summary.horasEfectivas.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 lg:p-6 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-orange-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <span className="text-lg lg:text-xl leading-none" aria-hidden>🏥</span>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Horas desc. médico</p>
              <p className="text-lg lg:text-2xl font-bold text-orange-600">{summary.horasDescansoMedico.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Mensaje cuando no hay registros */}
          {filteredRecords.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay registros</h3>
              <p className="text-gray-600 mb-4">
                No se encontraron registros de asistencia para {appliedSearchName ? `"${appliedSearchName}" en ` : ''}el filtro seleccionado.
              </p>
              {(appliedSearchName || appliedHoursFilter) && (
            <button
                  onClick={() => { setAppliedSearchName(''); setHoursFilter(''); setAppliedHoursFilter(''); }}
                  className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors duration-200"
                >
                  Limpiar búsqueda
            </button>
              )}
      </div>
          )}

          {filteredRecords.length > 0 && (
            <>
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
                          <div className="min-w-full">
                <table className="w-full divide-y divide-gray-200" style={{minWidth: '800px'}}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '4%'}}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      disabled={getSelectableRecords().length === 0}
                    />
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: esTodasSedes ? '18%' : '21%'}}>
                    Empleado
                  </th>
                  {esTodasSedes && (
                    <th className="px-2 py-3 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider" style={{width: '10%'}}>
                      Proyecto
                    </th>
                  )}
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                    Fecha
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                    Tipo
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '18%'}}>
                    Horarios
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '8%'}}>
                    Horas
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '15%'}}>
                    Conceptos
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '12%'}}>
                    Notas
                  </th>
                  {tasaNocturnaActiva && (
                    <th className="px-2 py-3 text-center text-xs font-medium text-indigo-600 uppercase tracking-wider" style={{width: '9%'}}>
                      T. Noct.
                    </th>
                  )}
                  <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '7%'}}>
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRecords.map((record) => {
                  const employee = getEmployeeInfo(record.employeeId);
                  return (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-2 py-2 text-center">
                        {canDeleteRecord(record) ? (
                          <input
                            type="checkbox"
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        ) : (
                          <div className="w-4 h-4 flex items-center justify-center">
                            <span className="text-gray-300" title="Registro automático - Solo edición de conceptos">
                              🔒
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center">
                          <div className="w-7 h-7 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold text-xs">
                              {employee?.name?.charAt(0)}
                            </span>
                          </div>
                          <div className="ml-2 min-w-0 flex-1">
                            <div className="text-xs font-medium text-gray-900 truncate">{employee?.name}</div>
                            <div className="text-xs text-gray-500 truncate">{employee?.position}</div>
                          </div>
                        </div>
                      </td>
                      {esTodasSedes && (
                        <td className="px-2 py-2 text-xs text-indigo-800 font-medium truncate max-w-[100px]" title={record.proyectoNombre || ''}>
                          {record.proyectoNombre || '—'}
                        </td>
                      )}
                      <td className="px-2 py-2 text-xs text-gray-900">
                        {formatDate(record.date)}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`inline-flex px-1 py-0.5 text-xs font-semibold rounded ${getEventTypeBadge(record.eventType)}`}>
                          {record.eventType === 'asistio' ? 'Asistió' : 
                           record.eventType === 'falta' ? 'Falta' : 
                           record.eventType === 'feriado' ? 'Feriado' : 
                           record.eventType === 'descanso_medico' ? 'D.Médico' : 
                           record.eventType === 'reposo_medico' ? 'Reposo' :
                           record.eventType === 'subsidio_por_enfermedad' ? 'Subsidio' :
                           record.eventType === 'permiso' ? 'Permiso' :
                           record.eventType === 'suspension' ? 'Suspensión' :
                           record.eventType === 'vacaciones' ? 'Vacaciones' :
                           getEventTypeLabel(record.eventType)}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900">
                        <div className="space-y-0.5">
                          <div>T: {record.theoreticalCheckIn}-{record.theoreticalCheckOut}</div>
                          <div className={record.checkIn !== record.theoreticalCheckIn || record.checkOut !== record.theoreticalCheckOut ? 'text-red-600' : ''}>
                            R: {record.checkIn || '-'}-{record.checkOut || '-'}
                          </div>
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900 font-medium">
                        {getDisplayHours(record)}h
                      </td>
                      <td className="px-2 py-2 text-xs">
                        <div className="space-y-0.5">
                          {record.concepts && record.concepts.length > 0 ? (
                            record.concepts.map((concept, idx) => {
                              const conceptInfo = getConceptInfo(concept.plame_configuracion_id, concept);
                              const sign = concept.tipo_concepto === 'adelanto' || concept.tipo_concepto === 'ingreso' ? '+' : '-';
                              const colorClass = concept.tipo_concepto === 'adelanto' ? 'text-green-600' : 
                                                concept.tipo_concepto === 'ingreso' ? 'text-blue-600' : 'text-red-600';
                              
                              return (
                                <div key={idx} className={`${colorClass} font-medium`}>
                                  <div className="font-semibold truncate max-w-[140px]" title={conceptInfo?.nombre || 'Concepto'}>
                                    {conceptInfo?.nombre || 'Concepto'}
                                  </div>
                                  {conceptInfo?.codigo && (
                                    <div className="text-[10px] text-gray-500">[{conceptInfo.codigo}]</div>
                                  )}
                                  <div>{sign}S/ {parseFloat(concept.monto || 0).toFixed(2)}</div>
                                  {concept.tipo_boleta && (
                                    <div className="text-[10px] text-amber-700">{concept.tipo_boleta}</div>
                                  )}
                                  {concept.observaciones && (
                                    <div className="text-[10px] text-gray-500 truncate max-w-[140px]" title={concept.observaciones}>
                                      {concept.observaciones}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-gray-900">
                        <div className="max-w-24 truncate" title={record.observaciones}>
                          {record.observaciones || '-'}
                        </div>
                      </td>
                      {tasaNocturnaActiva && (
                        <td className="px-2 py-2 text-center text-xs">
                          {record.considerarTasaNocturna ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 font-medium" title="Este día considera tasa nocturna">
                              🌙 Sí
                            </span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                      )}
                      <td className="px-2 py-2 text-center">
                        <div className="flex items-center justify-center space-x-1">
                          {isAutomaticRecord(record) ? (
                            // Registro automático: solo editar conceptos
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleEditRecord(record)}
                                className="text-purple-600 hover:text-purple-900 transition-colors duration-200 p-1"
                                title="Editar conceptos"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                              </button>
                              <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded" title="Solo edición de conceptos">
                                💰 Conceptos
                              </span>
                            </div>
                          ) : (
                            // Registro normal: editar completo y eliminar
                            <>
                              <button
                                onClick={() => handleEditRecord(record)}
                                className="text-blue-600 hover:text-blue-900 transition-colors duration-200 p-1"
                                title="Editar"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleDeleteRecord(record.id)}
                                className="text-red-600 hover:text-red-900 transition-colors duration-200 p-1"
                                title="Eliminar"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
              </div>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y divide-gray-200">
            {filteredRecords.map((record) => {
              const employee = getEmployeeInfo(record.employeeId);
              return (
                <div key={record.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-sm">
                          {employee?.name?.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{employee?.name}</div>
                        <div className="text-xs text-gray-500">{employee?.position}</div>
                        <div className="text-xs text-gray-500">📅 {formatDate(record.date)}</div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {canDeleteRecord(record) ? (
                        <input
                          type="checkbox"
                          checked={selectedRecords.includes(record.id)}
                          onChange={() => handleSelectRecord(record.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      ) : (
                        <div className="w-4 h-4 flex items-center justify-center">
                          <span className="text-gray-300 text-sm" title="Registro automático - Solo edición de conceptos">
                            🔒
                          </span>
                        </div>
                      )}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEventTypeBadge(record.eventType)}`}>
                        {getEventTypeLabel(record.eventType)}
                    </span>
                    </div>
                  </div>
                  
                  {/* Horarios */}
                  <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                    <div className="text-xs font-medium text-gray-700 mb-2">Horarios</div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <span className="text-gray-500 text-xs">Teórico:</span>
                        <div className="font-medium">{record.theoreticalCheckIn} - {record.theoreticalCheckOut}</div>
                    </div>
                    <div>
                        <span className="text-gray-500 text-xs">Real:</span>
                        <div className={`font-medium ${(record.checkIn !== record.theoreticalCheckIn || record.checkOut !== record.theoreticalCheckOut) && record.checkIn ? 'text-red-600' : ''}`}>
                          {record.checkIn || '-'} - {record.checkOut || '-'}
                    </div>
                    </div>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <div className="text-sm">
                      <span className="text-gray-500">Horas trabajadas:</span>
                      <span className="ml-2 font-medium">{getDisplayHours(record)}h</span>
                    </div>
                  </div>

                  {tasaNocturnaActiva && (
                    <div className="mb-3">
                      <span className="text-gray-500 text-sm">Tasa nocturna:</span>
                      {record.considerarTasaNocturna ? (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 text-xs font-medium">
                          🌙 Sí
                        </span>
                      ) : (
                        <span className="ml-2 text-sm text-gray-500">No</span>
                      )}
                    </div>
                  )}

                  {/* Conceptos */}
                  {record.concepts && record.concepts.length > 0 && (
                    <div className="mb-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-xs font-medium text-gray-700 mb-2">Conceptos</div>
                      <div className="space-y-2">
                        {record.concepts.map((concept, idx) => {
                          const conceptInfo = getConceptInfo(concept.plame_configuracion_id, concept);
                          const sign = concept.tipo_concepto === 'adelanto' || concept.tipo_concepto === 'ingreso' ? '+' : '-';
                          const colorClass = concept.tipo_concepto === 'adelanto' ? 'text-green-600' : 
                                            concept.tipo_concepto === 'ingreso' ? 'text-blue-600' : 'text-red-600';
                          const bgClass = concept.tipo_concepto === 'adelanto' ? 'bg-green-100 text-green-800' : 
                                         concept.tipo_concepto === 'ingreso' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800';
                          
                          return (
                            <div key={idx} className="flex justify-between items-start text-xs">
                              <div className="flex-1">
                                <span className={`px-2 py-1 rounded text-xs font-medium ${bgClass}`}>
                                  {conceptInfo?.nombre || 'Concepto'}
                                  {conceptInfo?.codigo ? ` [${conceptInfo.codigo}]` : ''}
                                </span>
                                {concept.tipo_boleta && (
                                  <div className="text-amber-700 mt-1 ml-2 text-xs">
                                    Destino: {concept.tipo_boleta}
                                  </div>
                                )}
                                {concept.observaciones && (
                                  <div className="text-gray-600 mt-1 ml-2 text-xs">
                                    {concept.observaciones}
                    </div>
                  )}
                              </div>
                              <span className={`font-semibold ${colorClass}`}>
                                {sign}S/ {parseFloat(concept.monto || 0).toFixed(2)}
                              </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

                  {record.observaciones && (
                    <div className="mt-3 text-sm">
                      <span className="text-gray-500">Notas:</span>
                      <span className="ml-2">{record.observaciones}</span>
        </div>
      )}

                  {/* Acciones */}
                  <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-end space-x-3">
                    {canEditRecord(record) ? (
                      <>
                        <button
                          onClick={() => handleEditRecord(record)}
                          className="flex items-center space-x-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          <span className="text-sm font-medium">Editar</span>
                        </button>
                        <button
                          onClick={() => handleDeleteRecord(record.id)}
                          className="flex items-center space-x-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors duration-200"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          <span className="text-sm font-medium">Eliminar</span>
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        <span className="text-sm font-medium">Registro Automático</span>
                      </div>
                    )}
                  </div>
          </div>
              );
            })}
        </div>
            </>
      )}
        </div>

      {/* Add Attendance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                  <h3 className="text-lg lg:text-xl font-semibold text-gray-900">
                    Registrar Tareo - {selectedProject?.nombre}
                  </h3>
                <button
                  onClick={() => {
                    console.log('❌ Cerrando modal de agregar y limpiando estado');
                    setShowAddModal(false);
                    setConsiderarTasaNocturnaIndividual(false);
                    setNewAttendanceData({
                      employeeId: '',
                      date: new Date().toISOString().split('T')[0],
                      eventType: 'asistio',
                      theoreticalCheckIn: '',
                      theoreticalCheckOut: '',
                      checkIn: '',
                      checkOut: '',
                      advances: 0,
                      income: 0,
                      discounts: 0,
                      notes: '',
                      concepts: []
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 lg:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={newAttendanceData.date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Empleado *
                  </label>
                  <select
                    name="employeeId"
                    value={newAttendanceData.employeeId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    disabled={employees.length === 0 || !newAttendanceData.date}
                  >
                    <option value="">
                      {!newAttendanceData.date ? 'Selecciona una fecha primero' :
                       employees.length === 0 ? 'Cargando empleados...' : 
                       filterEmployeesByDate(newAttendanceData.date).length === 0 ? `No hay empleados del proyecto "${selectedProject?.nombre}" activos en esta fecha` :
                       'Seleccionar empleado'}
                    </option>
                    {newAttendanceData.date && filterEmployeesByDate(newAttendanceData.date).map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.nombre_completo} - {employee.cargo} 
                        {employee.ultimo_historial?.fecha_cese ? ' (Cesado)' : ' (Activo)'}
                      </option>
                    ))}
                  </select>
                  {newAttendanceData.date && filterEmployeesByDate(newAttendanceData.date).length === 0 && employees.length > 0 && (
                    <p className="mt-1 text-sm text-amber-600">
                      ⚠️ No hay empleados del proyecto "{selectedProject?.nombre}" disponibles para la fecha {formatDate(newAttendanceData.date)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Evento *
                  </label>
                  <select
                    name="eventType"
                    value={newAttendanceData.eventType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="asistio">Asistió</option>
                    <option value="falta">Falta</option>
                    <option value="feriado">Feriado</option>
                    <option value="feriado_laborado">Feriado Laborado</option>
                    <option value="descanso">Descanso</option>
                    <option value="descanso_laborado">Descanso Laborado</option>
                    <option value="descanso_medico">Descanso Médico</option>
                    <option value="reposo_medico">Reposo Médico</option>
                    <option value="subsidio_por_enfermedad">Subsidio por Enfermedad</option>
                    <option value="licencia_con_goce_de_haber">Licencia con Goce de Haber</option>
                  </select>
                </div>
              </div>

              {/* Horarios Teóricos - Solo mostrar si NO es tareo por horas */}
              {!esTareoPorHoras() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horario Teórico
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Entrada Teórica</label>
                      <input
                        type="time"
                        name="theoreticalCheckIn"
                        value={newAttendanceData.theoreticalCheckIn}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Salida Teórica</label>
                      <input
                        type="time"
                        name="theoreticalCheckOut"
                        value={newAttendanceData.theoreticalCheckOut}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Campos para Tareo por Horas: estado local aquí, no re-renderiza toda la página al escribir */}
              {esTareoPorHoras() && (
                <HoursConfigInputs
                  key={`hours-${showAddModal}-${showEditModal}-${editingRecord?.id ?? 0}-${newAttendanceData.employeeId}-${newAttendanceData.theoreticalCheckIn}`}
                  entrada={newAttendanceData.theoreticalCheckIn}
                  hTeoricas={newAttendanceData.horasTeoricas}
                  hReales={newAttendanceData.horasReales}
                  onCommit={handleHoursConfigCommit}
                  calcularHoraSalida={calcularHoraSalida}
                />
              )}

              {/* Horarios Reales - Solo mostrar si el tareo es detallado */}
              {configuracionTareo && configuracionTareo.es_detallado && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora de Entrada Real
                  </label>
                    <div className="space-y-2">
                  <input
                    type="time"
                    name="checkIn"
                    value={newAttendanceData.checkIn}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                      <button
                        type="button"
                        onClick={handleNoCheckIn}
                        className="w-full px-3 py-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-200 transition-colors duration-200 text-sm font-medium"
                      >
                        🚫 No marcó entrada (+30 min)
                      </button>
                    </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora de Salida Real
                  </label>
                    <div className="space-y-2">
                  <input
                    type="time"
                    name="checkOut"
                    value={newAttendanceData.checkOut}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                      <button
                        type="button"
                        onClick={handleNoCheckOut}
                        className="w-full px-3 py-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-200 transition-colors duration-200 text-sm font-medium"
                      >
                        🚫 No marcó salida (-30 min)
                      </button>
                </div>
              </div>
                </div>
              )}

              {/* Mensaje informativo para tareo por horas */}
              {esTareoPorHoras() && (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-purple-800">Tareo por Horas Activado</p>
                      <p className="text-xs text-purple-600">Los horarios de salida se calculan automáticamente basándose en las horas trabajadas (incluye 1h descanso si &gt;6h).</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje informativo para tareo simple */}
              {configuracionTareo && configuracionTareo.es_simple && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Tareo Simple Activado</p>
                      <p className="text-xs text-blue-600">Las horas reales serán automáticamente iguales a las horas teóricas.</p>
                    </div>
                  </div>
                </div>
              )}

              {tasaNocturnaActiva && (
                <label className="flex items-center gap-2 p-4 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={considerarTasaNocturnaIndividual}
                    onChange={(e) => setConsiderarTasaNocturnaIndividual(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-indigo-900">Considerar tasa nocturna</span>
                </label>
              )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas
                  </label>
                  <input
                    type="text"
                    name="notes"
                    value={newAttendanceData.notes}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Observaciones..."
                  />
                </div>

              {/* Adelantos, Descuentos y Otros */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    Conceptos de Adelantos/Descuentos/Ingresos/Otros
                  </label>
                  <button
                    type="button"
                    onClick={addConcept}
                    className="bg-teal-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-teal-600 transition-colors duration-200"
                  >
                    + Agregar Concepto
                  </button>
                </div>
                
                {newAttendanceData.concepts.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="text-gray-500 text-sm">No hay conceptos agregados</p>
                    <p className="text-gray-400 text-xs mt-1">Haz clic en "Agregar Concepto" para añadir adelantos o descuentos</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {newAttendanceData.concepts.map((concept, index) => (
                      <ConceptRowInputs
                        key={index}
                        index={index}
                        type={concept.type}
                        amount={concept.amount}
                        description={concept.description}
                        destinoBoleta={concept.destino_boleta || concept.tipo_boleta || 'plame'}
                        mostrarDestino={planillaAlternativaActiva}
                        conceptTypes={conceptTypes}
                        onBlurRow={updateConceptRow}
                        onRemove={removeConcept}
                      />
                    ))}
                    
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 lg:p-6 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => {
                  console.log('❌ Cancelando modal de agregar y limpiando estado');
                  setShowAddModal(false);
                  setConsiderarTasaNocturnaIndividual(false);
                  setNewAttendanceData({
                    employeeId: '',
                    date: new Date().toISOString().split('T')[0],
                    eventType: 'asistio',
                    theoreticalCheckIn: '',
                    theoreticalCheckOut: '',
                    checkIn: '',
                    checkOut: '',
                    advances: 0,
                    income: 0,
                    discounts: 0,
                    notes: '',
                    concepts: []
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleAddAttendance}
                disabled={isCreating}
                className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                  isCreating 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700'
                }`}
              >
                {isCreating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Registrando...</span>
                  </>
                ) : (
                  <span>Registrar Asistencia</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Attendance Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900">
                  Editar Tareo - {selectedProject?.nombre}
                </h3>
                <button
                                  onClick={() => {
                  console.log('❌ Cerrando modal de editar y limpiando estado');
                  setShowEditModal(false);
                  setEditingRecord(null);
                  setConsiderarTasaNocturnaIndividual(false);
                  setNewAttendanceData({
                    employeeId: '',
                    date: new Date().toISOString().split('T')[0],
                    eventType: 'asistio',
                    theoreticalCheckIn: '',
                    theoreticalCheckOut: '',
                    checkIn: '',
                    checkOut: '',
                    advances: 0,
                    income: 0,
                    discounts: 0,
                    notes: '',
                    concepts: []
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 lg:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={newAttendanceData.date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Empleado *
                  </label>
                  <select
                    name="employeeId"
                    value={newAttendanceData.employeeId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    disabled={employees.length === 0 || !newAttendanceData.date}
                  >
                    <option value="">
                      {!newAttendanceData.date ? 'Selecciona una fecha primero' :
                       employees.length === 0 ? 'Cargando empleados...' : 
                       filterEmployeesByDate(newAttendanceData.date).length === 0 ? `No hay empleados del proyecto "${selectedProject?.nombre}" activos en esta fecha` :
                       'Seleccionar empleado'}
                    </option>
                    {newAttendanceData.date && filterEmployeesByDate(newAttendanceData.date).map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.nombre_completo} - {employee.cargo} 
                        {employee.ultimo_historial?.fecha_cese ? ' (Cesado)' : ' (Activo)'}
                      </option>
                    ))}
                  </select>
                  {newAttendanceData.date && filterEmployeesByDate(newAttendanceData.date).length === 0 && employees.length > 0 && (
                    <p className="mt-1 text-sm text-amber-600">
                      ⚠️ No hay empleados del proyecto "{selectedProject?.nombre}" disponibles para la fecha {formatDate(newAttendanceData.date)}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tipo de Evento *
                  </label>
                  <select
                    name="eventType"
                    value={newAttendanceData.eventType}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="asistio">Asistió</option>
                    <option value="falta">Falta</option>
                    <option value="feriado">Feriado</option>
                    <option value="feriado_laborado">Feriado Laborado</option>
                    <option value="descanso">Descanso</option>
                    <option value="descanso_laborado">Descanso Laborado</option>
                    <option value="descanso_medico">Descanso Médico</option>
                    <option value="reposo_medico">Reposo Médico</option>
                    <option value="subsidio_por_enfermedad">Subsidio por Enfermedad</option>
                  </select>
                </div>
              </div>

              {/* Horarios Teóricos - Solo mostrar si NO es tareo por horas */}
              {!esTareoPorHoras() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Horario Teórico
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Entrada Teórica</label>
                      <input
                        type="time"
                        name="theoreticalCheckIn"
                        value={newAttendanceData.theoreticalCheckIn}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Salida Teórica</label>
                      <input
                        type="time"
                        name="theoreticalCheckOut"
                        value={newAttendanceData.theoreticalCheckOut}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Campos para Tareo por Horas */}
              {esTareoPorHoras() && (
                <HoursConfigInputs
                  key={`hours-edit-${editingRecord?.id ?? 0}-${newAttendanceData.theoreticalCheckIn}`}
                  entrada={newAttendanceData.theoreticalCheckIn}
                  hTeoricas={newAttendanceData.horasTeoricas}
                  hReales={newAttendanceData.horasReales}
                  onCommit={handleHoursConfigCommit}
                  calcularHoraSalida={calcularHoraSalida}
                />
              )}

              {/* Horarios Reales - Solo mostrar si el tareo es detallado */}
              {configuracionTareo && configuracionTareo.es_detallado && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora de Entrada Real
                  </label>
                    <div className="space-y-2">
                  <input
                    type="time"
                    name="checkIn"
                    value={newAttendanceData.checkIn}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                      <button
                        type="button"
                        onClick={handleNoCheckIn}
                        className="w-full px-3 py-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-200 transition-colors duration-200 text-sm font-medium"
                      >
                        🚫 No marcó entrada (+30 min)
                      </button>
                    </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hora de Salida Real
                  </label>
                    <div className="space-y-2">
                  <input
                    type="time"
                    name="checkOut"
                    value={newAttendanceData.checkOut}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                      <button
                        type="button"
                        onClick={handleNoCheckOut}
                        className="w-full px-3 py-2 bg-orange-100 text-orange-700 border border-orange-300 rounded-lg hover:bg-orange-200 transition-colors duration-200 text-sm font-medium"
                      >
                        🚫 No marcó salida (-30 min)
                      </button>
                </div>
              </div>
                </div>
              )}

              {/* Mensaje informativo para tareo por horas */}
              {esTareoPorHoras() && (
                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-purple-800">Tareo por Horas Activado</p>
                      <p className="text-xs text-purple-600">Los horarios de salida se calculan automáticamente basándose en las horas trabajadas (incluye 1h descanso si &gt;6h).</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Mensaje informativo para tareo simple */}
              {configuracionTareo && configuracionTareo.es_simple && (
                <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Tareo Simple Activado</p>
                      <p className="text-xs text-blue-600">Las horas reales serán automáticamente iguales a las horas teóricas.</p>
                    </div>
                  </div>
                </div>
              )}

              {tasaNocturnaActiva && (
                <label className="flex items-center gap-2 p-4 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={considerarTasaNocturnaIndividual}
                    onChange={(e) => setConsiderarTasaNocturnaIndividual(e.target.checked)}
                    className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-indigo-900">Considerar tasa nocturna</span>
                </label>
              )}
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas
                  </label>
                  <input
                    type="text"
                    name="notes"
                    value={newAttendanceData.notes}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="Observaciones..."
                  />
                </div>

              {/* Adelantos, Descuentos y Otros */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label className="block text-sm font-medium text-gray-700">
                    Conceptos de Adelantos/Descuentos/Ingresos/Otros
                  </label>
                  <button
                    type="button"
                    onClick={addConcept}
                    className="bg-teal-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-teal-600 transition-colors duration-200"
                  >
                    + Agregar Concepto
                  </button>
              </div>
                
                {newAttendanceData.concepts.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                    <p className="text-gray-500 text-sm">No hay conceptos agregados</p>
                    <p className="text-gray-400 text-xs mt-1">Haz clic en "Agregar Concepto" para añadir adelantos o descuentos</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {newAttendanceData.concepts.map((concept, index) => (
                      <ConceptRowInputs
                        key={index}
                        index={index}
                        type={concept.type}
                        amount={concept.amount}
                        description={concept.description}
                        destinoBoleta={concept.destino_boleta || concept.tipo_boleta || 'plame'}
                        mostrarDestino={planillaAlternativaActiva}
                        conceptTypes={conceptTypes}
                        onBlurRow={updateConceptRow}
                        onRemove={removeConcept}
                      />
                    ))}
                    
                  </div>
                )}
              </div>
            </div>

            <div className="p-4 lg:p-6 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => {
                  console.log('❌ Cancelando modal de editar y limpiando estado');
                  setShowEditModal(false);
                  setEditingRecord(null);
                  setConsiderarTasaNocturnaIndividual(false);
                  setNewAttendanceData({
                    employeeId: '',
                    date: new Date().toISOString().split('T')[0],
                    eventType: 'asistio',
                    theoreticalCheckIn: '',
                    theoreticalCheckOut: '',
                    checkIn: '',
                    checkOut: '',
                    advances: 0,
                    income: 0,
                    discounts: 0,
                    notes: '',
                    concepts: []
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateRecord}
                disabled={isUpdating}
                className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                  isUpdating 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700'
                }`}
              >
                {isUpdating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Actualizando...</span>
                  </>
                ) : (
                  <span>Actualizar Registro</span>
                )}
              </button>
                        </div>
          </div>
        </div>
      )}

      {/* Massive Registration Modal */}
      {showMassiveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 lg:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg lg:text-xl font-semibold text-gray-900">
                  🚀 Registro Masivo de Tareos - {selectedProject?.nombre}
                </h3>
                <button
                  onClick={handleCloseMassiveModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-4 lg:p-6 space-y-6">
              {/* Header con fecha */}
              <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 p-4 rounded-lg">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                  <div>
                    <h4 className="text-lg font-semibold text-purple-900">
                      📅 Fecha del Registro Masivo
                    </h4>
                    <p className="text-sm text-purple-700">
                      Selecciona la fecha y automáticamente se cargarán todos los empleados activos
                    </p>
                  </div>
                  <div className="lg:w-48">
                    <input
                      type="date"
                      value={massiveDate}
                      onChange={(e) => handleMassiveDateChange(e.target.value)}
                      className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              {/* Indicador de configuración de tareo */}
              {configuracionTareo && (
                <div className={`border p-4 rounded-lg ${
                  configuracionTareo.es_simple 
                    ? 'bg-blue-50 border-blue-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className={`text-sm font-medium ${
                        configuracionTareo.es_simple 
                          ? 'text-blue-800' 
                          : esTareoPorHoras()
                          ? 'text-purple-800'
                          : 'text-green-800'
                      }`}>
                        {configuracionTareo.es_simple 
                    ? '⚡ Tareo Simple Activado' 
                    : esTareoPorHoras()
                    ? '⏰ Tareo por Horas Activado'
                    : '🔧 Tareo Detallado Activado'}
                      </p>
                      <p className={`text-xs ${
                        configuracionTareo.es_simple 
                          ? 'text-blue-600' 
                          : esTareoPorHoras()
                          ? 'text-purple-600'
                          : 'text-green-600'
                      }`}>
                        {configuracionTareo.es_simple 
                          ? 'Las horas reales serán automáticamente iguales a las horas teóricas'
                          : esTareoPorHoras()
                          ? 'Los horarios se calculan automáticamente basándose en las horas trabajadas (incluye 1h descanso si &gt;6h)'
                          : 'Se pueden configurar horas reales diferentes a las teóricas'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de empleados */}
              {massiveEmployees.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empleados activos</h3>
                  <p className="text-gray-600">
                    No se encontraron empleados del proyecto "{selectedProject?.nombre}" activos para la fecha {formatDate(massiveDate)}.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">
                          👥 Empleados para Registrar ({massiveEmployees.length})
                        </h4>
                        <p className="text-sm text-gray-600">
                          Configura los tareos para cada empleado. Puedes eliminar empleados que no quieras registrar.
                        </p>
                      </div>
                    </div>

                    
                    {/* Controles de Cambios Masivos */}
                    <div className="space-y-3">
                      {/* Control de Evento Masivo */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">⚡ Evento Masivo:</span>
                          <select
                            onChange={(e) => {
                              if (e.target.value && e.target.value !== '') {
                                updateAllMassiveEmployeesEvent(e.target.value);
                                e.target.value = ''; // Reset del select
                              }
                            }}
                            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            defaultValue=""
                          >
                            <option value="">Seleccionar evento para todos...</option>
                            <option value="asistio">✅ Asistió</option>
                            <option value="falta">❌ Falta</option>
                            <option value="feriado">🎉 Feriado</option>
                            <option value="feriado_laborado">🎉💼 Feriado Laborado</option>
                            <option value="descanso">😴 Descanso</option>
                            <option value="descanso_laborado">😴💼 Descanso Laborado</option>
                            <option value="descanso_medico">🏥 Descanso Médico</option>
                            <option value="reposo_medico">🛏️ Reposo Médico</option>
                            <option value="subsidio_por_enfermedad">💊 Subsidio por Enfermedad</option>
                          </select>
                        </div>
                        <div className="text-xs text-gray-500">
                          Aplica el mismo evento a todos los empleados
                        </div>
                      </div>

                                                {/* Control de Horas Masivas - Solo para tareo por horas */}
                      {esTareoPorHoras() && (
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                          <div className="flex items-center mb-3">
                            <span className="text-purple-700 font-medium text-sm">🕐 Configuración Masiva de Horas</span>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Horas Teóricas Masivas */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-purple-700">⏱️ H. Teóricas:</span>
                                <input
                                  type="number"
                                  step="0.25"
                                  min="0"
                                  max="24"
                                  defaultValue="8"
                                  className="w-20 px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-center"
                                  id="massive-horas-teoricas"
                                />
                                <button
                                  onClick={() => {
                                    const value = document.getElementById('massive-horas-teoricas').value;
                                    if (value && parseFloat(value) >= 0) {
                                      updateAllMassiveEmployeesHours('horasTeoricas', parseFloat(value));
                                    }
                                  }}
                                  className="px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 transition-colors duration-200"
                                >
                                  Aplicar a todos
                                </button>
                              </div>
                              <div className="text-xs text-purple-600">
                                Establece las mismas horas teóricas para todos
                              </div>
                            </div>

                            {/* Horas Reales Masivas */}
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-purple-700">⚡ H. Reales:</span>
                                <input
                                  type="number"
                                  step="0.25"
                                  min="0"
                                  max="24"
                                  defaultValue="8"
                                  className="w-20 px-2 py-1 border border-purple-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white text-center"
                                  id="massive-horas-reales"
                                />
                                <button
                                  onClick={() => {
                                    const value = document.getElementById('massive-horas-reales').value;
                                    if (value && parseFloat(value) >= 0) {
                                      updateAllMassiveEmployeesHours('horasReales', parseFloat(value));
                                    }
                                  }}
                                  className="px-3 py-1 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 transition-colors duration-200"
                                >
                                  Aplicar a todos
                                </button>
                              </div>
                              <div className="text-xs text-purple-600">
                                ⭐ Establece las mismas horas reales para todos
                              </div>
                            </div>
                          </div>

                          {/* Botones rápidos */}
                          <div className="mt-3 pt-3 border-t border-purple-200">
                            <div className="flex items-center space-x-2">
                              <span className="text-xs font-medium text-purple-700">🚀 Aplicación rápida:</span>
                              <button
                                onClick={() => updateAllMassiveEmployeesHours('horasReales', 6)}
                                className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors duration-200"
                              >
                                6h todos
                              </button>
                              <button
                                onClick={() => updateAllMassiveEmployeesHours('horasReales', 8)}
                                className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors duration-200"
                              >
                                8h todos
                              </button>
                              <button
                                onClick={() => updateAllMassiveEmployeesHours('horasReales', 10)}
                                className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors duration-200"
                              >
                                10h todos
                              </button>
                              <button
                                onClick={() => updateAllMassiveEmployeesHours('horasReales', 12)}
                                className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs hover:bg-purple-200 transition-colors duration-200"
                              >
                                12h todos
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* 💰 Control de Conceptos Masivos - Compacto */}
                      <div className="bg-teal-50 p-3 rounded-lg border border-teal-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-teal-700 font-medium text-sm">💰 Conceptos Masivos</span>
                          <div className="text-xs text-teal-600">
                            {massiveEmployees.reduce((total, emp) => total + (emp.concepts?.length || 0), 0)} conceptos |
                            {massiveEmployees.filter(emp => emp.concepts?.length > 0).length} empleados
                          </div>
                        </div>
                        
                        {/* Formulario compacto en una sola fila */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <select
                            className="flex-1 min-w-40 px-2 py-1 border border-teal-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                            id="massive-concept-type"
                          >
                            <option value="">Tipo de concepto</option>
                            {conceptTypes.map((conceptType) => (
                              <option key={conceptType.value} value={conceptType.value}>
                                {conceptType.label}
                              </option>
                            ))}
                          </select>
                          
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="Monto"
                            className="w-20 px-2 py-1 border border-teal-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white text-center"
                            id="massive-concept-amount"
                          />
                          
                          <input
                            type="text"
                            placeholder="Observaciones..."
                            className="flex-1 min-w-32 px-2 py-1 border border-teal-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500 bg-white"
                            id="massive-concept-description"
                          />
                          
                          <button
                            onClick={() => {
                              const type = document.getElementById('massive-concept-type').value;
                              const amount = parseFloat(document.getElementById('massive-concept-amount').value) || 0;
                              const description = document.getElementById('massive-concept-description').value;
                              
                              if (type && amount > 0) {
                                setMassiveEmployees(prev => 
                                  prev.map(emp => ({
                                    ...emp,
                                    concepts: [...emp.concepts, { 
                                      type: type, 
                                      amount: amount, 
                                      description: description,
                                      conceptType: conceptTypes.find(ct => ct.value === type)?.type || 'discount'
                                    }]
                                  }))
                                );
                                
                                // Limpiar campos
                                document.getElementById('massive-concept-type').value = '';
                                document.getElementById('massive-concept-amount').value = '';
                                document.getElementById('massive-concept-description').value = '';
                              } else {
                                alert('Selecciona tipo y monto válido');
                              }
                            }}
                            className="px-3 py-1 bg-teal-600 text-white rounded text-xs font-medium hover:bg-teal-700 transition-colors duration-200 whitespace-nowrap"
                          >
                            ➕ Agregar
                          </button>
                          
                          <button
                            onClick={() => {
                              if (window.confirm('¿Eliminar TODOS los conceptos?')) {
                                setMassiveEmployees(prev => 
                                  prev.map(emp => ({ ...emp, concepts: [] }))
                                );
                              }
                            }}
                            className="px-3 py-1 bg-red-100 text-red-700 border border-red-300 rounded text-xs font-medium hover:bg-red-200 transition-colors duration-200 whitespace-nowrap"
                          >
                            🗑️ Limpiar
                          </button>
                        </div>
                      </div>


                    </div>
                  </div>

                  {/* Desktop Table */}
                  <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                              Empleado
                            </th>
                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                              Evento
                            </th>
                            {esTareoPorHoras() ? (
                              <>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  <span className="text-purple-600">🕐 Entrada</span>
                                </th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  <span className="text-purple-600">⏱️ H.Teór</span>
                                </th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  <span className="text-purple-600">⚡ H.Real</span>
                                </th>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                                  <span className="text-gray-400">🕓 Salida</span>
                                </th>
                              </>
                            ) : (
                              <>
                                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                  Horario Teórico
                                </th>
                                {configuracionTareo && configuracionTareo.es_detallado && (
                                  <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                                    Horario Real
                                  </th>
                                )}
                              </>
                            )}

                            {tasaNocturnaActiva && (
                              <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                                <span className="text-indigo-600">🌙 T. Noct.</span>
                              </th>
                            )}

                            <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                              <span className="text-teal-600">💰 Conceptos</span>
                            </th>
                            <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                              Acciones
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {massiveEmployees.map((empleado) => (
                            <tr key={empleado.id} className="hover:bg-blue-50 transition-colors duration-200">
                              <td className="px-3 py-3">
                                <div className="flex items-center">
                                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold text-sm">
                                      {empleado.nombre_completo?.charAt(0)}
                                    </span>
                                  </div>
                                  <div className="ml-2">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {empleado.nombre_completo}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {empleado.cargo}
                                    </div>
                                    <div className={`text-xs truncate ${empleado.area_trabajo === 'Sin área' ? 'text-gray-400 italic' : 'text-purple-600'}`}>
                                      📍 {empleado.area_trabajo}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 py-3">
                                <select
                                  value={empleado.eventType}
                                  onChange={(e) => updateMassiveEmployee(empleado.id, 'eventType', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                                >
                                  <option value="asistio">Asistió</option>
                                  <option value="falta">Falta</option>
                                  <option value="feriado">Feriado</option>
                                  <option value="feriado_laborado">F.Laborado</option>
                                  <option value="descanso">Descanso</option>
                                  <option value="descanso_laborado">D.Laborado</option>
                                  <option value="descanso_medico">D.Médico</option>
                                  <option value="reposo_medico">🛏️ Reposo</option>
                                  <option value="subsidio_por_enfermedad">💊 Subsidio</option>
                                  <option value="licencia_con_goce_de_haber">🧾 Lic. Goce</option>
                                </select>
                              </td>
                              {esTareoPorHoras() ? (
                                <>
                                  <MassiveEditHoursInputs
                                    variant="table"
                                    employeeId={empleado.id}
                                    entrada={empleado.theoreticalCheckIn}
                                    hTeoricas={empleado.horasTeoricas}
                                    hReales={empleado.horasReales}
                                    onCommit={commitMassiveEmployeeHours}
                                    calcularHoraSalida={calcularHoraSalida}
                                  />
                                </>
                              ) : (
                                <>
                                  <td className="px-2 py-3">
                                    <div className="flex items-center space-x-1">
                                      <input
                                        type="time"
                                        value={empleado.theoreticalCheckIn}
                                        onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'theoreticalCheckIn', e.target.value)}
                                        className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                                      />
                                      <span className="text-gray-400 text-xs">-</span>
                                      <input
                                        type="time"
                                        value={empleado.theoreticalCheckOut}
                                        onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'theoreticalCheckOut', e.target.value)}
                                        className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                                      />
                                    </div>
                                  </td>
                                  {configuracionTareo && configuracionTareo.es_detallado && (
                                    <td className="px-2 py-3">
                                      <div className="flex items-center space-x-1">
                                        <input
                                          type="time"
                                          value={empleado.checkIn}
                                          onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'checkIn', e.target.value)}
                                          className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                                        />
                                        <span className="text-gray-400 text-xs">-</span>
                                        <input
                                          type="time"
                                          value={empleado.checkOut}
                                          onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'checkOut', e.target.value)}
                                          className="w-16 px-1 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-purple-500"
                                        />
                                      </div>
                                    </td>
                                  )}
                                </>
                              )}

                              {tasaNocturnaActiva && (
                                <td className="px-2 py-3 text-center">
                                  <label className="inline-flex items-center justify-center cursor-pointer" title="Considerar tasa nocturna">
                                    <input
                                      type="checkbox"
                                      checked={Boolean(empleado.considerarTasaNocturna)}
                                      onChange={(e) => updateMassiveEmployee(empleado.id, 'considerarTasaNocturna', e.target.checked)}
                                      className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                    />
                                  </label>
                                </td>
                              )}

                              <td className="px-2 py-3">
                                <div className="space-y-1 max-w-48">
                                  {/* Conceptos existentes */}
                                  {empleado.concepts && empleado.concepts.length > 0 ? (
                                    <div className="space-y-1">
                                      {empleado.concepts.map((concept, conceptIndex) => (
                                        <MassiveConceptRowInputs
                                          key={conceptIndex}
                                          variant="table"
                                          employeeId={empleado.id}
                                          conceptIndex={conceptIndex}
                                          type={concept.type}
                                          amount={concept.amount}
                                          description={concept.description}
                                          destinoBoleta={concept.destino_boleta || concept.tipo_boleta || 'plame'}
                                          mostrarDestino={planillaAlternativaActiva}
                                          conceptTypes={conceptTypes}
                                          onBlurRow={commitMassiveConcept}
                                          onRemove={removeMassiveConcept}
                                        />
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-gray-400 text-xs text-center">Sin conceptos</div>
                                  )}
                                  
                                  {/* Botón compacto para agregar */}
                                  <button
                                    onClick={() => addMassiveConcept(empleado.id)}
                                    className="w-full text-teal-600 hover:text-teal-800 text-xs p-1 border border-dashed border-teal-300 rounded hover:bg-teal-50 transition-colors duration-200"
                                    title="Agregar concepto"
                                  >
                                    + Agregar
                                  </button>
                                </div>
                              </td>
                                                              <td className="px-2 py-3 text-center">
                                <button
                                  onClick={() => removeMassiveEmployee(empleado.id)}
                                  className="text-red-600 hover:text-red-900 hover:bg-red-50 p-1 rounded transition-colors duration-200"
                                  title="Eliminar de la lista"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile Cards */}
                  <div className="lg:hidden space-y-4">
                    {massiveEmployees.map((empleado) => (
                      <div key={empleado.id} className="p-4 border rounded-lg bg-white border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {empleado.nombre_completo?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {empleado.nombre_completo}
                              </div>
                              <div className="text-xs text-gray-500">
                                {empleado.cargo}
                              </div>
                              <div className={`text-xs ${empleado.area_trabajo === 'Sin área' ? 'text-gray-400 italic' : 'text-purple-600'}`}>
                                📍 {empleado.area_trabajo}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeMassiveEmployee(empleado.id)}
                            className="text-red-600 hover:text-red-900 hover:bg-red-50 p-2 rounded transition-colors duration-200"
                            title="Eliminar de la lista"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                        
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Evento</label>
                            <select
                              value={empleado.eventType}
                              onChange={(e) => updateMassiveEmployee(empleado.id, 'eventType', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            >
                              <option value="asistio">Asistió</option>
                              <option value="falta">Falta</option>
                              <option value="feriado">Feriado</option>
                              <option value="feriado_laborado">Feriado Laborado</option>
                              <option value="descanso">Descanso</option>
                              <option value="descanso_laborado">Descanso Laborado</option>
                              <option value="descanso_medico">Descanso Médico</option>
                              <option value="reposo_medico">Reposo Médico</option>
                              <option value="subsidio_por_enfermedad">Subsidio por Enfermedad</option>
                            </select>
                          </div>
                          
                          {esTareoPorHoras() ? (
                            <>
                              {/* Para tareo por horas */}
                              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                <div className="flex items-center mb-2">
                                  <span className="text-purple-600 text-xs font-medium">🕐 CONFIGURACIÓN POR HORAS</span>
                                </div>
                                
                                <MassiveEditHoursInputs
                                  variant="card"
                                  employeeId={empleado.id}
                                  entrada={empleado.theoreticalCheckIn}
                                  hTeoricas={empleado.horasTeoricas}
                                  hReales={empleado.horasReales}
                                  onCommit={commitMassiveEmployeeHours}
                                  calcularHoraSalida={calcularHoraSalida}
                                />
                                
                                <div className="mt-2 text-xs text-purple-600 text-center">
                                  ℹ️ Horarios de salida se calculan automáticamente
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              {/* Para tareo simple/detallado: solo evento de cambio de hora */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">H. Teórico Entrada</label>
                                  <input
                                    type="time"
                                    value={empleado.theoreticalCheckIn}
                                    onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'theoreticalCheckIn', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">H. Teórico Salida</label>
                                  <input
                                    type="time"
                                    value={empleado.theoreticalCheckOut}
                                    onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'theoreticalCheckOut', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  />
                                </div>
                              </div>

                              {configuracionTareo && configuracionTareo.es_detallado && (
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">H. Real Entrada</label>
                                    <input
                                      type="time"
                                      value={empleado.checkIn}
                                      onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'checkIn', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">H. Real Salida</label>
                                    <input
                                      type="time"
                                      value={empleado.checkOut}
                                      onChange={(e) => handleMassiveHourFieldChange(empleado.id, 'checkOut', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {tasaNocturnaActiva && (
                            <label className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-lg cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(empleado.considerarTasaNocturna)}
                                onChange={(e) => updateMassiveEmployee(empleado.id, 'considerarTasaNocturna', e.target.checked)}
                                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                              />
                              <span className="text-sm font-medium text-indigo-900">Considerar tasa nocturna</span>
                            </label>
                          )}

                          {/* 💰 SECCIÓN DE CONCEPTOS */}
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <label className="block text-xs font-medium text-gray-700">💰 Conceptos</label>
                              <button
                                onClick={() => addMassiveConcept(empleado.id)}
                                className="text-teal-600 hover:text-teal-800 text-xs flex items-center space-x-1 px-2 py-1 border border-teal-300 rounded hover:bg-teal-50 transition-colors duration-200"
                                title="Agregar concepto"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                <span>Agregar</span>
                              </button>
                            </div>

                            {empleado.concepts && empleado.concepts.length > 0 ? (
                              <div className="space-y-2">
                                {empleado.concepts.map((concept, conceptIndex) => (
                                  <MassiveConceptRowInputs
                                    key={conceptIndex}
                                    variant="card"
                                    employeeId={empleado.id}
                                    conceptIndex={conceptIndex}
                                    type={concept.type}
                                    amount={concept.amount}
                                    description={concept.description}
                                    destinoBoleta={concept.destino_boleta || concept.tipo_boleta || 'plame'}
                                    mostrarDestino={planillaAlternativaActiva}
                                    conceptTypes={conceptTypes}
                                    onBlurRow={commitMassiveConcept}
                                    onRemove={removeMassiveConcept}
                                  />
                                ))}
                              </div>
                            ) : (
                              <div className="text-gray-400 text-xs text-center py-2 border border-dashed border-gray-300 rounded">
                                Sin conceptos agregados
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 lg:p-6 border-t border-gray-200 flex flex-col sm:flex-row items-stretch sm:items-center justify-between space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="text-sm text-gray-600">
                📊 Total: {massiveEmployees.length} empleados | 
                📅 Fecha: {formatDate(massiveDate)} |
                💰 Conceptos: {massiveEmployees.reduce((total, emp) => total + (emp.concepts?.length || 0), 0)}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleCloseMassiveModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleMassiveRegister}
                  disabled={isCreatingMassive || massiveEmployees.length === 0}
                  className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                    isCreatingMassive || massiveEmployees.length === 0
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                  }`}
                >
                  {isCreatingMassive ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Registrando {massiveEmployees.length} tareos...</span>
                    </>
                  ) : (
                    <span>🚀 Registrar {massiveEmployees.length} Tareo{massiveEmployees.length > 1 ? 's' : ''}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Importador de Tareos desde Excel */}
      {showImportarTareosModal && selectedCompany && (selectedProject || importadorModoGlobal) && (
        <ImportadorTareos
          onClose={() => {
            setShowImportarTareosModal(false);
            setImportadorModoGlobal(false);
            setImportadorInitialMode('importar');
          }}
          onImportSuccess={() => {
            if (selectedProject) {
              loadAttendanceRecords();
            }
            setShowImportarTareosModal(false);
            setImportadorModoGlobal(false);
            setImportadorInitialMode('importar');
          }}
          selectedCompany={selectedCompany}
          selectedProject={importadorModoGlobal || esTodasSedes ? null : selectedProject}
          projects={projects}
          employees={employees}
          plameConceptos={plameConceptos}
          initialMode={importadorModoGlobal ? importadorInitialMode : 'importar'}
          esTareoSimple={Boolean(configuracionTareo?.es_simple)}
          tasaNocturnaActiva={tasaNocturnaActiva}
        />
      )}

      {/* Modal Tareo perfecto - múltiples empleados */}
      {showTareoPerfectoModal && selectedCompany && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Tareo perfecto</h3>
              <p className="text-sm text-gray-500 mt-1">Genera el teórico para uno o más empleados: asistencias en días laborables y descanso los domingos. Cada empleado debe estar activo en el rango y sin registros previos.</p>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Empleados activos</label>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => seleccionarTodosTareoPerfecto(true)} className="text-xs text-cyan-600 hover:underline">Todos</button>
                    <button type="button" onClick={() => seleccionarTodosTareoPerfecto(false)} className="text-xs text-gray-500 hover:underline">Ninguno</button>
                  </div>
                </div>
                <div className="border border-gray-300 rounded-lg max-h-44 overflow-y-auto p-2 space-y-1">
                  {empleadosParaTareoPerfecto.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">No hay empleados activos en este proyecto.</p>
                  ) : (
                    empleadosParaTareoPerfecto.map(emp => (
                      <label key={emp.id} className="flex items-center gap-2 py-1 hover:bg-gray-50 rounded px-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={tareoPerfectoData.empleadoIds.includes(emp.id)}
                          onChange={() => toggleTareoPerfectoEmpleado(emp.id)}
                          className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                        />
                        <span className="text-sm text-gray-800">{emp.nombre_completo}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">{tareoPerfectoData.empleadoIds.length} seleccionado(s)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
                <input
                  type="date"
                  value={tareoPerfectoData.fechaInicio}
                  onChange={e => setTareoPerfectoData(prev => ({ ...prev, fechaInicio: e.target.value, error: null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
                <input
                  type="date"
                  value={tareoPerfectoData.fechaFin}
                  onChange={e => setTareoPerfectoData(prev => ({ ...prev, fechaFin: e.target.value, error: null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
              {tareoPerfectoData.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{tareoPerfectoData.error}</div>
              )}
              {tareoPerfectoData.omitidos && tareoPerfectoData.omitidos.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <span className="font-medium">Omitidos:</span>
                  <ul className="mt-1 list-disc list-inside">
                    {tareoPerfectoData.omitidos.map((o, i) => (
                      <li key={i}>{o.nombre}: {o.motivo}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="p-5 flex justify-end gap-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setShowTareoPerfectoModal(false);
                  setTareoPerfectoData({ empleadoIds: [], fechaInicio: '', fechaFin: '', validando: false, error: null, omitidos: [], creados: 0 });
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={tareoPerfectoData.validando || !tareoPerfectoData.empleadoIds.length}
                onClick={verificarTareoPerfectoYGenerar}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {tareoPerfectoData.validando ? 'Verificando...' : `Generar tareo perfecto (${tareoPerfectoData.empleadoIds.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔒 MODAL PARA EDICIÓN DE CONCEPTOS EN REGISTROS AUTOMÁTICOS */}
      {showAutomaticEditModal && editingRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <span>🔒</span>
                    <span>Editar Conceptos - Registro Automático</span>
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Solo se pueden modificar los conceptos</p>
    </div>
                <button
                  onClick={() => {
                    setShowAutomaticEditModal(false);
                    setEditingRecord(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Información del registro (Solo lectura) */}
            <div className="p-6 bg-gray-50 border-b border-gray-200">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">📋 Información del Registro (Solo lectura)</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Empleado:</span>
                  <div className="font-medium">{getEmployeeName(editingRecord.employeeId)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Fecha:</span>
                  <div className="font-medium">{formatDate(editingRecord.date)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Tipo de evento:</span>
                  <div className="font-medium">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEventTypeBadge(editingRecord.eventType)}`}>
                      {getEventTypeLabel(editingRecord.eventType)}
                    </span>
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Horas trabajadas:</span>
                  <div className="font-medium">{getDisplayHours(editingRecord)}h</div>
                </div>
              </div>
              {editingRecord.observaciones && (
                <div className="mt-3">
                  <span className="text-gray-500">Observaciones:</span>
                  <div className="text-sm bg-white p-2 rounded border mt-1">
                    {editingRecord.observaciones}
                  </div>
                </div>
              )}
            </div>

            {/* Sección de conceptos (Editable) */}
            <div className="p-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center space-x-2">
                <span>💰</span>
                <span>Conceptos (Editable)</span>
              </h4>

              {/* Lista de conceptos existentes (commit solo en onBlur: select, monto, observaciones) */}
              <div className="space-y-3 mb-4">
                {editingRecord.concepts && editingRecord.concepts.length > 0 ? (
                  editingRecord.concepts.map((concept, index) => (
                    <ConceptRowInputs
                      key={index}
                      index={index}
                      type={concept.plame_configuracion_id || concept.type || ''}
                      amount={concept.monto ?? concept.amount ?? 0}
                      description={concept.observaciones || concept.description || ''}
                      destinoBoleta={concept.destino_boleta || concept.tipo_boleta || 'plame'}
                      mostrarDestino={planillaAlternativaActiva}
                      conceptTypes={conceptTypes}
                      onBlurRow={updateEditingRecordConcept}
                      onRemove={() => {
                              const updatedConcepts = editingRecord.concepts.filter((_, i) => i !== index);
                        setEditingRecord({ ...editingRecord, concepts: updatedConcepts });
                      }}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                    <p className="text-sm">No hay conceptos agregados</p>
                  </div>
                )}
              </div>

              {/* Botón para agregar concepto */}
              <button
                onClick={() => {
                  // Usar el primer concepto disponible como valor por defecto
                  const defaultConcept = conceptTypes[0] || plameConceptos[0];
                  const newConcept = {
                    plame_configuracion_id: defaultConcept?.value || defaultConcept?.id || '',
                    type: defaultConcept?.value || defaultConcept?.id || '',
                    tipo_concepto: defaultConcept?.type || defaultConcept?.tipo_concepto || 'adelanto',
                    conceptType: defaultConcept?.type || defaultConcept?.tipo_concepto || 'adelanto',
                    monto: 0,
                    amount: 0,
                    observaciones: '',
                    description: '',
                    destino_boleta: 'plame',
                    tipo_boleta: 'plame',
                  };
                  setEditingRecord({
                    ...editingRecord,
                    concepts: [...(editingRecord.concepts || []), newConcept]
                  });
                }}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-purple-400 hover:text-purple-600 transition-colors duration-200 flex items-center justify-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Agregar concepto</span>
              </button>
            </div>

            {/* Footer con botones */}
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowAutomaticEditModal(false);
                  setEditingRecord(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleUpdateRecord}
                disabled={isUpdating}
                className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 ${
                  isUpdating
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700'
                }`}
              >
                {isUpdating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Actualizando...</span>
                  </>
                ) : (
                  <span>💾 Guardar Conceptos</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tareos; 